// Code generated by apic; DO NOT EDIT.
import { useEffect, useRef, useState } from "react";

export type FileUpload = {
  blob: Blob;
  filename?: string;
  contentType?: string;
};

export type RequestState<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
  isPending: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  refetch: () => Promise<T>;
};

export type QueryHookOptions = { enabled?: boolean };

export type MutationState<TReq, TResp> = {
  data: TResp | null;
  error: Error | null;
  loading: boolean;
  isPending: boolean;
  isIdle: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (req: TReq) => Promise<TResp>;
  mutateAsync: (req: TReq) => Promise<TResp>;
};

export type StreamState<T> = {
  connected: boolean;
  error: Error | null;
  lastMessage: T | null;
  close: () => void;
};

export type APIKeySigningInput = {
  method: string;
  path: string;
  bodyBytes: ArrayBuffer;
  transport: "http" | "ws";
};

export type APIKeySigningResult = {
  authorization: string;
  nonce: string;
  timestamp: string;
};

export type ClientConfig = {
  baseUrl: string;
  bearerToken?: string | (() => string | null | undefined | Promise<string | null | undefined>);
  /**
   * Signs an api_key request/handshake. The signer owns all hashing/HMAC, so
   * the client itself needs no WebCrypto: inject a platform hasher here to use
   * api_key auth on React Native (where crypto.subtle is unavailable).
   */
  apiKeySigner?: (input: APIKeySigningInput) => Promise<APIKeySigningResult> | APIKeySigningResult;
  headers?: Record<string, string>;
  fetchImpl?: typeof fetch;
  webSocketFactory?: (url: string, protocols?: string | string[]) => WebSocket;
  wsProtocols?: Record<string, string[]>;
  wsOrigin?: string;
  /**
   * Called once when a request receives HTTP 401. If it resolves truthy
   * (e.g. a token was refreshed), the request is retried exactly once.
   * Mirrors the CSRF 403-retry already built into this client.
   */
  onUnauthorized?: () => Promise<boolean | void> | boolean | void;
  /** Query-fetch retry count (default 0). Mirrors react-query's retry knob (TSC-15). */
  retry?: number;
  /** Base backoff (ms) for the exponential retry delay (default 250). TSC-15. */
  retryBackoffMs?: number;
  /**
   * Cache entry time-to-live (ms) for the shared QueryCache: a settled entry
   * older than this is treated as absent and garbage-collected on next access.
   * Mirrors react-query's gcTime. Default 30 minutes (TSC-03).
   */
  gcTimeMs?: number;
  /**
   * Maximum number of settled entries retained in the shared QueryCache;
   * exceeding it evicts the least-recently-used settled entries. Bounds memory
   * so the cache cannot grow without limit. Default 500 (TSC-03).
   */
  maxCacheEntries?: number;
  /**
   * Overrides the correlation-header NAME sent on every REST/GraphQL call.
   * Defaults to the server's observability.tracing.correlation_header
   * ("X-Correlation-ID"). The value is generated per call
   * (crypto.randomUUID) unless supplied via RequestOptions.correlationId.
   */
  correlationHeader?: string;
};

export type RequestOptions = {
  signal?: AbortSignal;
  headers?: HeadersInit;
  timeoutMs?: number;
  /**
   * Per-call end-to-end correlation id. Sent as the correlation header when it
   * passes the header-safe grammar (1..128, [A-Za-z0-9][A-Za-z0-9._-]*); an
   * invalid value is dropped and a fresh id is generated instead.
   */
  correlationId?: string;
  /**
   * Per-call W3C traceparent, forwarded verbatim only when well-formed
   * ("00-<32hex>-<16hex>-<2hex>"). The client never synthesizes one.
   */
  traceparent?: string;
};

async function resolveBearerToken(value: ClientConfig["bearerToken"]): Promise<string> {
  if (value === undefined || value === null) {
    return "";
  }
  const resolved = typeof value === "function" ? await value() : value;
  return resolved ?? "";
}

export interface MessageStream<T> {
  close(): void;
  onMessage(handler: (value: T) => void): () => void;
}

export interface JSONSocket<T> extends MessageStream<T> {
  socket: WebSocket;
  send(value: T): void;
}

export interface BinarySocket extends MessageStream<Uint8Array> {
  socket: WebSocket;
  send(value: Uint8Array): void;
}

export interface GraphQLSubscription<T> extends MessageStream<T> {
  socket: WebSocket;
}

type MessageHandler<T> = (value: T) => void;

export class HTTPError extends Error {
  readonly status: number;
  readonly code?: string | undefined;
  readonly body: unknown;
  readonly bodyText: string;
  constructor(
    status: number,
    bodyText: string,
    body: unknown,
    code?: string,
    options?: { cause?: unknown },
  ) {
    super(resolveErrorMessage(status, body), options as ErrorOptions);
    this.name = "HTTPError";
    this.status = status;
    this.bodyText = bodyText;
    this.body = body;
    this.code = code;
  }
}

function resolveErrorMessage(status: number, body: unknown): string {
  if (body && typeof body === "object") {
    const rec = body as Record<string, unknown>;
    const parsed = {
      error: typeof rec.error === "string" ? rec.error : undefined,
      message: typeof rec.message === "string" ? rec.message : undefined,
    };
    return parsed.error ?? parsed.message ?? `Request failed (${status})`;
  }
  return `Request failed (${status})`;
}

// parseErrorBody reads the error response body once, JSON-parsing it when
// possible and falling back to the raw text. Returns the parsed value (or the
// raw string), the raw text (back-compat/debug), and the optional `code`
// lifted from a JSON envelope.
async function parseErrorBody(
  response: Response,
): Promise<{ body: unknown; bodyText: string; code?: string }> {
  const bodyText = await response.text().catch(() => "");
  if (!bodyText) {
    return { body: null, bodyText };
  }
  try {
    const parsed = JSON.parse(bodyText) as unknown;
    let code: string | undefined;
    if (parsed && typeof parsed === "object") {
      const c = (parsed as Record<string, unknown>).code;
      if (typeof c === "string") {
        code = c;
      }
    }
    return code === undefined ? { body: parsed, bodyText } : { body: parsed, bodyText, code };
  } catch {
    return { body: bodyText, bodyText };
  }
}

async function httpErrorFromResponse(response: Response): Promise<HTTPError> {
  const { body, bodyText, code } = await parseErrorBody(response);
  return new HTTPError(response.status, bodyText, body, code);
}

// networkError wraps a rejected fetch (DNS/offline/CORS/abort) as a typed
// HTTPError with status 0 and code "network_error", preserving the original
// throwable as `cause`. TSC-02.
function networkError(cause: unknown): HTTPError {
  const message = cause instanceof Error ? cause.message : String(cause);
  return new HTTPError(0, message, null, "network_error", { cause });
}

// mergeAbortSignal returns a signal that fires when EITHER the caller's signal
// aborts OR the timeout elapses, plus a cleanup that clears the timer. Manual
// relay (no AbortSignal.any) keeps the client ES2022/Hermes-safe. TSC-13.
function mergeAbortSignal(
  signal: AbortSignal | undefined,
  timeoutMs: number | undefined,
): { signal: AbortSignal | undefined; cleanup: () => void } {
  if (timeoutMs === undefined || timeoutMs <= 0) {
    return { signal, cleanup: () => {} };
  }
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", onAbort);
    }
  }
  const timer = setTimeout(() => controller.abort(new DOMException("timeout", "AbortError")), timeoutMs);
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    },
  };
}
export interface APIKeyRoleCatalogEntry {
  description?: string;
  label?: string;
  role: string;
}
export interface AccessRequestItem {
  actions?: Array<string>;
  activatedAt?: string;
  approverIds?: Array<string>;
  clientId?: string;
  createdAt: string;
  denialReason?: string;
  expiresAt?: string;
  id: string;
  idempotencyKey?: string;
  justification?: string;
  linkedUserId?: string;
  permissions?: Array<string>;
  purpose?: string;
  requestedById?: string;
  requestedDurationSeconds?: number;
  resourceId: string;
  startTime?: string;
  status: string;
  subjectId: string;
  subjectLifecycle?: string;
  tenantId: string;
  ticketRef?: Record<string, unknown>;
  updatedAt: string;
}
export interface AdminAPIKeyResp {
  appId: string;
  createdAt: string;
  createdBy?: string;
  expiresAt: string;
  keyId: string;
  name: string;
  revokedAt?: string;
  roles: Array<string>;
  rotatedFrom?: string;
  subject: string;
}
export interface AdminAPIKeySecretResp {
  apiKey: string;
  expiresAt: string;
  keyId: string;
}
export interface AdminAccessRequestResp {
  actions?: Array<string>;
  activatedAt?: string;
  approverIds?: Array<string>;
  clientId?: string;
  createdAt: string;
  denialReason?: string;
  expiresAt?: string;
  id: string;
  idempotencyKey?: string;
  justification?: string;
  permissions?: Array<string>;
  purpose?: string;
  requestedById?: string;
  requestedDurationSeconds?: number;
  resourceId: string;
  startTime?: string;
  status: string;
  subjectId: string;
  tenantId: string;
  ticketRef?: Record<string, unknown>;
  updatedAt: string;
}
export interface AdminActivateBreakGlassReq {
  emergencyRoleId?: string;
  justification?: string;
  tenantId?: string;
}
export interface AdminAppClaimsSurfacePreviewResp {
  appId: string;
  clientsBound: number;
  exampleFailures?: Array<AdminClaimsSurfacePreviewFailure>;
  failCount: number;
  passCount: number;
  previewToken: string;
  previewTokenExpiresAt: number;
  sampleLimit: number;
  sampleTruncated: boolean;
  subjectsExamined: number;
  tenantId: string;
  view: string;
  warnings?: Array<string>;
}
export interface AdminAppClaimsSurfaceResp {
  appId: string;
  rules: Array<AdminClaimRuleItem>;
  tenantId: string;
  view: string;
}
export interface AdminAppItem {
  attributes?: string;
  createdAt: string;
  description?: string;
  id: string;
  name: string;
  parentId?: string;
  path?: Array<string>;
  status: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminAppPasswordsResp {
  appPasswords: Array<Record<string, unknown>>;
}
export interface AdminAppResp {
  apiKeyRoleCatalog?: Array<APIKeyRoleCatalogEntry>;
  attributes?: string;
  createdAt: string;
  description?: string;
  id: string;
  name: string;
  parentId?: string;
  path?: Array<string>;
  status: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminAppRoleAssignmentItem {
  appId: string;
  createdAt: string;
  id: string;
  roleId: string;
  subjectId: string;
  tenantId: string;
}
export interface AdminAppRoleAssignmentResp {
  appId: string;
  createdAt: string;
  id: string;
  roleId: string;
  subjectId: string;
  tenantId: string;
}
export interface AdminAppRoleItem {
  appId: string;
  createdAt: string;
  description?: string;
  id: string;
  name: string;
  permissions: Array<string>;
  tenantId: string;
  updatedAt: string;
}
export interface AdminAppRoleResp {
  appId: string;
  createdAt: string;
  description?: string;
  id: string;
  name: string;
  permissions: Array<string>;
  tenantId: string;
  updatedAt: string;
}
export interface AdminApproveAccessRequestReq {
  rationale: string;
  requestId?: string;
  tenantId?: string;
}
export interface AdminAttestationResp {
}
export interface AdminBreakGlassEventResp {
}
export interface AdminBrokeredSessionResp {
}
export interface AdminCAEConfigResp {
  thresholds: Record<string, unknown>;
}
export interface AdminCAEEventResp {
  eventType: string;
  id: string;
  occurredAt: string;
  payload?: Record<string, unknown>;
  recordedAt: string;
  sessionId?: string;
  severity: string;
  source: string;
  subjectRef: string;
  tenantId: string;
}
export interface AdminCAERiskStateResp {
  computedAt: string;
  contributingIds?: Array<string>;
  currentLevel: string;
  sessionId?: string;
  subjectRef: string;
  tenantId: string;
}
export interface AdminCampaignResp {
}
export interface AdminClaimRuleItem {
  key?: string;
  name: string;
  required?: boolean;
  scopes?: Array<string>;
  source?: string;
  suppress?: boolean;
  tokens?: Array<string>;
  value?: string;
}
export interface AdminClaimsSurfacePreviewFailure {
  missingRequired: Array<string>;
  subjectId: string;
}
export interface AdminClientCAListResp {
}
export interface AdminClientCAResp {
  createdAt?: string;
  enabled?: boolean;
  fingerprint?: string;
  id?: string;
  label?: string;
  notAfter?: string;
  pem?: string;
}
export interface AdminClientItem {
  appId?: string;
  createdAt: string;
  grantTypes: Array<string>;
  id: string;
  jwksUri?: string;
  name: string;
  postLogoutRedirectUris?: Array<string>;
  public: boolean;
  redirectUris: Array<string>;
  scopes: Array<string>;
  tenantId: string;
  tokenEndpointAuthMethod?: string;
  tokenExchangeEnabled: boolean;
  updatedAt: string;
}
export interface AdminClientResp {
  appId?: string;
  createdAt: string;
  grantTypes: Array<string>;
  id: string;
  jwksUri?: string;
  name: string;
  postLogoutRedirectUris?: Array<string>;
  public: boolean;
  redirectUris: Array<string>;
  scopes: Array<string>;
  serviceAccountRoles?: Array<string>;
  tenantId: string;
  tokenEndpointAuthMethod?: string;
  tokenExchangeEnabled: boolean;
  updatedAt: string;
}
export interface AdminCloseCampaignReq {
  campaignId?: string;
  tenantId?: string;
}
export interface AdminCompleteBreakGlassReviewReq {
  eventId?: string;
  notes?: string;
  tenantId?: string;
}
export interface AdminConnectorItem {
  config?: Record<string, unknown>;
  createdAt: string;
  enabled: boolean;
  id: string;
  name: string;
  tenantId: string;
  type: string;
  updatedAt: string;
}
export interface AdminConnectorResp {
  config?: Record<string, unknown>;
  createdAt: string;
  enabled: boolean;
  id: string;
  name: string;
  tenantId: string;
  type: string;
  updatedAt: string;
}
export interface AdminCreateAccessRequestReq {
  actions: Array<string>;
  approverIds?: Array<string>;
  clientId?: string;
  idempotencyKey?: string;
  justification?: string;
  permissions: Array<string>;
  purpose: string;
  requestedDurationSeconds: number;
  resourceId: string;
  startTime?: string;
  subjectId?: string;
  tenantId?: string;
  ticketRef: Record<string, unknown>;
}
export interface AdminCreateAppReq {
  apiKeyRoleCatalog?: Array<APIKeyRoleCatalogEntry>;
  attributes?: string;
  description?: string;
  name: string;
  parentId?: string;
  status?: string;
  tenantId?: string;
}
export interface AdminCreateAppRoleAssignmentReq {
  appId?: string;
  roleId?: string;
  subjectId: string;
  tenantId?: string;
}
export interface AdminCreateAppRoleReq {
  appId?: string;
  description?: string;
  name: string;
  permissions: Array<string>;
  tenantId?: string;
}
export interface AdminCreateCampaignReq {
  description?: string;
  dueAt: string;
  name: string;
  reviewers?: Array<string>;
  scope?: Record<string, unknown>;
  tenantId?: string;
}
export interface AdminCreateClientCAReq {
  label: string;
  pem: string;
  tenantId?: string;
}
export interface AdminCreateClientReq {
  appId?: string;
  clientId?: string;
  grantTypes?: Array<string>;
  jwksUri?: string;
  name: string;
  postLogoutRedirectUris?: Array<string>;
  public?: boolean;
  redirectUris: Array<string>;
  scopes?: Array<string>;
  serviceAccountRoles?: Array<string>;
  tenantId?: string;
  tokenEndpointAuthMethod?: string;
  tokenExchangeEnabled?: boolean;
}
export interface AdminCreateClientResp {
  appId?: string;
  clientSecret?: string;
  createdAt: string;
  grantTypes: Array<string>;
  id: string;
  jwksUri?: string;
  name: string;
  postLogoutRedirectUris?: Array<string>;
  public: boolean;
  redirectUris: Array<string>;
  scopes: Array<string>;
  serviceAccountRoles?: Array<string>;
  tenantId: string;
  tokenEndpointAuthMethod?: string;
  tokenExchangeEnabled: boolean;
  updatedAt: string;
}
export interface AdminCreateConnectorReq {
  clientSecret?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  name: string;
  tenantId?: string;
  type: string;
}
export interface AdminCreateEmergencyRoleReq {
  maxTtlSeconds?: number;
  name: string;
  permissions?: Array<string>;
  resourceId?: string;
  scope?: Array<string>;
  tenantId?: string;
}
export interface AdminCreateEntitlementReq {
  candidateSubjects: Array<string>;
  conflictingGroups?: Array<Array<string>>;
  maxDurationSeconds: number;
  permissions?: Array<string>;
  requiredApprovals?: number;
  resourceId: string;
  selfApprovalAllowed?: boolean;
  sensitivity?: string;
  tenantId?: string;
}
export interface AdminCreatePolicyReq {
  conditions?: Array<Record<string, unknown>>;
  description?: string;
  effect: string;
  enabled?: boolean;
  name: string;
  policyId?: string;
  priority?: number;
  target?: Record<string, unknown>;
  tenantId?: string;
}
export interface AdminCreateResourceReq {
  appId?: string;
  approvalPolicyRef?: string;
  environment?: string;
  metadata?: string;
  name: string;
  nativeType: string;
  owner?: string;
  sensitivity?: string;
  tenantId?: string;
}
export interface AdminCreateRoleDefReq {
  description?: string;
  name: string;
  permissions?: Array<string>;
  tenantId?: string;
}
export interface AdminCreateSubjectReq {
  activeThreatLevel?: string;
  assuranceLevel?: string;
  displayName?: string;
  externalIds?: Array<string>;
  lifecycle?: string;
  linkedUserId?: string;
  pamAccessDisabled?: boolean;
  privilegeEscalationDisabled?: boolean;
  riskContext?: string;
  riskLevel?: string;
  tenantId?: string;
  type: string;
}
export interface AdminCreateTenantReq {
  id: string;
  name: string;
  requireMtls?: boolean;
  status?: string;
}
export interface AdminCreateUserReq {
  accountLocked?: boolean;
  activeThreatLevel?: string;
  email: string;
  emailVerified?: boolean;
  initialPasswordResetRequired?: boolean;
  name?: string;
  pamAccessDisabled?: boolean;
  password: string;
  privilegeEscalationDisabled?: boolean;
  riskLevel?: string;
  roles?: Array<string>;
  tenantId?: string;
}
export interface AdminCredentialLeaseResp {
  expiresAt: string;
  id: string;
  issuedAt: string;
  issuerType: string;
  resourceId?: string;
  revokedAt?: string;
  scope?: string;
  sessionId?: string;
  state: string;
  subjectId: string;
  tenantId: string;
}
export interface AdminCredentialProviderResp {
  configured: boolean;
  issuerType: string;
}
export interface AdminDeleteAppRoleAssignmentReq {
  appId?: string;
  assignmentId?: string;
  roleId?: string;
  tenantId?: string;
}
export interface AdminDenyAccessRequestReq {
  rationale: string;
  requestId?: string;
  tenantId?: string;
}
export interface AdminDevicePostureResp {
  compliant?: boolean;
  deviceId: string;
  impossibleTravel?: boolean;
  managed?: boolean;
  riskScore?: number;
  source?: string;
  tenantId: string;
  updatedAt?: string;
  verified?: boolean;
}
export interface AdminDriftReportReq {
  tenantId?: string;
}
export interface AdminDriftReportResp {
  drift: Array<Record<string, unknown>>;
  driftCount: number;
  orphaned: Array<Record<string, unknown>>;
  orphanedCount: number;
  overPrivileged: Array<Record<string, unknown>>;
  overPrivilegedCount: number;
  stale: Array<Record<string, unknown>>;
  staleCount: number;
  tenantId: string;
  totalFlagged: number;
}
export interface AdminEmergencyRoleResp {
}
export interface AdminEntitlementItem {
  candidateSubjects?: Array<string>;
  conflictingGroups?: Array<Array<string>>;
  createdAt: string;
  id: string;
  maxDurationSeconds: number;
  permissions?: Array<string>;
  requiredApprovals?: number;
  resourceId: string;
  selfApprovalAllowed?: boolean;
  sensitivity?: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminEntitlementResp {
  candidateSubjects?: Array<string>;
  conflictingGroups?: Array<Array<string>>;
  createdAt: string;
  id: string;
  maxDurationSeconds: number;
  permissions?: Array<string>;
  requiredApprovals?: number;
  resourceId: string;
  selfApprovalAllowed?: boolean;
  sensitivity?: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminEphemeralCredResp {
  caPubKey?: string;
  credentialMaterial: string;
  issuerType: string;
  leaseId: string;
  validUntil: string;
}
export interface AdminExtendAccessRequestReq {
  newDurationSeconds: number;
  requestId?: string;
  tenantId?: string;
}
export interface AdminGetAPIKeyReq {
  keyId?: string;
  tenantId?: string;
}
export interface AdminGetAccessRequestReq {
  requestId?: string;
  tenantId?: string;
}
export interface AdminGetAppClaimsSurfaceReq {
  appId?: string;
  resolved?: string;
  tenantId?: string;
}
export interface AdminGetAppReq {
  appId?: string;
  tenantId?: string;
}
export interface AdminGetAppRoleReq {
  appId?: string;
  roleId?: string;
  tenantId?: string;
}
export interface AdminGetAuditCaseFileReq {
  requestId?: string;
  tenantId?: string;
}
export interface AdminGetAuditChainIntegrityReq {
  tenantId?: string;
}
export interface AdminGetAuditChainIntegrityResp {
  anomalies?: Array<ChainAnomaly>;
  tamperSuspected?: boolean;
  total?: number;
  truncated?: boolean;
}
export interface AdminGetBreakGlassEventReq {
  eventId?: string;
  tenantId?: string;
}
export interface AdminGetBrokeredSessionRecordingsReq {
  cursor?: string;
  limit?: number;
  sessionId?: string;
  tenantId?: string;
}
export interface AdminGetBrokeredSessionRecordingsResp {
  entries: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface AdminGetBrokeredSessionReq {
  sessionId?: string;
  tenantId?: string;
}
export interface AdminGetCAEConfigReq {
  tenantId?: string;
}
export interface AdminGetCAEEventReq {
  eventId?: string;
  tenantId?: string;
}
export interface AdminGetCAERiskStateReq {
  subjectId?: string;
  tenantId?: string;
}
export interface AdminGetCampaignReq {
  campaignId?: string;
  tenantId?: string;
}
export interface AdminGetClientCAReq {
  id?: string;
}
export interface AdminGetClientReq {
  clientId?: string;
  tenantId?: string;
}
export interface AdminGetConnectorReq {
  connectorId?: string;
  tenantId?: string;
}
export interface AdminGetCredentialLeaseReq {
  id?: string;
  tenantId?: string;
}
export interface AdminGetDevicePostureReq {
  deviceId?: string;
  tenantId?: string;
}
export interface AdminGetEmergencyRoleReq {
  roleId?: string;
  tenantId?: string;
}
export interface AdminGetEntitlementReq {
  entitlementId?: string;
  tenantId?: string;
}
export interface AdminGetMFAPolicyReq {
  tenantId?: string;
}
export interface AdminGetMailProviderReq {
  tenantId?: string;
}
export interface AdminGetPolicyReq {
  policyId?: string;
  tenantId?: string;
}
export interface AdminGetProfilePolicyReq {
  tenantId?: string;
}
export interface AdminGetResourceReq {
  resourceId?: string;
  tenantId?: string;
}
export interface AdminGetRoleDefReq {
  name?: string;
  tenantId?: string;
}
export interface AdminGetSecretsProviderReq {
  tenantId?: string;
}
export interface AdminGetSessionControlPolicyReq {
  resourceId?: string;
  tenantId?: string;
}
export interface AdminGetStepUpPolicyReq {
  tenantId?: string;
}
export interface AdminGetStepUpPolicyResp {
  maxAgeSecs: number;
  requirePhishingResistant: boolean;
  requiredLevel: string;
}
export interface AdminGetSubjectReq {
  subjectId?: string;
  tenantId?: string;
}
export interface AdminGetSubjectSecurityHistoryReq {
  cursor?: string;
  from?: string;
  limit?: number;
  subjectId?: string;
  tenantId?: string;
  to?: string;
}
export interface AdminGetTenantReq {
  tenantId?: string;
}
export interface AdminGetUserProfileReq {
  tenantId?: string;
  userId?: string;
}
export interface AdminGetUserReq {
  tenantId?: string;
  userId?: string;
}
export interface AdminGetUserSecurityHistoryReq {
  cursor?: string;
  from?: string;
  limit?: number;
  tenantId?: string;
  to?: string;
  userId?: string;
}
export interface AdminGetUserSecurityHistoryResp {
  activeThreatPoints: Array<AdminUserActiveThreatHistoryPoint>;
  nextCursor?: string;
  riskPoints: Array<AdminUserRiskHistoryPoint>;
}
export interface AdminGovernanceActionResp {
}
export interface AdminGovernanceApproveReq {
  actionId?: string;
  tenantId?: string;
}
export interface AdminGovernanceAssignRoleReq {
  kind: string;
  subjectId?: string;
  tenantId?: string;
}
export interface AdminGovernanceListActionsReq {
  tenantId?: string;
}
export interface AdminGovernanceListActionsResp {
  actions: Array<Record<string, unknown>>;
  nextCursor: string;
}
export interface AdminGovernanceListRolesReq {
  tenantId?: string;
}
export interface AdminGovernanceListRolesResp {
  assignments: Array<Record<string, unknown>>;
  note: string;
}
export interface AdminGovernanceProposeReq {
  kind: string;
  payloadRef?: string;
  tenantId?: string;
}
export interface AdminGovernanceRejectReq {
  actionId?: string;
  tenantId?: string;
}
export interface AdminGovernanceRunDiscoveryReq {
  tenantId?: string;
}
export interface AdminGovernanceRunDiscoveryResp {
  count: number;
  persisted: number;
  tenantId: string;
}
export interface AdminInitiateBrokeredSessionReq {
  driverType: string;
  grantId?: string;
  resourceId: string;
  sshPublicKey?: string;
  subjectId: string;
  tenantId?: string;
}
export interface AdminIssueEphemeralCredReq {
  grantId: string;
  issuerParams?: Record<string, unknown>;
  issuerType: string;
  resourceId: string;
  scope?: string;
  sessionId?: string;
  subjectId: string;
  tenantId?: string;
  ttlSeconds?: number;
}
export interface AdminKillBrokeredSessionReq {
  reason?: string;
  sessionId?: string;
  tenantId?: string;
}
export interface AdminLaunchCampaignReq {
  campaignId?: string;
  tenantId?: string;
}
export interface AdminListAPIKeysReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListAPIKeysResp {
  apiKeys: Array<AdminAPIKeyResp>;
  nextCursor?: string;
}
export interface AdminListAccessRequestsReq {
  approver?: string;
  cursor?: string;
  limit?: number;
  resource?: string;
  status?: string;
  subject?: string;
  tenantId?: string;
  unexpired?: boolean;
}
export interface AdminListAccessRequestsResp {
  items: Array<AccessRequestItem>;
  nextCursor?: string;
}
export interface AdminListAppChildrenReq {
  appId?: string;
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListAppRoleAssignmentsReq {
  appId?: string;
  roleId?: string;
  tenantId?: string;
}
export interface AdminListAppRoleAssignmentsResp {
  assignments: Array<AdminAppRoleAssignmentItem>;
}
export interface AdminListAppRolesReq {
  appId?: string;
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListAppRolesResp {
  nextCursor?: string;
  roles: Array<AdminAppRoleItem>;
}
export interface AdminListAppsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListAppsResp {
  apps: Array<AdminAppItem>;
  nextCursor?: string;
}
export interface AdminListAttestationsReq {
  campaignId?: string;
  tenantId?: string;
}
export interface AdminListAttestationsResp {
  attestations: Array<Record<string, unknown>>;
  nextCursor: string;
}
export interface AdminListBreakGlassEventsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListBreakGlassEventsResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface AdminListBrokeredSessionsReq {
  resource?: string;
  state?: string;
  subject?: string;
  tenantId?: string;
}
export interface AdminListBrokeredSessionsResp {
  nextCursor?: string;
  sessions: Array<Record<string, unknown>>;
}
export interface AdminListCAEEventsReq {
  cursor?: string;
  limit?: number;
  sessionId?: string;
  subjectRef?: string;
  tenantId?: string;
}
export interface AdminListCAEEventsResp {
  events: Array<AdminCAEEventResp>;
  nextCursor?: string;
}
export interface AdminListCampaignsResp {
  campaigns: Array<Record<string, unknown>>;
  nextCursor: string;
}
export interface AdminListClientsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListClientsResp {
  clients: Array<AdminClientItem>;
  nextCursor?: string;
}
export interface AdminListConnectorsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListConnectorsResp {
  connectors: Array<AdminConnectorItem>;
  nextCursor?: string;
}
export interface AdminListCredentialLeasesReq {
  sessionId?: string;
  subjectId?: string;
  tenantId?: string;
}
export interface AdminListCredentialLeasesResp {
  leases: Array<AdminCredentialLeaseResp>;
}
export interface AdminListCredentialProvidersReq {
  tenantId?: string;
}
export interface AdminListCredentialProvidersResp {
  providers: Array<AdminCredentialProviderResp>;
}
export interface AdminListDiscoveryReq {
  tenantId?: string;
}
export interface AdminListDiscoveryResp {
  items: Array<Record<string, unknown>>;
  nextCursor: string;
}
export interface AdminListEmergencyRolesReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListEmergencyRolesResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface AdminListEntitlementsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListEntitlementsResp {
  entitlements: Array<AdminEntitlementItem>;
  nextCursor?: string;
}
export interface AdminListHighRiskSubjectsReq {
  cursor?: string;
  limit?: number;
  minLevel?: string;
  tenantId?: string;
}
export interface AdminListHighRiskSubjectsResp {
  nextCursor?: string;
  subjects: Array<AdminCAERiskStateResp>;
}
export interface AdminListPoliciesReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListPoliciesResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface AdminListResourcesReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListResourcesResp {
  nextCursor?: string;
  resources: Array<AdminResourceItem>;
}
export interface AdminListRoleDefsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListRoleDefsResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface AdminListSubjectsReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListSubjectsResp {
  nextCursor?: string;
  subjects: Array<AdminSubjectItem>;
}
export interface AdminListTenantClientCAsReq {
  tenantId?: string;
}
export interface AdminListTenantsReq {
  cursor?: string;
  limit?: number;
}
export interface AdminListTenantsResp {
  nextCursor?: string;
  tenants: Array<AdminTenantResp>;
}
export interface AdminListUserAppPasswordsReq {
  tenantId?: string;
  userId?: string;
}
export interface AdminListUserFactorsReq {
  tenantId?: string;
  userId?: string;
}
export interface AdminListUserFactorsResp {
  factors: Array<AdminUserFactorItem>;
}
export interface AdminListUsersReq {
  cursor?: string;
  limit?: number;
  tenantId?: string;
}
export interface AdminListUsersResp {
  nextCursor?: string;
  users: Array<AdminUserItem>;
}
export interface AdminMFAPolicyResp {
  nonDangerousLevel: string;
  nonDangerousRequired: boolean;
  requireForAdminLogin: boolean;
  requireSecondFactorForPasskeyPrimary: boolean;
}
export interface AdminMTLSEmptyReq {
}
export interface AdminMTLSStatusResp {
  count: number;
  enabled: boolean;
}
export interface AdminMailProviderResp {
  createdAt: string;
  credentialSet: boolean;
  envLocked: boolean;
  fromAddr?: string;
  host: string;
  port?: number;
  tenantId: string;
  tlsMode?: string;
  updatedAt: string;
  username?: string;
}
export interface AdminMintAPIKeyReq {
  appId: string;
  audience: string;
  name: string;
  roles: Array<string>;
  tenantId?: string;
  ttlSeconds?: number;
}
export interface AdminOverrideCAERiskReq {
  level: string;
  reason: string;
  subjectId?: string;
  tenantId?: string;
}
export interface AdminOverrideCAERiskResp {
  level: string;
  status: string;
}
export interface AdminPAMMaintenanceToggleReq {
  enabled: boolean;
  tenantId: string;
}
export interface AdminPAMMaintenanceToggleResp {
  maintenanceMode: boolean;
}
export interface AdminPAMResilienceHealthReq {
  tenantId?: string;
}
export interface AdminPAMResilienceHealthResp {
}
export interface AdminPAMResilienceStatusReq {
  tenantId?: string;
}
export interface AdminPAMResilienceStatusResp {
}
export interface AdminPAMSLODashboardReq {
  tenantId?: string;
}
export interface AdminPAMSLODashboardResp {
}
export interface AdminPolicyCondJSON {
  field: string;
  operator: string;
  value: string;
}
export interface AdminPolicyResp {
  conditions?: Array<Record<string, unknown>>;
  createdAt?: string;
  description?: string;
  effect?: string;
  enabled?: boolean;
  id?: string;
  name?: string;
  priority?: number;
  target?: Record<string, unknown>;
  tenantId?: string;
  updatedAt?: string;
  version?: number;
}
export interface AdminPolicyTargetJSON {
  actions?: Array<string>;
  properties?: Array<string>;
  resourceIds?: Array<string>;
  resourceTypes?: Array<string>;
}
export interface AdminPolicyVersionReq {
  policyId?: string;
  tenantId?: string;
  version?: string;
}
export interface AdminPolicyVersionsReq {
  policyId?: string;
  tenantId?: string;
}
export interface AdminPolicyVersionsResp {
  items: Array<Record<string, unknown>>;
}
export interface AdminPreviewAppClaimsSurfaceReq {
  appId?: string;
  rules?: Array<AdminClaimRuleItem>;
  tenantId?: string;
}
export interface AdminProfilePolicyResp {
  allowUserAttributes: boolean;
  appPasswordScopes: Array<string>;
  appPasswordsEnabled: boolean;
  hrEnabled: boolean;
  maxAppPasswords: number;
  tenantManagedFields: Array<string>;
}
export interface AdminPutAppClaimsSurfaceReq {
  appId?: string;
  previewToken?: string;
  rules?: Array<AdminClaimRuleItem>;
  tenantId?: string;
}
export interface AdminPutMailProviderReq {
  credential?: string;
  fromAddr?: string;
  host: string;
  port?: number;
  tenantId?: string;
  tlsMode?: string;
  username?: string;
}
export interface AdminPutProfilePolicyReq {
  allowUserAttributes: boolean;
  appPasswordScopes?: Array<string>;
  appPasswordsEnabled: boolean;
  hrEnabled: boolean;
  maxAppPasswords: number;
  tenantId?: string;
  tenantManagedFields?: Array<string>;
}
export interface AdminPutSecretsProviderReq {
  address?: string;
  backend: string;
  credential?: string;
  endpoint?: string;
  kvMount?: string;
  namespace?: string;
  project?: string;
  region?: string;
  tenantId?: string;
  vaultUrl?: string;
}
export interface AdminQueryAuditReq {
  actor?: string;
  cursor?: string;
  event?: string;
  jti?: string;
  limit?: number;
  outcome?: string;
  pamRequestId?: string;
  sessionId?: string;
  since?: string;
  tenantId?: string;
  type?: string;
  until?: string;
}
export interface AdminQueryAuditResp {
  nextCursor?: string;
  records?: Array<AuditRecord>;
}
export interface AdminRebaselineAuditChainReq {
  override?: boolean;
  overrideReason?: string;
  tenantId?: string;
}
export interface AdminRebaselineAuditChainResp {
  overrideUsed?: boolean;
  sealedAnomalies?: number;
  sealedCount?: number;
  sealedThrough?: string;
  tamperSuspected?: boolean;
}
export interface AdminResourceItem {
  appId?: string;
  approvalPolicyRef?: string;
  createdAt: string;
  environment?: string;
  id: string;
  name: string;
  nativeType: string;
  owner?: string;
  sensitivity?: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminResourceResp {
  appId?: string;
  approvalPolicyRef?: string;
  createdAt: string;
  environment?: string;
  id: string;
  name: string;
  nativeType: string;
  owner?: string;
  sensitivity?: string;
  tenantId: string;
  updatedAt: string;
}
export interface AdminResumeBrokeredSessionReq {
  sessionId?: string;
  stepUpSatisfied?: string;
  tenantId?: string;
}
export interface AdminRevokeCredentialLeaseResp {
  leaseId: string;
  status: string;
}
export interface AdminRevokeUserAppPasswordReq {
  appPasswordId?: string;
  tenantId?: string;
  userId?: string;
}
export interface AdminRevokeUserAppPasswordResp {
  ok: boolean;
}
export interface AdminRevokeUserFactorReq {
  factorId?: string;
  factorKind?: string;
  tenantId?: string;
  userId?: string;
}
export interface AdminRevokeUserFactorResp {
  ok: boolean;
}
export interface AdminRoleDefResp {
}
export interface AdminRollbackPolicyReq {
  policyId?: string;
  tenantId?: string;
  toVersion: number;
}
export interface AdminRotateAPIKeyReq {
  audience: string;
  keyId?: string;
  tenantId?: string;
  ttlSeconds?: number;
}
export interface AdminRotateClientSecretReq {
  clientId?: string;
  tenantId?: string;
}
export interface AdminRotateClientSecretResp {
  clientId: string;
  clientSecret: string;
}
export interface AdminRotateCredentialKeyReq {
  issuerType: string;
  tenantId?: string;
}
export interface AdminRotateCredentialKeyResp {
  newKeyId: string;
  oldKeyId: string;
  rotatedAt: string;
}
export interface AdminRotateSigningKeysReq {
}
export interface AdminRotateSigningKeysResp {
}
export interface AdminSecretsProviderResp {
  address?: string;
  backend: string;
  createdAt: string;
  credentialSet: boolean;
  endpoint?: string;
  envLocked: boolean;
  kvMount?: string;
  namespace?: string;
  project?: string;
  region?: string;
  tenantId: string;
  updatedAt: string;
  vaultUrl?: string;
}
export interface AdminSessionControlPolicyResp {
  absTtlSeconds: number;
  allowPause: boolean;
  idleTtlSeconds: number;
  maxConcurrentPerSubject: number;
}
export interface AdminSetMFAPolicyReq {
  nonDangerousLevel: string;
  nonDangerousRequired: boolean;
  requireForAdminLogin: boolean;
  requireSecondFactorForPasskeyPrimary?: boolean;
  tenantId?: string;
}
export interface AdminSetSessionControlPolicyReq {
  absTtlSeconds: number;
  allowPause: boolean;
  idleTtlSeconds: number;
  maxConcurrentPerSubject: number;
  resourceId?: string;
  tenantId?: string;
}
export interface AdminSetUserProfileReq {
  hr?: Record<string, unknown>;
  removeAttributes?: Array<string>;
  setAttributes?: Record<string, unknown>;
  tenantId?: string;
  userId?: string;
}
export interface AdminSimulatePolicyReq {
  policies?: Array<Record<string, unknown>>;
  query: Record<string, unknown>;
  tenantId?: string;
}
export interface AdminSimulatePolicyResp {
  decision?: Record<string, unknown>;
  denyOverride?: boolean;
  matchedPolicies?: Array<string>;
}
export interface AdminSubjectItem {
  activeThreatLevel: string;
  assuranceLevel?: string;
  createdAt: string;
  displayName?: string;
  externalIds?: Array<string>;
  id: string;
  lifecycle: string;
  linkedUserId?: string;
  pamAccessDisabled: boolean;
  privilegeEscalationDisabled: boolean;
  riskLevel: string;
  tenantId: string;
  type: string;
  updatedAt: string;
}
export interface AdminSubjectResp {
  activeThreatLevel: string;
  assuranceLevel?: string;
  createdAt: string;
  displayName?: string;
  externalIds?: Array<string>;
  id: string;
  lifecycle: string;
  linkedUserId?: string;
  pamAccessDisabled: boolean;
  privilegeEscalationDisabled: boolean;
  riskLevel: string;
  tenantId: string;
  type: string;
  updatedAt: string;
}
export interface AdminSubmitAttestationReq {
  campaignId?: string;
  decision: string;
  discoveryId: string;
  justification?: string;
  tenantId?: string;
}
export interface AdminTenantResp {
  createdAt: string;
  id: string;
  issuer: string;
  name: string;
  protected: boolean;
  requireMtls: boolean;
  status: string;
  updatedAt: string;
}
export interface AdminTriggerCAEEvaluateReq {
  subjectId?: string;
  tenantId?: string;
}
export interface AdminTriggerCAEEvaluateResp {
  status: string;
}
export interface AdminUpdateAppReq {
  apiKeyRoleCatalog?: Array<APIKeyRoleCatalogEntry>;
  appId?: string;
  attributes?: string;
  description?: string;
  name?: string;
  parentId?: string;
  status?: string;
  tenantId?: string;
}
export interface AdminUpdateAppRoleReq {
  appId?: string;
  description?: string;
  name?: string;
  permissions?: Array<string>;
  roleId?: string;
  tenantId?: string;
}
export interface AdminUpdateCAEConfigReq {
  failedAuthBurstCritical?: number;
  failedAuthBurstHigh?: number;
  impossibleTravelKmCritical?: number;
  sensitiveActionRateHigh?: number;
  velocityAnomalyCritical?: number;
  velocityAnomalyHigh?: number;
  tenantId?: string;
}
export interface AdminUpdateCAEConfigResp {
  status: string;
}
export interface AdminUpdateClientCAReq {
  enabled?: boolean;
  id?: string;
}
export interface AdminUpdateClientReq {
  appId?: string;
  clientId?: string;
  grantTypes?: Array<string>;
  jwksUri?: string;
  name?: string;
  postLogoutRedirectUris?: Array<string>;
  redirectUris?: Array<string>;
  scopes?: Array<string>;
  serviceAccountRoles?: Array<string>;
  tenantId?: string;
  tokenEndpointAuthMethod?: string;
  tokenExchangeEnabled?: boolean;
}
export interface AdminUpdateConnectorReq {
  clientSecret?: string;
  config?: Record<string, unknown>;
  connectorId?: string;
  enabled?: boolean;
  name?: string;
  tenantId?: string;
  type?: string;
}
export interface AdminUpdateEmergencyRoleReq {
  maxTtlSeconds?: number;
  name: string;
  permissions?: Array<string>;
  resourceId?: string;
  roleId?: string;
  scope?: Array<string>;
  tenantId?: string;
}
export interface AdminUpdateEntitlementReq {
  candidateSubjects?: Array<string>;
  conflictingGroups?: Array<Array<string>>;
  entitlementId?: string;
  maxDurationSeconds?: number;
  permissions?: Array<string>;
  requiredApprovals?: number;
  resourceId?: string;
  selfApprovalAllowed?: boolean;
  sensitivity?: string;
  tenantId?: string;
}
export interface AdminUpdateResourceReq {
  appId?: string;
  approvalPolicyRef?: string;
  environment?: string;
  metadata?: string;
  name?: string;
  nativeType?: string;
  owner?: string;
  resourceId?: string;
  sensitivity?: string;
  tenantId?: string;
}
export interface AdminUpdateRoleDefReq {
  description?: string;
  name?: string;
  permissions?: Array<string>;
  tenantId?: string;
}
export interface AdminUpdateSubjectReq {
  activeThreatLevel?: string;
  assuranceLevel?: string;
  displayName?: string;
  externalIds?: Array<string>;
  lifecycle?: string;
  linkedUserId?: string;
  pamAccessDisabled?: boolean;
  privilegeEscalationDisabled?: boolean;
  riskContext?: string;
  riskLevel?: string;
  subjectId?: string;
  tenantId?: string;
  type?: string;
}
export interface AdminUpdateTenantReq {
  name?: string;
  requireMtls?: boolean;
  status?: string;
  tenantId?: string;
}
export interface AdminUpdateUserReq {
  accountLocked?: boolean;
  activeThreatLevel?: string;
  emailVerified?: boolean;
  name?: string;
  pamAccessDisabled?: boolean;
  passwordResetRequired?: boolean;
  privilegeEscalationDisabled?: boolean;
  riskLevel?: string;
  roles?: Array<string>;
  tenantId?: string;
  userId?: string;
}
export interface AdminUpsertDevicePostureReq {
  compliant?: boolean;
  deviceId: string;
  impossibleTravel?: boolean;
  managed?: boolean;
  riskScore?: number;
  source: string;
  tenantId?: string;
  verified?: boolean;
}
export interface AdminUserActiveThreatHistoryPoint {
  actorSub?: string;
  id: string;
  level: string;
  recordedAt: string;
  score: number;
  source: string;
}
export interface AdminUserFactorItem {
  createdAt?: string;
  id: string;
  kind: string;
  name?: string;
}
export interface AdminUserItem {
  accountLocked: boolean;
  activeThreatLevel: string;
  email: string;
  emailVerified: boolean;
  id: string;
  name: string;
  pamAccessDisabled: boolean;
  passwordResetRequired: boolean;
  privilegeEscalationDisabled: boolean;
  riskLevel: string;
  roles?: Array<string>;
  securityOnboardingRequired: boolean;
}
export interface AdminUserPasswordResetReq {
  password: string;
  tenantId?: string;
  userId?: string;
}
export interface AdminUserProfileResp {
  attributes: Record<string, unknown>;
  email?: string;
  emailVerified?: boolean;
  familyName?: string;
  givenName?: string;
  hr?: Record<string, unknown>;
  name?: string;
  picture?: string;
  sub: string;
  tenantManagedFields: Array<string>;
}
export interface AdminUserResp {
  accountLocked: boolean;
  activeThreatLevel: string;
  email: string;
  emailVerified: boolean;
  id: string;
  name: string;
  pamAccessDisabled: boolean;
  passwordResetRequired: boolean;
  privilegeEscalationDisabled: boolean;
  riskLevel: string;
  roles?: Array<string>;
  securityOnboardingRequired: boolean;
}
export interface AdminUserRevokeSessionsReq {
  tenantId?: string;
  userId?: string;
}
export interface AdminUserRiskHistoryPoint {
  actorSub?: string;
  id: string;
  level: string;
  recordedAt: string;
  score: number;
  source: string;
}
export interface AdminWhoAmIResp {
  adminRole?: string;
  permissions: Array<string>;
  roles: Array<string>;
  sub: string;
  tenant: string;
}
export interface AuditRecord {
  actorSub?: string;
  catalogCode?: string;
  event?: string;
  id?: string;
  jti?: string;
  outcome?: string;
  pamRequestId?: string;
  resource?: string;
  sessionId?: string;
  tenantId?: string;
  timestamp?: string;
}
export interface AuthorizationCodeTokenReq {
  clientId: string;
  clientSecret: string;
  code: string;
  codeVerifier: string;
  grantType: string;
  redirectUri: string;
}
export interface ChainAnomaly {
  atUnixNano?: number;
  computedRecordHash?: string;
  eventCode?: string;
  expectedPrevHash?: string;
  explanation?: string;
  index?: number;
  kind?: string;
  matchesIndex?: number;
  recordId?: string;
  storedPrevHash?: string;
  storedRecordHash?: string;
}
export interface DiscoverReq {
}
export interface DiscoverResp {
  authorizationEndpoint: string;
  grantType?: string;
  issuer: string;
  jwksUri: string;
  responseType?: string;
  scope?: string;
  subjectType?: string;
  tokenEndpoint: string;
  tokenSigningAlg?: string;
  userinfoEndpoint: string;
}
export interface EmailVerifyConfirmReq {
  token: string;
}
export interface EmailVerifyConfirmResp {
  verified: boolean;
}
export interface EmailVerifyRequestReq {
  email: string;
}
export interface EmailVerifyRequestResp {
  status: string;
}
export interface HealthReq {
}
export interface HealthResp {
  status: string;
  uptimeSeconds?: number;
  version?: string;
}
export interface InitBeginReq {
}
export interface InitBeginResp {
  initialized: boolean;
  steps: Array<string>;
}
export interface InitCompleteReq {
}
export interface InitCompleteResp {
  ok: boolean;
}
export interface InitCreateAdminReq {
  email?: string;
  name?: string;
  password?: string;
}
export interface InitCreateAdminResp {
  adminId?: string;
  ok: boolean;
}
export interface InitStatusReq {
}
export interface InitStatusResp {
  initialized: boolean;
  setupTokenEnabled: boolean;
  steps: Array<string>;
}
export interface JWK {
  alg: string;
  e: string;
  kid: string;
  kty: string;
  n: string;
  use: string;
}
export interface JWKSReq {
}
export interface JWKSResp {
  keys: Array<JWK>;
}
export interface ListRevokedAPIKeysReq {
  tenantId?: string;
}
export interface ListRevokedAPIKeysResp {
  refreshSeconds: number;
  revoked: Array<string>;
}
export interface MTLSAuthorizeReq {
  clientId: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  nonce?: string;
  redirectUri: string;
  scope?: string;
  state?: string;
}
export interface MTLSAuthorizeResp {
  code: string;
  redirectUri?: string;
  state?: string;
}
export interface MTLSCertificateRegisterReq {
  label?: string;
}
export interface MTLSCertificateRegisterResp {
  fingerprint: string;
  issuer?: string;
  notAfter?: string;
  subject?: string;
}
export interface MagicLinkRequestReq {
  clientId: string;
  codeChallenge: string;
  codeChallengeMethod?: string;
  email: string;
  nonce?: string;
  redirectUri: string;
  scope?: string;
  state?: string;
}
export interface MagicLinkRequestResp {
  status: string;
}
export interface MagicLinkVerifyReq {
  securityAnswers?: Array<SecurityQuestionItem>;
  token: string;
}
export interface MagicLinkVerifyResp {
  code?: string;
  mfaRequired?: boolean;
  redirectTo?: string;
  redirectUri?: string;
  responseKind: string;
  state?: string;
}
export interface MeAccessRequestResp {
  actions?: Array<string>;
  activatedAt?: string;
  approverIds?: Array<string>;
  clientId?: string;
  createdAt: string;
  denialReason?: string;
  expiresAt?: string;
  id: string;
  idempotencyKey?: string;
  justification?: string;
  permissions?: Array<string>;
  purpose?: string;
  requestedById?: string;
  requestedDurationSeconds?: number;
  resourceId: string;
  startTime?: string;
  status: string;
  subjectId: string;
  tenantId: string;
  ticketRef?: Record<string, unknown>;
  updatedAt: string;
}
export interface MeActivateAccessRequestReq {
  requestId?: string;
}
export interface MeAddEmailReq {
  email: string;
}
export interface MeAddEmailResp {
  status: string;
}
export interface MeAppPasswordsResp {
  appPasswords: Array<Record<string, unknown>>;
}
export interface MeApproveRequestReq {
  rationale: string;
  requestId: string;
}
export interface MeBrokeredSessionResp {
}
export interface MeCreateAccessRequestReq {
  actions: Array<string>;
  clientId?: string;
  idempotencyKey?: string;
  justification?: string;
  permissions: Array<string>;
  purpose: string;
  requestedDurationSeconds: number;
  resourceId: string;
  startTime?: string;
  ticketRef: Record<string, unknown>;
}
export interface MeCreateAppPasswordReq {
  expiresInDays?: number;
  label: string;
  scopes?: Array<string>;
}
export interface MeCreateAppPasswordResp {
  createdAt: string;
  expiresAt?: string;
  id: string;
  label: string;
  scopes?: Array<string>;
  secret: string;
}
export interface MeCredentialLeaseResp {
  expiresAt: string;
  id: string;
  issuedAt: string;
  issuerType: string;
  resourceId?: string;
  revokedAt?: string;
  scope?: string;
  sessionId?: string;
  state: string;
  subjectId: string;
  tenantId: string;
}
export interface MeDeleteEmailReq {
  emailId?: string;
}
export interface MeDeleteEmailResp {
  ok: boolean;
}
export interface MeDenyRequestReq {
  reason: string;
  requestId: string;
}
export interface MeEmailsResp {
  emails: Array<Record<string, unknown>>;
}
export interface MeEndSessionReq {
  sessionId?: string;
}
export interface MeEphemeralCredResp {
  caPubKey?: string;
  credentialMaterial: string;
  issuerType: string;
  leaseId: string;
  validUntil: string;
}
export interface MeGetAccessRequestReq {
  requestId?: string;
}
export interface MeGetCredentialReq {
  leaseId?: string;
}
export interface MeGetGrantReq {
  grantId?: string;
}
export interface MeGetGrantResp {
}
export interface MeGetProfileReq {
}
export interface MeGetSessionReq {
  sessionId?: string;
}
export interface MeInitiateSessionReq {
  driverType: string;
  grantId?: string;
  resourceId: string;
  sshPublicKey?: string;
}
export interface MeIssueCredentialReq {
  grantId?: string;
  issuerParams?: Record<string, unknown>;
  issuerType: string;
  resourceId?: string;
  scope?: string;
  ttlSeconds?: number;
}
export interface MeListAccessRequestsReq {
  cursor?: string;
  limit?: number;
}
export interface MeListAccessRequestsResp {
  items: Array<AccessRequestItem>;
  nextCursor?: string;
}
export interface MeListAppPasswordsReq {
}
export interface MeListApprovalsReq {
  cursor?: string;
  limit?: number;
}
export interface MeListApprovalsResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface MeListCredentialsReq {
}
export interface MeListCredentialsResp {
  leases: Array<MeCredentialLeaseResp>;
}
export interface MeListEmailsReq {
}
export interface MeListEntitlementsReq {
}
export interface MeListEntitlementsResp {
  items: Array<AdminEntitlementItem>;
}
export interface MeListGrantsReq {
  cursor?: string;
  limit?: number;
}
export interface MeListGrantsResp {
  items: Array<Record<string, unknown>>;
  nextCursor?: string;
}
export interface MeListSessionsReq {
}
export interface MeListSessionsResp {
  sessions: Array<Record<string, unknown>>;
}
export interface MeProfileResp {
  attributes: Record<string, unknown>;
  email?: string;
  emailVerified?: boolean;
  familyName?: string;
  givenName?: string;
  hr?: Record<string, unknown>;
  name?: string;
  picture?: string;
  sub: string;
  tenantManagedFields: Array<string>;
}
export interface MeRevokeAppPasswordReq {
  appPasswordId?: string;
}
export interface MeRevokeAppPasswordResp {
  ok: boolean;
}
export interface MeRevokeCredentialReq {
  leaseId?: string;
}
export interface MeRevokeCredentialResp {
  leaseId: string;
  status: string;
}
export interface MeSetPrimaryEmailReq {
  emailId?: string;
}
export interface MeUpdateProfileReq {
  familyName?: string;
  givenName?: string;
  name?: string;
  picture?: string;
  removeAttributes?: Array<string>;
  setAttributes?: Record<string, unknown>;
}
export interface OIDCAuthorizeRequest {
  acrValues?: string;
  clientId: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  loginHint?: string;
  maxAge?: number;
  nonce?: string;
  prompt?: string;
  redirectUri: string;
  responseType: string;
  scope?: string;
  state?: string;
}
export interface OIDCAuthorizeResponse {
  code: string;
  expiresIn?: number;
  redirectUri?: string;
  state?: string;
}
export interface OIDCEventMsg {
  clientId?: string;
  eventType: string;
  timestamp: string;
  userId?: string;
}
export interface OIDCTokenRequest {
  clientAssertion?: string;
  clientAssertionType?: string;
  clientId: string;
  clientSecret?: string;
  code?: string;
  codeVerifier?: string;
  grantType: string;
  password?: string;
  redirectUri?: string;
  refreshToken?: string;
  scope?: string;
  username?: string;
}
export interface OIDCTokenResponse {
  accessToken: string;
  expiresIn?: number;
  idToken?: string;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
  scope?: string;
  tokenType: string;
}
export interface OIDCUserinfoRequest {
}
export interface OIDCUserinfoResponse {
  email?: string;
  emailVerified?: boolean;
  familyName?: string;
  givenName?: string;
  locale?: string;
  name?: string;
  picture?: string;
  preferredUsername?: string;
  sub: string;
}
export interface PamAccessRequestActivationResp {
  accessToken: string;
  expiresIn: number;
  grantId: string;
  privileged: boolean;
  tokenType: string;
}
export interface PamActivationReturnReq {
  returnTo?: string;
}
export interface PamActivationReturnResp {
}
export interface PasswordForgotReq {
  email: string;
}
export interface PasswordForgotResp {
  status: string;
}
export interface PasswordResetReq {
  newPassword: string;
  securityAnswers?: Array<SecurityQuestionItem>;
  token: string;
}
export interface PasswordResetResp {
  reset: boolean;
}
export interface RecoveryKeyGenerateReq {
  tenantId?: string;
  userId?: string;
}
export interface RecoveryKeyGenerateResp {
  recoveryKey: string;
}
export interface RecoveryKeyRedeemReq {
  recoveryKey: string;
  tenantId?: string;
}
export interface RecoveryKeyRedeemResp {
  redeemed: boolean;
  userId: string;
}
export interface RefreshTokenReq {
  clientId: string;
  clientSecret: string;
  grantType: string;
  refreshToken: string;
}
export interface RegisterReq {
  email: string;
  familyName?: string;
  givenName?: string;
  name?: string;
  password: string;
}
export interface RegisterResp {
  emailVerified: boolean;
  userId: string;
}
export interface SecondaryEmailVerifyConfirmReq {
  token: string;
}
export interface SecondaryEmailVerifyConfirmResp {
  verified: boolean;
}
export interface SecurityQuestionItem {
  answer: string;
  question: string;
}
export interface SecurityQuestionsSetReq {
  questions: Array<SecurityQuestionItem>;
}
export interface SecurityQuestionsSetResp {
  count: number;
}
export interface SecurityQuestionsVerifyReq {
  answers: Array<SecurityQuestionItem>;
  email: string;
}
export interface SecurityQuestionsVerifyResp {
  verified: boolean;
}
export interface TOTPDisableReq {
  tenantId?: string;
  userId?: string;
}
export interface TOTPDisableResp {
  disabled: boolean;
}
export interface TOTPEnrollBeginReq {
  account?: string;
  tenantId?: string;
  userId?: string;
}
export interface TOTPEnrollBeginResp {
  otpauthUri: string;
  secret: string;
}
export interface TOTPEnrollVerifyReq {
  code: string;
  tenantId?: string;
  userId?: string;
}
export interface TOTPEnrollVerifyResp {
  verified: boolean;
}
export interface TicketRef {
  externalId: string;
  system: string;
}
export interface TokenExchangeReq {
  actorToken?: string;
  actorTokenType?: string;
  audience?: string;
  grantType: string;
  requestedTokenType?: string;
  resource?: string;
  scope?: string;
  subjectToken: string;
  subjectTokenType: string;
}
export interface WebAuthnAuthenticateBeginReq {
  clientId: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  nonce?: string;
  redirectUri: string;
  scope?: string;
  state?: string;
  userId?: string;
}
export interface WebAuthnAuthenticateBeginResp {
  options: Record<string, unknown>;
  sessionId: string;
}
export interface WebAuthnAuthenticateCompleteReq {
  clientId?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  credential: Record<string, unknown>;
  nonce?: string;
  redirectUri?: string;
  scope?: string;
  sessionId: string;
  state?: string;
}
export interface WebAuthnAuthenticateCompleteResp {
  code?: string;
  redirectTo?: string;
  redirectUri?: string;
  responseKind: string;
  state?: string;
}
export interface WebAuthnCredentialSummary {
  aaguid?: string;
  alias?: string;
  createdAt?: string;
  credentialId: string;
  transports?: Array<string>;
}
export interface WebAuthnDeleteCredentialReq {
  credentialId: string;
}
export interface WebAuthnDeleteCredentialResp {
  ok: boolean;
}
export interface WebAuthnEnrollmentStatusReq {
}
export interface WebAuthnEnrollmentStatusResp {
  enrolled: boolean;
}
export interface WebAuthnListCredentialsReq {
}
export interface WebAuthnListCredentialsResp {
  credentials: Array<WebAuthnCredentialSummary>;
}
export interface WebAuthnRegisterBeginReq {
  attestation?: string;
  authenticatorAttachment?: string;
  displayName?: string;
  userId?: string;
}
export interface WebAuthnRegisterBeginResp {
  options: Record<string, unknown>;
  sessionId: string;
}
export interface WebAuthnRegisterCompleteReq {
  credential: Record<string, unknown>;
  sessionId: string;
}
export interface WebAuthnRegisterCompleteResp {
  aaguid?: string;
  credentialId: string;
  transports?: Array<string>;
}
export interface WebAuthnSetCredentialAliasReq {
  alias: string;
  credentialId: string;
}
export interface WebAuthnSetCredentialAliasResp {
  ok: boolean;
}
// API_PREFIX is the common path prefix shared by every generated route. The
// env/same-origin/dev-default base-URL resolver stays app-side and is fed into
// createClient({ baseUrl }); this constant reproduces the prefix for callers
// that build URLs directly. TSC-18.
export const API_PREFIX = "/v1";
type PreparedBody = {
  body?: BodyInit;
  contentType?: string;
  bytes: ArrayBuffer;
};

type GraphQLError = { message: string };
type GraphQLEnvelope = {
  data?: Record<string, unknown>;
  errors?: GraphQLError[];
};

type GraphQLWSFrame = {
  id?: string;
  type: string;
  payload?: unknown;
};

const MAX_BUFFERED_MESSAGES = 64;

function copyRecord(value: unknown): Record<string, unknown> {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value !== "object") {
    return {};
  }
  if (Array.isArray(value)) {
    return {};
  }
  return { ...(value as Record<string, unknown>) };
}

const schemaWireKeys: Record<string, Record<string, string>> = {
  "APIKeyRoleCatalogEntry": {
    "description": "description",
    "label": "label",
    "role": "role",
  },
  "AccessRequestItem": {
    "actions": "actions",
    "activatedAt": "activated_at",
    "approverIds": "approver_ids",
    "clientId": "client_id",
    "createdAt": "created_at",
    "denialReason": "denial_reason",
    "expiresAt": "expires_at",
    "id": "id",
    "idempotencyKey": "idempotency_key",
    "justification": "justification",
    "linkedUserId": "linked_user_id",
    "permissions": "permissions",
    "purpose": "purpose",
    "requestedById": "requested_by_id",
    "requestedDurationSeconds": "requested_duration_seconds",
    "resourceId": "resource_id",
    "startTime": "start_time",
    "status": "status",
    "subjectId": "subject_id",
    "subjectLifecycle": "subject_lifecycle",
    "tenantId": "tenant_id",
    "ticketRef": "ticket_ref",
    "updatedAt": "updated_at",
  },
  "AdminAPIKeyResp": {
    "appId": "app_id",
    "createdAt": "created_at",
    "createdBy": "created_by",
    "expiresAt": "expires_at",
    "keyId": "key_id",
    "name": "name",
    "revokedAt": "revoked_at",
    "roles": "roles",
    "rotatedFrom": "rotated_from",
    "subject": "subject",
  },
  "AdminAPIKeySecretResp": {
    "apiKey": "api_key",
    "expiresAt": "expires_at",
    "keyId": "key_id",
  },
  "AdminAccessRequestResp": {
    "actions": "actions",
    "activatedAt": "activated_at",
    "approverIds": "approver_ids",
    "clientId": "client_id",
    "createdAt": "created_at",
    "denialReason": "denial_reason",
    "expiresAt": "expires_at",
    "id": "id",
    "idempotencyKey": "idempotency_key",
    "justification": "justification",
    "permissions": "permissions",
    "purpose": "purpose",
    "requestedById": "requested_by_id",
    "requestedDurationSeconds": "requested_duration_seconds",
    "resourceId": "resource_id",
    "startTime": "start_time",
    "status": "status",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
    "ticketRef": "ticket_ref",
    "updatedAt": "updated_at",
  },
  "AdminActivateBreakGlassReq": {
    "emergencyRoleId": "emergency_role_id",
    "justification": "justification",
    "tenantId": "tenantId",
  },
  "AdminAppClaimsSurfacePreviewResp": {
    "appId": "app_id",
    "clientsBound": "clients_bound",
    "exampleFailures": "example_failures",
    "failCount": "fail_count",
    "passCount": "pass_count",
    "previewToken": "preview_token",
    "previewTokenExpiresAt": "preview_token_expires_at",
    "sampleLimit": "sample_limit",
    "sampleTruncated": "sample_truncated",
    "subjectsExamined": "subjects_examined",
    "tenantId": "tenant_id",
    "view": "view",
    "warnings": "warnings",
  },
  "AdminAppClaimsSurfaceResp": {
    "appId": "app_id",
    "rules": "rules",
    "tenantId": "tenant_id",
    "view": "view",
  },
  "AdminAppItem": {
    "attributes": "attributes",
    "createdAt": "created_at",
    "description": "description",
    "id": "id",
    "name": "name",
    "parentId": "parent_id",
    "path": "path",
    "status": "status",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminAppPasswordsResp": {
    "appPasswords": "app_passwords",
  },
  "AdminAppResp": {
    "apiKeyRoleCatalog": "apiKeyRoleCatalog",
    "attributes": "attributes",
    "createdAt": "created_at",
    "description": "description",
    "id": "id",
    "name": "name",
    "parentId": "parent_id",
    "path": "path",
    "status": "status",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminAppRoleAssignmentItem": {
    "appId": "app_id",
    "createdAt": "created_at",
    "id": "id",
    "roleId": "role_id",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
  },
  "AdminAppRoleAssignmentResp": {
    "appId": "app_id",
    "createdAt": "created_at",
    "id": "id",
    "roleId": "role_id",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
  },
  "AdminAppRoleItem": {
    "appId": "app_id",
    "createdAt": "created_at",
    "description": "description",
    "id": "id",
    "name": "name",
    "permissions": "permissions",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminAppRoleResp": {
    "appId": "app_id",
    "createdAt": "created_at",
    "description": "description",
    "id": "id",
    "name": "name",
    "permissions": "permissions",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminApproveAccessRequestReq": {
    "rationale": "rationale",
    "requestId": "requestId",
    "tenantId": "tenantId",
  },
  "AdminAttestationResp": {
  },
  "AdminBreakGlassEventResp": {
  },
  "AdminBrokeredSessionResp": {
  },
  "AdminCAEConfigResp": {
    "thresholds": "thresholds",
  },
  "AdminCAEEventResp": {
    "eventType": "event_type",
    "id": "id",
    "occurredAt": "occurred_at",
    "payload": "payload",
    "recordedAt": "recorded_at",
    "sessionId": "session_id",
    "severity": "severity",
    "source": "source",
    "subjectRef": "subject_ref",
    "tenantId": "tenant_id",
  },
  "AdminCAERiskStateResp": {
    "computedAt": "computed_at",
    "contributingIds": "contributing_ids",
    "currentLevel": "current_level",
    "sessionId": "session_id",
    "subjectRef": "subject_ref",
    "tenantId": "tenant_id",
  },
  "AdminCampaignResp": {
  },
  "AdminClaimRuleItem": {
    "key": "key",
    "name": "name",
    "required": "required",
    "scopes": "scopes",
    "source": "source",
    "suppress": "suppress",
    "tokens": "tokens",
    "value": "value",
  },
  "AdminClaimsSurfacePreviewFailure": {
    "missingRequired": "missing_required",
    "subjectId": "subject_id",
  },
  "AdminClientCAListResp": {
  },
  "AdminClientCAResp": {
    "createdAt": "created_at",
    "enabled": "enabled",
    "fingerprint": "fingerprint",
    "id": "id",
    "label": "label",
    "notAfter": "not_after",
    "pem": "pem",
  },
  "AdminClientItem": {
    "appId": "app_id",
    "createdAt": "created_at",
    "grantTypes": "grant_types",
    "id": "id",
    "jwksUri": "jwks_uri",
    "name": "name",
    "postLogoutRedirectUris": "post_logout_redirect_uris",
    "public": "public",
    "redirectUris": "redirect_uris",
    "scopes": "scopes",
    "tenantId": "tenant_id",
    "tokenEndpointAuthMethod": "token_endpoint_auth_method",
    "tokenExchangeEnabled": "token_exchange_enabled",
    "updatedAt": "updated_at",
  },
  "AdminClientResp": {
    "appId": "app_id",
    "createdAt": "created_at",
    "grantTypes": "grant_types",
    "id": "id",
    "jwksUri": "jwks_uri",
    "name": "name",
    "postLogoutRedirectUris": "post_logout_redirect_uris",
    "public": "public",
    "redirectUris": "redirect_uris",
    "scopes": "scopes",
    "serviceAccountRoles": "service_account_roles",
    "tenantId": "tenant_id",
    "tokenEndpointAuthMethod": "token_endpoint_auth_method",
    "tokenExchangeEnabled": "token_exchange_enabled",
    "updatedAt": "updated_at",
  },
  "AdminCloseCampaignReq": {
    "campaignId": "campaignId",
    "tenantId": "tenantId",
  },
  "AdminCompleteBreakGlassReviewReq": {
    "eventId": "eventId",
    "notes": "notes",
    "tenantId": "tenantId",
  },
  "AdminConnectorItem": {
    "config": "config",
    "createdAt": "created_at",
    "enabled": "enabled",
    "id": "id",
    "name": "name",
    "tenantId": "tenant_id",
    "type": "type",
    "updatedAt": "updated_at",
  },
  "AdminConnectorResp": {
    "config": "config",
    "createdAt": "created_at",
    "enabled": "enabled",
    "id": "id",
    "name": "name",
    "tenantId": "tenant_id",
    "type": "type",
    "updatedAt": "updated_at",
  },
  "AdminCreateAccessRequestReq": {
    "actions": "actions",
    "approverIds": "approver_ids",
    "clientId": "client_id",
    "idempotencyKey": "idempotency_key",
    "justification": "justification",
    "permissions": "permissions",
    "purpose": "purpose",
    "requestedDurationSeconds": "requested_duration_seconds",
    "resourceId": "resource_id",
    "startTime": "start_time",
    "subjectId": "subject_id",
    "tenantId": "tenantId",
    "ticketRef": "ticket_ref",
  },
  "AdminCreateAppReq": {
    "apiKeyRoleCatalog": "apiKeyRoleCatalog",
    "attributes": "attributes",
    "description": "description",
    "name": "name",
    "parentId": "parent_id",
    "status": "status",
    "tenantId": "tenantId",
  },
  "AdminCreateAppRoleAssignmentReq": {
    "appId": "appId",
    "roleId": "roleId",
    "subjectId": "subject_id",
    "tenantId": "tenantId",
  },
  "AdminCreateAppRoleReq": {
    "appId": "appId",
    "description": "description",
    "name": "name",
    "permissions": "permissions",
    "tenantId": "tenantId",
  },
  "AdminCreateCampaignReq": {
    "description": "description",
    "dueAt": "due_at",
    "name": "name",
    "reviewers": "reviewers",
    "scope": "scope",
    "tenantId": "tenantId",
  },
  "AdminCreateClientCAReq": {
    "label": "label",
    "pem": "pem",
    "tenantId": "tenantId",
  },
  "AdminCreateClientReq": {
    "appId": "app_id",
    "clientId": "client_id",
    "grantTypes": "grant_types",
    "jwksUri": "jwks_uri",
    "name": "name",
    "postLogoutRedirectUris": "post_logout_redirect_uris",
    "public": "public",
    "redirectUris": "redirect_uris",
    "scopes": "scopes",
    "serviceAccountRoles": "service_account_roles",
    "tenantId": "tenantId",
    "tokenEndpointAuthMethod": "token_endpoint_auth_method",
    "tokenExchangeEnabled": "token_exchange_enabled",
  },
  "AdminCreateClientResp": {
    "appId": "app_id",
    "clientSecret": "client_secret",
    "createdAt": "created_at",
    "grantTypes": "grant_types",
    "id": "id",
    "jwksUri": "jwks_uri",
    "name": "name",
    "postLogoutRedirectUris": "post_logout_redirect_uris",
    "public": "public",
    "redirectUris": "redirect_uris",
    "scopes": "scopes",
    "serviceAccountRoles": "service_account_roles",
    "tenantId": "tenant_id",
    "tokenEndpointAuthMethod": "token_endpoint_auth_method",
    "tokenExchangeEnabled": "token_exchange_enabled",
    "updatedAt": "updated_at",
  },
  "AdminCreateConnectorReq": {
    "clientSecret": "client_secret",
    "config": "config",
    "enabled": "enabled",
    "name": "name",
    "tenantId": "tenantId",
    "type": "type",
  },
  "AdminCreateEmergencyRoleReq": {
    "maxTtlSeconds": "max_ttl_seconds",
    "name": "name",
    "permissions": "permissions",
    "resourceId": "resource_id",
    "scope": "scope",
    "tenantId": "tenantId",
  },
  "AdminCreateEntitlementReq": {
    "candidateSubjects": "candidate_subjects",
    "conflictingGroups": "conflicting_groups",
    "maxDurationSeconds": "max_duration_seconds",
    "permissions": "permissions",
    "requiredApprovals": "required_approvals",
    "resourceId": "resource_id",
    "selfApprovalAllowed": "self_approval_allowed",
    "sensitivity": "sensitivity",
    "tenantId": "tenantId",
  },
  "AdminCreatePolicyReq": {
    "conditions": "conditions",
    "description": "description",
    "effect": "effect",
    "enabled": "enabled",
    "name": "name",
    "policyId": "policyId",
    "priority": "priority",
    "target": "target",
    "tenantId": "tenantId",
  },
  "AdminCreateResourceReq": {
    "appId": "app_id",
    "approvalPolicyRef": "approval_policy_ref",
    "environment": "environment",
    "metadata": "metadata",
    "name": "name",
    "nativeType": "native_type",
    "owner": "owner",
    "sensitivity": "sensitivity",
    "tenantId": "tenantId",
  },
  "AdminCreateRoleDefReq": {
    "description": "description",
    "name": "name",
    "permissions": "permissions",
    "tenantId": "tenantId",
  },
  "AdminCreateSubjectReq": {
    "activeThreatLevel": "active_threat_level",
    "assuranceLevel": "assurance_level",
    "displayName": "display_name",
    "externalIds": "external_ids",
    "lifecycle": "lifecycle",
    "linkedUserId": "linked_user_id",
    "pamAccessDisabled": "pam_access_disabled",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskContext": "risk_context",
    "riskLevel": "risk_level",
    "tenantId": "tenantId",
    "type": "type",
  },
  "AdminCreateTenantReq": {
    "id": "id",
    "name": "name",
    "requireMtls": "requireMtls",
    "status": "status",
  },
  "AdminCreateUserReq": {
    "accountLocked": "account_locked",
    "activeThreatLevel": "active_threat_level",
    "email": "email",
    "emailVerified": "email_verified",
    "initialPasswordResetRequired": "initial_password_reset_required",
    "name": "name",
    "pamAccessDisabled": "pam_access_disabled",
    "password": "password",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "roles": "roles",
    "tenantId": "tenantId",
  },
  "AdminCredentialLeaseResp": {
    "expiresAt": "expires_at",
    "id": "id",
    "issuedAt": "issued_at",
    "issuerType": "issuer_type",
    "resourceId": "resource_id",
    "revokedAt": "revoked_at",
    "scope": "scope",
    "sessionId": "session_id",
    "state": "state",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
  },
  "AdminCredentialProviderResp": {
    "configured": "configured",
    "issuerType": "issuer_type",
  },
  "AdminDeleteAppRoleAssignmentReq": {
    "appId": "appId",
    "assignmentId": "assignmentId",
    "roleId": "roleId",
    "tenantId": "tenantId",
  },
  "AdminDenyAccessRequestReq": {
    "rationale": "rationale",
    "requestId": "requestId",
    "tenantId": "tenantId",
  },
  "AdminDevicePostureResp": {
    "compliant": "compliant",
    "deviceId": "device_id",
    "impossibleTravel": "impossible_travel",
    "managed": "managed",
    "riskScore": "risk_score",
    "source": "source",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
    "verified": "verified",
  },
  "AdminDriftReportReq": {
    "tenantId": "tenantId",
  },
  "AdminDriftReportResp": {
    "drift": "drift",
    "driftCount": "drift_count",
    "orphaned": "orphaned",
    "orphanedCount": "orphaned_count",
    "overPrivileged": "over_privileged",
    "overPrivilegedCount": "over_privileged_count",
    "stale": "stale",
    "staleCount": "stale_count",
    "tenantId": "tenant_id",
    "totalFlagged": "total_flagged",
  },
  "AdminEmergencyRoleResp": {
  },
  "AdminEntitlementItem": {
    "candidateSubjects": "candidate_subjects",
    "conflictingGroups": "conflicting_groups",
    "createdAt": "created_at",
    "id": "id",
    "maxDurationSeconds": "max_duration_seconds",
    "permissions": "permissions",
    "requiredApprovals": "required_approvals",
    "resourceId": "resource_id",
    "selfApprovalAllowed": "self_approval_allowed",
    "sensitivity": "sensitivity",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminEntitlementResp": {
    "candidateSubjects": "candidate_subjects",
    "conflictingGroups": "conflicting_groups",
    "createdAt": "created_at",
    "id": "id",
    "maxDurationSeconds": "max_duration_seconds",
    "permissions": "permissions",
    "requiredApprovals": "required_approvals",
    "resourceId": "resource_id",
    "selfApprovalAllowed": "self_approval_allowed",
    "sensitivity": "sensitivity",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminEphemeralCredResp": {
    "caPubKey": "ca_pub_key",
    "credentialMaterial": "credential_material",
    "issuerType": "issuer_type",
    "leaseId": "lease_id",
    "validUntil": "valid_until",
  },
  "AdminExtendAccessRequestReq": {
    "newDurationSeconds": "new_duration_seconds",
    "requestId": "requestId",
    "tenantId": "tenantId",
  },
  "AdminGetAPIKeyReq": {
    "keyId": "keyId",
    "tenantId": "tenantId",
  },
  "AdminGetAccessRequestReq": {
    "requestId": "requestId",
    "tenantId": "tenantId",
  },
  "AdminGetAppClaimsSurfaceReq": {
    "appId": "appId",
    "resolved": "resolved",
    "tenantId": "tenantId",
  },
  "AdminGetAppReq": {
    "appId": "appId",
    "tenantId": "tenantId",
  },
  "AdminGetAppRoleReq": {
    "appId": "appId",
    "roleId": "roleId",
    "tenantId": "tenantId",
  },
  "AdminGetAuditCaseFileReq": {
    "requestId": "requestId",
    "tenantId": "tenantId",
  },
  "AdminGetAuditChainIntegrityReq": {
    "tenantId": "tenantId",
  },
  "AdminGetAuditChainIntegrityResp": {
    "anomalies": "anomalies",
    "tamperSuspected": "tamper_suspected",
    "total": "total",
    "truncated": "truncated",
  },
  "AdminGetBreakGlassEventReq": {
    "eventId": "eventId",
    "tenantId": "tenantId",
  },
  "AdminGetBrokeredSessionRecordingsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "sessionId": "sessionId",
    "tenantId": "tenantId",
  },
  "AdminGetBrokeredSessionRecordingsResp": {
    "entries": "entries",
    "nextCursor": "next_cursor",
  },
  "AdminGetBrokeredSessionReq": {
    "sessionId": "sessionId",
    "tenantId": "tenantId",
  },
  "AdminGetCAEConfigReq": {
    "tenantId": "tenantId",
  },
  "AdminGetCAEEventReq": {
    "eventId": "eventId",
    "tenantId": "tenantId",
  },
  "AdminGetCAERiskStateReq": {
    "subjectId": "subjectId",
    "tenantId": "tenantId",
  },
  "AdminGetCampaignReq": {
    "campaignId": "campaignId",
    "tenantId": "tenantId",
  },
  "AdminGetClientCAReq": {
    "id": "id",
  },
  "AdminGetClientReq": {
    "clientId": "clientId",
    "tenantId": "tenantId",
  },
  "AdminGetConnectorReq": {
    "connectorId": "connectorId",
    "tenantId": "tenantId",
  },
  "AdminGetCredentialLeaseReq": {
    "id": "id",
    "tenantId": "tenantId",
  },
  "AdminGetDevicePostureReq": {
    "deviceId": "deviceId",
    "tenantId": "tenantId",
  },
  "AdminGetEmergencyRoleReq": {
    "roleId": "roleId",
    "tenantId": "tenantId",
  },
  "AdminGetEntitlementReq": {
    "entitlementId": "entitlementId",
    "tenantId": "tenantId",
  },
  "AdminGetMFAPolicyReq": {
    "tenantId": "tenantId",
  },
  "AdminGetMailProviderReq": {
    "tenantId": "tenantId",
  },
  "AdminGetPolicyReq": {
    "policyId": "policyId",
    "tenantId": "tenantId",
  },
  "AdminGetProfilePolicyReq": {
    "tenantId": "tenantId",
  },
  "AdminGetResourceReq": {
    "resourceId": "resourceId",
    "tenantId": "tenantId",
  },
  "AdminGetRoleDefReq": {
    "name": "name",
    "tenantId": "tenantId",
  },
  "AdminGetSecretsProviderReq": {
    "tenantId": "tenantId",
  },
  "AdminGetSessionControlPolicyReq": {
    "resourceId": "resourceId",
    "tenantId": "tenantId",
  },
  "AdminGetStepUpPolicyReq": {
    "tenantId": "tenant_id",
  },
  "AdminGetStepUpPolicyResp": {
    "maxAgeSecs": "max_age_secs",
    "requirePhishingResistant": "require_phishing_resistant",
    "requiredLevel": "required_level",
  },
  "AdminGetSubjectReq": {
    "subjectId": "subjectId",
    "tenantId": "tenantId",
  },
  "AdminGetSubjectSecurityHistoryReq": {
    "cursor": "cursor",
    "from": "from",
    "limit": "limit",
    "subjectId": "subjectId",
    "tenantId": "tenantId",
    "to": "to",
  },
  "AdminGetTenantReq": {
    "tenantId": "tenantId",
  },
  "AdminGetUserProfileReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminGetUserReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminGetUserSecurityHistoryReq": {
    "cursor": "cursor",
    "from": "from",
    "limit": "limit",
    "tenantId": "tenantId",
    "to": "to",
    "userId": "userId",
  },
  "AdminGetUserSecurityHistoryResp": {
    "activeThreatPoints": "active_threat_points",
    "nextCursor": "next_cursor",
    "riskPoints": "risk_points",
  },
  "AdminGovernanceActionResp": {
  },
  "AdminGovernanceApproveReq": {
    "actionId": "actionId",
    "tenantId": "tenantId",
  },
  "AdminGovernanceAssignRoleReq": {
    "kind": "kind",
    "subjectId": "subjectId",
    "tenantId": "tenantId",
  },
  "AdminGovernanceListActionsReq": {
    "tenantId": "tenantId",
  },
  "AdminGovernanceListActionsResp": {
    "actions": "actions",
    "nextCursor": "next_cursor",
  },
  "AdminGovernanceListRolesReq": {
    "tenantId": "tenantId",
  },
  "AdminGovernanceListRolesResp": {
    "assignments": "assignments",
    "note": "note",
  },
  "AdminGovernanceProposeReq": {
    "kind": "kind",
    "payloadRef": "payload_ref",
    "tenantId": "tenantId",
  },
  "AdminGovernanceRejectReq": {
    "actionId": "actionId",
    "tenantId": "tenantId",
  },
  "AdminGovernanceRunDiscoveryReq": {
    "tenantId": "tenantId",
  },
  "AdminGovernanceRunDiscoveryResp": {
    "count": "count",
    "persisted": "persisted",
    "tenantId": "tenant_id",
  },
  "AdminInitiateBrokeredSessionReq": {
    "driverType": "driver_type",
    "grantId": "grant_id",
    "resourceId": "resource_id",
    "sshPublicKey": "ssh_public_key",
    "subjectId": "subject_id",
    "tenantId": "tenantId",
  },
  "AdminIssueEphemeralCredReq": {
    "grantId": "grant_id",
    "issuerParams": "issuer_params",
    "issuerType": "issuer_type",
    "resourceId": "resource_id",
    "scope": "scope",
    "sessionId": "session_id",
    "subjectId": "subject_id",
    "tenantId": "tenantId",
    "ttlSeconds": "ttl_seconds",
  },
  "AdminKillBrokeredSessionReq": {
    "reason": "reason",
    "sessionId": "sessionId",
    "tenantId": "tenantId",
  },
  "AdminLaunchCampaignReq": {
    "campaignId": "campaignId",
    "tenantId": "tenantId",
  },
  "AdminListAPIKeysReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListAPIKeysResp": {
    "apiKeys": "api_keys",
    "nextCursor": "next_cursor",
  },
  "AdminListAccessRequestsReq": {
    "approver": "approver",
    "cursor": "cursor",
    "limit": "limit",
    "resource": "resource",
    "status": "status",
    "subject": "subject",
    "tenantId": "tenantId",
    "unexpired": "unexpired",
  },
  "AdminListAccessRequestsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListAppChildrenReq": {
    "appId": "appId",
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListAppRoleAssignmentsReq": {
    "appId": "appId",
    "roleId": "roleId",
    "tenantId": "tenantId",
  },
  "AdminListAppRoleAssignmentsResp": {
    "assignments": "assignments",
  },
  "AdminListAppRolesReq": {
    "appId": "appId",
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListAppRolesResp": {
    "nextCursor": "next_cursor",
    "roles": "roles",
  },
  "AdminListAppsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListAppsResp": {
    "apps": "apps",
    "nextCursor": "next_cursor",
  },
  "AdminListAttestationsReq": {
    "campaignId": "campaignId",
    "tenantId": "tenantId",
  },
  "AdminListAttestationsResp": {
    "attestations": "attestations",
    "nextCursor": "next_cursor",
  },
  "AdminListBreakGlassEventsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListBreakGlassEventsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListBrokeredSessionsReq": {
    "resource": "resource",
    "state": "state",
    "subject": "subject",
    "tenantId": "tenantId",
  },
  "AdminListBrokeredSessionsResp": {
    "nextCursor": "next_cursor",
    "sessions": "sessions",
  },
  "AdminListCAEEventsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "sessionId": "session_id",
    "subjectRef": "subject_ref",
    "tenantId": "tenantId",
  },
  "AdminListCAEEventsResp": {
    "events": "events",
    "nextCursor": "next_cursor",
  },
  "AdminListCampaignsResp": {
    "campaigns": "campaigns",
    "nextCursor": "next_cursor",
  },
  "AdminListClientsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListClientsResp": {
    "clients": "clients",
    "nextCursor": "next_cursor",
  },
  "AdminListConnectorsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListConnectorsResp": {
    "connectors": "connectors",
    "nextCursor": "next_cursor",
  },
  "AdminListCredentialLeasesReq": {
    "sessionId": "session_id",
    "subjectId": "subject_id",
    "tenantId": "tenantId",
  },
  "AdminListCredentialLeasesResp": {
    "leases": "leases",
  },
  "AdminListCredentialProvidersReq": {
    "tenantId": "tenantId",
  },
  "AdminListCredentialProvidersResp": {
    "providers": "providers",
  },
  "AdminListDiscoveryReq": {
    "tenantId": "tenantId",
  },
  "AdminListDiscoveryResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListEmergencyRolesReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListEmergencyRolesResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListEntitlementsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListEntitlementsResp": {
    "entitlements": "entitlements",
    "nextCursor": "next_cursor",
  },
  "AdminListHighRiskSubjectsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "minLevel": "min_level",
    "tenantId": "tenantId",
  },
  "AdminListHighRiskSubjectsResp": {
    "nextCursor": "next_cursor",
    "subjects": "subjects",
  },
  "AdminListPoliciesReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListPoliciesResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListResourcesReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListResourcesResp": {
    "nextCursor": "next_cursor",
    "resources": "resources",
  },
  "AdminListRoleDefsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListRoleDefsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "AdminListSubjectsReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListSubjectsResp": {
    "nextCursor": "next_cursor",
    "subjects": "subjects",
  },
  "AdminListTenantClientCAsReq": {
    "tenantId": "tenantId",
  },
  "AdminListTenantsReq": {
    "cursor": "cursor",
    "limit": "limit",
  },
  "AdminListTenantsResp": {
    "nextCursor": "next_cursor",
    "tenants": "tenants",
  },
  "AdminListUserAppPasswordsReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminListUserFactorsReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminListUserFactorsResp": {
    "factors": "factors",
  },
  "AdminListUsersReq": {
    "cursor": "cursor",
    "limit": "limit",
    "tenantId": "tenantId",
  },
  "AdminListUsersResp": {
    "nextCursor": "next_cursor",
    "users": "users",
  },
  "AdminMFAPolicyResp": {
    "nonDangerousLevel": "non_dangerous_level",
    "nonDangerousRequired": "non_dangerous_required",
    "requireForAdminLogin": "require_for_admin_login",
    "requireSecondFactorForPasskeyPrimary": "require_second_factor_for_passkey_primary",
  },
  "AdminMTLSEmptyReq": {
  },
  "AdminMTLSStatusResp": {
    "count": "count",
    "enabled": "enabled",
  },
  "AdminMailProviderResp": {
    "createdAt": "created_at",
    "credentialSet": "credential_set",
    "envLocked": "env_locked",
    "fromAddr": "from_addr",
    "host": "host",
    "port": "port",
    "tenantId": "tenant_id",
    "tlsMode": "tls_mode",
    "updatedAt": "updated_at",
    "username": "username",
  },
  "AdminMintAPIKeyReq": {
    "appId": "app_id",
    "audience": "audience",
    "name": "name",
    "roles": "roles",
    "tenantId": "tenantId",
    "ttlSeconds": "ttl_seconds",
  },
  "AdminOverrideCAERiskReq": {
    "level": "level",
    "reason": "reason",
    "subjectId": "subjectId",
    "tenantId": "tenantId",
  },
  "AdminOverrideCAERiskResp": {
    "level": "level",
    "status": "status",
  },
  "AdminPAMMaintenanceToggleReq": {
    "enabled": "enabled",
    "tenantId": "tenantId",
  },
  "AdminPAMMaintenanceToggleResp": {
    "maintenanceMode": "maintenance_mode",
  },
  "AdminPAMResilienceHealthReq": {
    "tenantId": "tenant_id",
  },
  "AdminPAMResilienceHealthResp": {
  },
  "AdminPAMResilienceStatusReq": {
    "tenantId": "tenant_id",
  },
  "AdminPAMResilienceStatusResp": {
  },
  "AdminPAMSLODashboardReq": {
    "tenantId": "tenant_id",
  },
  "AdminPAMSLODashboardResp": {
  },
  "AdminPolicyCondJSON": {
    "field": "field",
    "operator": "operator",
    "value": "value",
  },
  "AdminPolicyResp": {
    "conditions": "conditions",
    "createdAt": "created_at",
    "description": "description",
    "effect": "effect",
    "enabled": "enabled",
    "id": "id",
    "name": "name",
    "priority": "priority",
    "target": "target",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
    "version": "version",
  },
  "AdminPolicyTargetJSON": {
    "actions": "actions",
    "properties": "properties",
    "resourceIds": "resource_ids",
    "resourceTypes": "resource_types",
  },
  "AdminPolicyVersionReq": {
    "policyId": "policyId",
    "tenantId": "tenantId",
    "version": "version",
  },
  "AdminPolicyVersionsReq": {
    "policyId": "policyId",
    "tenantId": "tenantId",
  },
  "AdminPolicyVersionsResp": {
    "items": "items",
  },
  "AdminPreviewAppClaimsSurfaceReq": {
    "appId": "appId",
    "rules": "rules",
    "tenantId": "tenantId",
  },
  "AdminProfilePolicyResp": {
    "allowUserAttributes": "allow_user_attributes",
    "appPasswordScopes": "app_password_scopes",
    "appPasswordsEnabled": "app_passwords_enabled",
    "hrEnabled": "hr_enabled",
    "maxAppPasswords": "max_app_passwords",
    "tenantManagedFields": "tenant_managed_fields",
  },
  "AdminPutAppClaimsSurfaceReq": {
    "appId": "appId",
    "previewToken": "preview_token",
    "rules": "rules",
    "tenantId": "tenantId",
  },
  "AdminPutMailProviderReq": {
    "credential": "credential",
    "fromAddr": "from_addr",
    "host": "host",
    "port": "port",
    "tenantId": "tenantId",
    "tlsMode": "tls_mode",
    "username": "username",
  },
  "AdminPutProfilePolicyReq": {
    "allowUserAttributes": "allow_user_attributes",
    "appPasswordScopes": "app_password_scopes",
    "appPasswordsEnabled": "app_passwords_enabled",
    "hrEnabled": "hr_enabled",
    "maxAppPasswords": "max_app_passwords",
    "tenantId": "tenantId",
    "tenantManagedFields": "tenant_managed_fields",
  },
  "AdminPutSecretsProviderReq": {
    "address": "address",
    "backend": "backend",
    "credential": "credential",
    "endpoint": "endpoint",
    "kvMount": "kv_mount",
    "namespace": "namespace",
    "project": "project",
    "region": "region",
    "tenantId": "tenantId",
    "vaultUrl": "vault_url",
  },
  "AdminQueryAuditReq": {
    "actor": "actor",
    "cursor": "cursor",
    "event": "event",
    "jti": "jti",
    "limit": "limit",
    "outcome": "outcome",
    "pamRequestId": "pam_request_id",
    "sessionId": "session_id",
    "since": "since",
    "tenantId": "tenantId",
    "type": "type",
    "until": "until",
  },
  "AdminQueryAuditResp": {
    "nextCursor": "next_cursor",
    "records": "records",
  },
  "AdminRebaselineAuditChainReq": {
    "override": "override",
    "overrideReason": "override_reason",
    "tenantId": "tenantId",
  },
  "AdminRebaselineAuditChainResp": {
    "overrideUsed": "override_used",
    "sealedAnomalies": "sealed_anomalies",
    "sealedCount": "sealed_count",
    "sealedThrough": "sealed_through",
    "tamperSuspected": "tamper_suspected",
  },
  "AdminResourceItem": {
    "appId": "app_id",
    "approvalPolicyRef": "approval_policy_ref",
    "createdAt": "created_at",
    "environment": "environment",
    "id": "id",
    "name": "name",
    "nativeType": "native_type",
    "owner": "owner",
    "sensitivity": "sensitivity",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminResourceResp": {
    "appId": "app_id",
    "approvalPolicyRef": "approval_policy_ref",
    "createdAt": "created_at",
    "environment": "environment",
    "id": "id",
    "name": "name",
    "nativeType": "native_type",
    "owner": "owner",
    "sensitivity": "sensitivity",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
  },
  "AdminResumeBrokeredSessionReq": {
    "sessionId": "sessionId",
    "stepUpSatisfied": "step_up_satisfied",
    "tenantId": "tenantId",
  },
  "AdminRevokeCredentialLeaseResp": {
    "leaseId": "lease_id",
    "status": "status",
  },
  "AdminRevokeUserAppPasswordReq": {
    "appPasswordId": "appPasswordId",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminRevokeUserAppPasswordResp": {
    "ok": "ok",
  },
  "AdminRevokeUserFactorReq": {
    "factorId": "factorId",
    "factorKind": "factorKind",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminRevokeUserFactorResp": {
    "ok": "ok",
  },
  "AdminRoleDefResp": {
  },
  "AdminRollbackPolicyReq": {
    "policyId": "policyId",
    "tenantId": "tenantId",
    "toVersion": "to_version",
  },
  "AdminRotateAPIKeyReq": {
    "audience": "audience",
    "keyId": "keyId",
    "tenantId": "tenantId",
    "ttlSeconds": "ttl_seconds",
  },
  "AdminRotateClientSecretReq": {
    "clientId": "clientId",
    "tenantId": "tenantId",
  },
  "AdminRotateClientSecretResp": {
    "clientId": "client_id",
    "clientSecret": "client_secret",
  },
  "AdminRotateCredentialKeyReq": {
    "issuerType": "issuer_type",
    "tenantId": "tenantId",
  },
  "AdminRotateCredentialKeyResp": {
    "newKeyId": "new_key_id",
    "oldKeyId": "old_key_id",
    "rotatedAt": "rotated_at",
  },
  "AdminRotateSigningKeysReq": {
  },
  "AdminRotateSigningKeysResp": {
  },
  "AdminSecretsProviderResp": {
    "address": "address",
    "backend": "backend",
    "createdAt": "created_at",
    "credentialSet": "credential_set",
    "endpoint": "endpoint",
    "envLocked": "env_locked",
    "kvMount": "kv_mount",
    "namespace": "namespace",
    "project": "project",
    "region": "region",
    "tenantId": "tenant_id",
    "updatedAt": "updated_at",
    "vaultUrl": "vault_url",
  },
  "AdminSessionControlPolicyResp": {
    "absTtlSeconds": "abs_ttl_seconds",
    "allowPause": "allow_pause",
    "idleTtlSeconds": "idle_ttl_seconds",
    "maxConcurrentPerSubject": "max_concurrent_per_subject",
  },
  "AdminSetMFAPolicyReq": {
    "nonDangerousLevel": "non_dangerous_level",
    "nonDangerousRequired": "non_dangerous_required",
    "requireForAdminLogin": "require_for_admin_login",
    "requireSecondFactorForPasskeyPrimary": "require_second_factor_for_passkey_primary",
    "tenantId": "tenantId",
  },
  "AdminSetSessionControlPolicyReq": {
    "absTtlSeconds": "abs_ttl_seconds",
    "allowPause": "allow_pause",
    "idleTtlSeconds": "idle_ttl_seconds",
    "maxConcurrentPerSubject": "max_concurrent_per_subject",
    "resourceId": "resourceId",
    "tenantId": "tenantId",
  },
  "AdminSetUserProfileReq": {
    "hr": "hr",
    "removeAttributes": "remove_attributes",
    "setAttributes": "set_attributes",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminSimulatePolicyReq": {
    "policies": "policies",
    "query": "query",
    "tenantId": "tenantId",
  },
  "AdminSimulatePolicyResp": {
    "decision": "decision",
    "denyOverride": "deny_override",
    "matchedPolicies": "matched_policies",
  },
  "AdminSubjectItem": {
    "activeThreatLevel": "active_threat_level",
    "assuranceLevel": "assurance_level",
    "createdAt": "created_at",
    "displayName": "display_name",
    "externalIds": "external_ids",
    "id": "id",
    "lifecycle": "lifecycle",
    "linkedUserId": "linked_user_id",
    "pamAccessDisabled": "pam_access_disabled",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "tenantId": "tenant_id",
    "type": "type",
    "updatedAt": "updated_at",
  },
  "AdminSubjectResp": {
    "activeThreatLevel": "active_threat_level",
    "assuranceLevel": "assurance_level",
    "createdAt": "created_at",
    "displayName": "display_name",
    "externalIds": "external_ids",
    "id": "id",
    "lifecycle": "lifecycle",
    "linkedUserId": "linked_user_id",
    "pamAccessDisabled": "pam_access_disabled",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "tenantId": "tenant_id",
    "type": "type",
    "updatedAt": "updated_at",
  },
  "AdminSubmitAttestationReq": {
    "campaignId": "campaignId",
    "decision": "decision",
    "discoveryId": "discovery_id",
    "justification": "justification",
    "tenantId": "tenantId",
  },
  "AdminTenantResp": {
    "createdAt": "created_at",
    "id": "id",
    "issuer": "issuer",
    "name": "name",
    "protected": "protected",
    "requireMtls": "require_mtls",
    "status": "status",
    "updatedAt": "updated_at",
  },
  "AdminTriggerCAEEvaluateReq": {
    "subjectId": "subjectId",
    "tenantId": "tenantId",
  },
  "AdminTriggerCAEEvaluateResp": {
    "status": "status",
  },
  "AdminUpdateAppReq": {
    "apiKeyRoleCatalog": "apiKeyRoleCatalog",
    "appId": "appId",
    "attributes": "attributes",
    "description": "description",
    "name": "name",
    "parentId": "parent_id",
    "status": "status",
    "tenantId": "tenantId",
  },
  "AdminUpdateAppRoleReq": {
    "appId": "appId",
    "description": "description",
    "name": "name",
    "permissions": "permissions",
    "roleId": "roleId",
    "tenantId": "tenantId",
  },
  "AdminUpdateCAEConfigReq": {
    "failedAuthBurstCritical": "FailedAuthBurstCritical",
    "failedAuthBurstHigh": "FailedAuthBurstHigh",
    "impossibleTravelKmCritical": "ImpossibleTravelKmCritical",
    "sensitiveActionRateHigh": "SensitiveActionRateHigh",
    "velocityAnomalyCritical": "VelocityAnomalyCritical",
    "velocityAnomalyHigh": "VelocityAnomalyHigh",
    "tenantId": "tenantId",
  },
  "AdminUpdateCAEConfigResp": {
    "status": "status",
  },
  "AdminUpdateClientCAReq": {
    "enabled": "enabled",
    "id": "id",
  },
  "AdminUpdateClientReq": {
    "appId": "app_id",
    "clientId": "clientId",
    "grantTypes": "grant_types",
    "jwksUri": "jwks_uri",
    "name": "name",
    "postLogoutRedirectUris": "post_logout_redirect_uris",
    "redirectUris": "redirect_uris",
    "scopes": "scopes",
    "serviceAccountRoles": "service_account_roles",
    "tenantId": "tenantId",
    "tokenEndpointAuthMethod": "token_endpoint_auth_method",
    "tokenExchangeEnabled": "token_exchange_enabled",
  },
  "AdminUpdateConnectorReq": {
    "clientSecret": "client_secret",
    "config": "config",
    "connectorId": "connectorId",
    "enabled": "enabled",
    "name": "name",
    "tenantId": "tenantId",
    "type": "type",
  },
  "AdminUpdateEmergencyRoleReq": {
    "maxTtlSeconds": "max_ttl_seconds",
    "name": "name",
    "permissions": "permissions",
    "resourceId": "resource_id",
    "roleId": "roleId",
    "scope": "scope",
    "tenantId": "tenantId",
  },
  "AdminUpdateEntitlementReq": {
    "candidateSubjects": "candidate_subjects",
    "conflictingGroups": "conflicting_groups",
    "entitlementId": "entitlementId",
    "maxDurationSeconds": "max_duration_seconds",
    "permissions": "permissions",
    "requiredApprovals": "required_approvals",
    "resourceId": "resource_id",
    "selfApprovalAllowed": "self_approval_allowed",
    "sensitivity": "sensitivity",
    "tenantId": "tenantId",
  },
  "AdminUpdateResourceReq": {
    "appId": "app_id",
    "approvalPolicyRef": "approval_policy_ref",
    "environment": "environment",
    "metadata": "metadata",
    "name": "name",
    "nativeType": "native_type",
    "owner": "owner",
    "resourceId": "resourceId",
    "sensitivity": "sensitivity",
    "tenantId": "tenantId",
  },
  "AdminUpdateRoleDefReq": {
    "description": "description",
    "name": "name",
    "permissions": "permissions",
    "tenantId": "tenantId",
  },
  "AdminUpdateSubjectReq": {
    "activeThreatLevel": "active_threat_level",
    "assuranceLevel": "assurance_level",
    "displayName": "display_name",
    "externalIds": "external_ids",
    "lifecycle": "lifecycle",
    "linkedUserId": "linked_user_id",
    "pamAccessDisabled": "pam_access_disabled",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskContext": "risk_context",
    "riskLevel": "risk_level",
    "subjectId": "subjectId",
    "tenantId": "tenantId",
    "type": "type",
  },
  "AdminUpdateTenantReq": {
    "name": "name",
    "requireMtls": "requireMtls",
    "status": "status",
    "tenantId": "tenantId",
  },
  "AdminUpdateUserReq": {
    "accountLocked": "account_locked",
    "activeThreatLevel": "active_threat_level",
    "emailVerified": "email_verified",
    "name": "name",
    "pamAccessDisabled": "pam_access_disabled",
    "passwordResetRequired": "password_reset_required",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "roles": "roles",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminUpsertDevicePostureReq": {
    "compliant": "compliant",
    "deviceId": "device_id",
    "impossibleTravel": "impossible_travel",
    "managed": "managed",
    "riskScore": "risk_score",
    "source": "source",
    "tenantId": "tenantId",
    "verified": "verified",
  },
  "AdminUserActiveThreatHistoryPoint": {
    "actorSub": "actor_sub",
    "id": "id",
    "level": "level",
    "recordedAt": "recorded_at",
    "score": "score",
    "source": "source",
  },
  "AdminUserFactorItem": {
    "createdAt": "created_at",
    "id": "id",
    "kind": "kind",
    "name": "name",
  },
  "AdminUserItem": {
    "accountLocked": "account_locked",
    "activeThreatLevel": "active_threat_level",
    "email": "email",
    "emailVerified": "email_verified",
    "id": "id",
    "name": "name",
    "pamAccessDisabled": "pam_access_disabled",
    "passwordResetRequired": "password_reset_required",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "roles": "roles",
    "securityOnboardingRequired": "security_onboarding_required",
  },
  "AdminUserPasswordResetReq": {
    "password": "password",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminUserProfileResp": {
    "attributes": "attributes",
    "email": "email",
    "emailVerified": "email_verified",
    "familyName": "family_name",
    "givenName": "given_name",
    "hr": "hr",
    "name": "name",
    "picture": "picture",
    "sub": "sub",
    "tenantManagedFields": "tenant_managed_fields",
  },
  "AdminUserResp": {
    "accountLocked": "account_locked",
    "activeThreatLevel": "active_threat_level",
    "email": "email",
    "emailVerified": "email_verified",
    "id": "id",
    "name": "name",
    "pamAccessDisabled": "pam_access_disabled",
    "passwordResetRequired": "password_reset_required",
    "privilegeEscalationDisabled": "privilege_escalation_disabled",
    "riskLevel": "risk_level",
    "roles": "roles",
    "securityOnboardingRequired": "security_onboarding_required",
  },
  "AdminUserRevokeSessionsReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "AdminUserRiskHistoryPoint": {
    "actorSub": "actor_sub",
    "id": "id",
    "level": "level",
    "recordedAt": "recorded_at",
    "score": "score",
    "source": "source",
  },
  "AdminWhoAmIResp": {
    "adminRole": "admin_role",
    "permissions": "permissions",
    "roles": "roles",
    "sub": "sub",
    "tenant": "tenant",
  },
  "AuditRecord": {
    "actorSub": "actor_sub",
    "catalogCode": "catalog_code",
    "event": "event",
    "id": "id",
    "jti": "jti",
    "outcome": "outcome",
    "pamRequestId": "pam_request_id",
    "resource": "resource",
    "sessionId": "session_id",
    "tenantId": "tenant_id",
    "timestamp": "timestamp",
  },
  "AuthorizationCodeTokenReq": {
    "clientId": "client_id",
    "clientSecret": "client_secret",
    "code": "code",
    "codeVerifier": "code_verifier",
    "grantType": "grant_type",
    "redirectUri": "redirect_uri",
  },
  "ChainAnomaly": {
    "atUnixNano": "at_unix_nano",
    "computedRecordHash": "computed_record_hash",
    "eventCode": "event_code",
    "expectedPrevHash": "expected_prev_hash",
    "explanation": "explanation",
    "index": "index",
    "kind": "kind",
    "matchesIndex": "matches_index",
    "recordId": "record_id",
    "storedPrevHash": "stored_prev_hash",
    "storedRecordHash": "stored_record_hash",
  },
  "DiscoverReq": {
  },
  "DiscoverResp": {
    "authorizationEndpoint": "authorization_endpoint",
    "grantType": "grant_type",
    "issuer": "issuer",
    "jwksUri": "jwks_uri",
    "responseType": "response_type",
    "scope": "scope",
    "subjectType": "subject_type",
    "tokenEndpoint": "token_endpoint",
    "tokenSigningAlg": "token_signing_alg",
    "userinfoEndpoint": "userinfo_endpoint",
  },
  "EmailVerifyConfirmReq": {
    "token": "token",
  },
  "EmailVerifyConfirmResp": {
    "verified": "verified",
  },
  "EmailVerifyRequestReq": {
    "email": "email",
  },
  "EmailVerifyRequestResp": {
    "status": "status",
  },
  "HealthReq": {
  },
  "HealthResp": {
    "status": "status",
    "uptimeSeconds": "uptime_seconds",
    "version": "version",
  },
  "InitBeginReq": {
  },
  "InitBeginResp": {
    "initialized": "initialized",
    "steps": "steps",
  },
  "InitCompleteReq": {
  },
  "InitCompleteResp": {
    "ok": "ok",
  },
  "InitCreateAdminReq": {
    "email": "email",
    "name": "name",
    "password": "password",
  },
  "InitCreateAdminResp": {
    "adminId": "admin_id",
    "ok": "ok",
  },
  "InitStatusReq": {
  },
  "InitStatusResp": {
    "initialized": "initialized",
    "setupTokenEnabled": "setup_token_enabled",
    "steps": "steps",
  },
  "JWK": {
    "alg": "alg",
    "e": "e",
    "kid": "kid",
    "kty": "kty",
    "n": "n",
    "use": "use",
  },
  "JWKSReq": {
  },
  "JWKSResp": {
    "keys": "keys",
  },
  "ListRevokedAPIKeysReq": {
    "tenantId": "tenantId",
  },
  "ListRevokedAPIKeysResp": {
    "refreshSeconds": "refresh_seconds",
    "revoked": "revoked",
  },
  "MTLSAuthorizeReq": {
    "clientId": "client_id",
    "codeChallenge": "code_challenge",
    "codeChallengeMethod": "code_challenge_method",
    "nonce": "nonce",
    "redirectUri": "redirect_uri",
    "scope": "scope",
    "state": "state",
  },
  "MTLSAuthorizeResp": {
    "code": "code",
    "redirectUri": "redirect_uri",
    "state": "state",
  },
  "MTLSCertificateRegisterReq": {
    "label": "label",
  },
  "MTLSCertificateRegisterResp": {
    "fingerprint": "fingerprint",
    "issuer": "issuer",
    "notAfter": "not_after",
    "subject": "subject",
  },
  "MagicLinkRequestReq": {
    "clientId": "client_id",
    "codeChallenge": "code_challenge",
    "codeChallengeMethod": "code_challenge_method",
    "email": "email",
    "nonce": "nonce",
    "redirectUri": "redirect_uri",
    "scope": "scope",
    "state": "state",
  },
  "MagicLinkRequestResp": {
    "status": "status",
  },
  "MagicLinkVerifyReq": {
    "securityAnswers": "security_answers",
    "token": "token",
  },
  "MagicLinkVerifyResp": {
    "code": "code",
    "mfaRequired": "mfa_required",
    "redirectTo": "redirect_to",
    "redirectUri": "redirect_uri",
    "responseKind": "response_kind",
    "state": "state",
  },
  "MeAccessRequestResp": {
    "actions": "actions",
    "activatedAt": "activated_at",
    "approverIds": "approver_ids",
    "clientId": "client_id",
    "createdAt": "created_at",
    "denialReason": "denial_reason",
    "expiresAt": "expires_at",
    "id": "id",
    "idempotencyKey": "idempotency_key",
    "justification": "justification",
    "permissions": "permissions",
    "purpose": "purpose",
    "requestedById": "requested_by_id",
    "requestedDurationSeconds": "requested_duration_seconds",
    "resourceId": "resource_id",
    "startTime": "start_time",
    "status": "status",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
    "ticketRef": "ticket_ref",
    "updatedAt": "updated_at",
  },
  "MeActivateAccessRequestReq": {
    "requestId": "requestId",
  },
  "MeAddEmailReq": {
    "email": "email",
  },
  "MeAddEmailResp": {
    "status": "status",
  },
  "MeAppPasswordsResp": {
    "appPasswords": "app_passwords",
  },
  "MeApproveRequestReq": {
    "rationale": "rationale",
    "requestId": "requestId",
  },
  "MeBrokeredSessionResp": {
  },
  "MeCreateAccessRequestReq": {
    "actions": "actions",
    "clientId": "client_id",
    "idempotencyKey": "idempotency_key",
    "justification": "justification",
    "permissions": "permissions",
    "purpose": "purpose",
    "requestedDurationSeconds": "requested_duration_seconds",
    "resourceId": "resource_id",
    "startTime": "start_time",
    "ticketRef": "ticket_ref",
  },
  "MeCreateAppPasswordReq": {
    "expiresInDays": "expires_in_days",
    "label": "label",
    "scopes": "scopes",
  },
  "MeCreateAppPasswordResp": {
    "createdAt": "created_at",
    "expiresAt": "expires_at",
    "id": "id",
    "label": "label",
    "scopes": "scopes",
    "secret": "secret",
  },
  "MeCredentialLeaseResp": {
    "expiresAt": "expires_at",
    "id": "id",
    "issuedAt": "issued_at",
    "issuerType": "issuer_type",
    "resourceId": "resource_id",
    "revokedAt": "revoked_at",
    "scope": "scope",
    "sessionId": "session_id",
    "state": "state",
    "subjectId": "subject_id",
    "tenantId": "tenant_id",
  },
  "MeDeleteEmailReq": {
    "emailId": "emailId",
  },
  "MeDeleteEmailResp": {
    "ok": "ok",
  },
  "MeDenyRequestReq": {
    "reason": "reason",
    "requestId": "requestId",
  },
  "MeEmailsResp": {
    "emails": "emails",
  },
  "MeEndSessionReq": {
    "sessionId": "sessionId",
  },
  "MeEphemeralCredResp": {
    "caPubKey": "ca_pub_key",
    "credentialMaterial": "credential_material",
    "issuerType": "issuer_type",
    "leaseId": "lease_id",
    "validUntil": "valid_until",
  },
  "MeGetAccessRequestReq": {
    "requestId": "requestId",
  },
  "MeGetCredentialReq": {
    "leaseId": "leaseId",
  },
  "MeGetGrantReq": {
    "grantId": "grantId",
  },
  "MeGetGrantResp": {
  },
  "MeGetProfileReq": {
  },
  "MeGetSessionReq": {
    "sessionId": "sessionId",
  },
  "MeInitiateSessionReq": {
    "driverType": "driver_type",
    "grantId": "grant_id",
    "resourceId": "resource_id",
    "sshPublicKey": "ssh_public_key",
  },
  "MeIssueCredentialReq": {
    "grantId": "grant_id",
    "issuerParams": "issuer_params",
    "issuerType": "issuer_type",
    "resourceId": "resource_id",
    "scope": "scope",
    "ttlSeconds": "ttl_seconds",
  },
  "MeListAccessRequestsReq": {
    "cursor": "cursor",
    "limit": "limit",
  },
  "MeListAccessRequestsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "MeListAppPasswordsReq": {
  },
  "MeListApprovalsReq": {
    "cursor": "cursor",
    "limit": "limit",
  },
  "MeListApprovalsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "MeListCredentialsReq": {
  },
  "MeListCredentialsResp": {
    "leases": "leases",
  },
  "MeListEmailsReq": {
  },
  "MeListEntitlementsReq": {
  },
  "MeListEntitlementsResp": {
    "items": "items",
  },
  "MeListGrantsReq": {
    "cursor": "cursor",
    "limit": "limit",
  },
  "MeListGrantsResp": {
    "items": "items",
    "nextCursor": "next_cursor",
  },
  "MeListSessionsReq": {
  },
  "MeListSessionsResp": {
    "sessions": "sessions",
  },
  "MeProfileResp": {
    "attributes": "attributes",
    "email": "email",
    "emailVerified": "email_verified",
    "familyName": "family_name",
    "givenName": "given_name",
    "hr": "hr",
    "name": "name",
    "picture": "picture",
    "sub": "sub",
    "tenantManagedFields": "tenant_managed_fields",
  },
  "MeRevokeAppPasswordReq": {
    "appPasswordId": "appPasswordId",
  },
  "MeRevokeAppPasswordResp": {
    "ok": "ok",
  },
  "MeRevokeCredentialReq": {
    "leaseId": "leaseId",
  },
  "MeRevokeCredentialResp": {
    "leaseId": "lease_id",
    "status": "status",
  },
  "MeSetPrimaryEmailReq": {
    "emailId": "emailId",
  },
  "MeUpdateProfileReq": {
    "familyName": "family_name",
    "givenName": "given_name",
    "name": "name",
    "picture": "picture",
    "removeAttributes": "remove_attributes",
    "setAttributes": "set_attributes",
  },
  "OIDCAuthorizeRequest": {
    "acrValues": "acr_values",
    "clientId": "client_id",
    "codeChallenge": "code_challenge",
    "codeChallengeMethod": "code_challenge_method",
    "loginHint": "login_hint",
    "maxAge": "max_age",
    "nonce": "nonce",
    "prompt": "prompt",
    "redirectUri": "redirect_uri",
    "responseType": "response_type",
    "scope": "scope",
    "state": "state",
  },
  "OIDCAuthorizeResponse": {
    "code": "code",
    "expiresIn": "expires_in",
    "redirectUri": "redirect_uri",
    "state": "state",
  },
  "OIDCEventMsg": {
    "clientId": "client_id",
    "eventType": "event_type",
    "timestamp": "timestamp",
    "userId": "user_id",
  },
  "OIDCTokenRequest": {
    "clientAssertion": "client_assertion",
    "clientAssertionType": "client_assertion_type",
    "clientId": "client_id",
    "clientSecret": "client_secret",
    "code": "code",
    "codeVerifier": "code_verifier",
    "grantType": "grant_type",
    "password": "password",
    "redirectUri": "redirect_uri",
    "refreshToken": "refresh_token",
    "scope": "scope",
    "username": "username",
  },
  "OIDCTokenResponse": {
    "accessToken": "access_token",
    "expiresIn": "expires_in",
    "idToken": "id_token",
    "refreshToken": "refresh_token",
    "refreshTokenExpiresIn": "refresh_token_expires_in",
    "scope": "scope",
    "tokenType": "token_type",
  },
  "OIDCUserinfoRequest": {
  },
  "OIDCUserinfoResponse": {
    "email": "email",
    "emailVerified": "email_verified",
    "familyName": "family_name",
    "givenName": "given_name",
    "locale": "locale",
    "name": "name",
    "picture": "picture",
    "preferredUsername": "preferred_username",
    "sub": "sub",
  },
  "PamAccessRequestActivationResp": {
    "accessToken": "access_token",
    "expiresIn": "expires_in",
    "grantId": "grant_id",
    "privileged": "privileged",
    "tokenType": "token_type",
  },
  "PamActivationReturnReq": {
    "returnTo": "return_to",
  },
  "PamActivationReturnResp": {
  },
  "PasswordForgotReq": {
    "email": "email",
  },
  "PasswordForgotResp": {
    "status": "status",
  },
  "PasswordResetReq": {
    "newPassword": "new_password",
    "securityAnswers": "security_answers",
    "token": "token",
  },
  "PasswordResetResp": {
    "reset": "reset",
  },
  "RecoveryKeyGenerateReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "RecoveryKeyGenerateResp": {
    "recoveryKey": "recovery_key",
  },
  "RecoveryKeyRedeemReq": {
    "recoveryKey": "recovery_key",
    "tenantId": "tenantId",
  },
  "RecoveryKeyRedeemResp": {
    "redeemed": "redeemed",
    "userId": "user_id",
  },
  "RefreshTokenReq": {
    "clientId": "client_id",
    "clientSecret": "client_secret",
    "grantType": "grant_type",
    "refreshToken": "refresh_token",
  },
  "RegisterReq": {
    "email": "email",
    "familyName": "family_name",
    "givenName": "given_name",
    "name": "name",
    "password": "password",
  },
  "RegisterResp": {
    "emailVerified": "email_verified",
    "userId": "user_id",
  },
  "SecondaryEmailVerifyConfirmReq": {
    "token": "token",
  },
  "SecondaryEmailVerifyConfirmResp": {
    "verified": "verified",
  },
  "SecurityQuestionItem": {
    "answer": "answer",
    "question": "question",
  },
  "SecurityQuestionsSetReq": {
    "questions": "questions",
  },
  "SecurityQuestionsSetResp": {
    "count": "count",
  },
  "SecurityQuestionsVerifyReq": {
    "answers": "answers",
    "email": "email",
  },
  "SecurityQuestionsVerifyResp": {
    "verified": "verified",
  },
  "TOTPDisableReq": {
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "TOTPDisableResp": {
    "disabled": "disabled",
  },
  "TOTPEnrollBeginReq": {
    "account": "account",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "TOTPEnrollBeginResp": {
    "otpauthUri": "otpauth_uri",
    "secret": "secret",
  },
  "TOTPEnrollVerifyReq": {
    "code": "code",
    "tenantId": "tenantId",
    "userId": "userId",
  },
  "TOTPEnrollVerifyResp": {
    "verified": "verified",
  },
  "TicketRef": {
    "externalId": "external_id",
    "system": "system",
  },
  "TokenExchangeReq": {
    "actorToken": "actor_token",
    "actorTokenType": "actor_token_type",
    "audience": "audience",
    "grantType": "grant_type",
    "requestedTokenType": "requested_token_type",
    "resource": "resource",
    "scope": "scope",
    "subjectToken": "subject_token",
    "subjectTokenType": "subject_token_type",
  },
  "WebAuthnAuthenticateBeginReq": {
    "clientId": "client_id",
    "codeChallenge": "code_challenge",
    "codeChallengeMethod": "code_challenge_method",
    "nonce": "nonce",
    "redirectUri": "redirect_uri",
    "scope": "scope",
    "state": "state",
    "userId": "user_id",
  },
  "WebAuthnAuthenticateBeginResp": {
    "options": "options",
    "sessionId": "session_id",
  },
  "WebAuthnAuthenticateCompleteReq": {
    "clientId": "client_id",
    "codeChallenge": "code_challenge",
    "codeChallengeMethod": "code_challenge_method",
    "credential": "credential",
    "nonce": "nonce",
    "redirectUri": "redirect_uri",
    "scope": "scope",
    "sessionId": "session_id",
    "state": "state",
  },
  "WebAuthnAuthenticateCompleteResp": {
    "code": "code",
    "redirectTo": "redirect_to",
    "redirectUri": "redirect_uri",
    "responseKind": "response_kind",
    "state": "state",
  },
  "WebAuthnCredentialSummary": {
    "aaguid": "aaguid",
    "alias": "alias",
    "createdAt": "createdAt",
    "credentialId": "credentialId",
    "transports": "transports",
  },
  "WebAuthnDeleteCredentialReq": {
    "credentialId": "credentialId",
  },
  "WebAuthnDeleteCredentialResp": {
    "ok": "ok",
  },
  "WebAuthnEnrollmentStatusReq": {
  },
  "WebAuthnEnrollmentStatusResp": {
    "enrolled": "enrolled",
  },
  "WebAuthnListCredentialsReq": {
  },
  "WebAuthnListCredentialsResp": {
    "credentials": "credentials",
  },
  "WebAuthnRegisterBeginReq": {
    "attestation": "attestation",
    "authenticatorAttachment": "authenticator_attachment",
    "displayName": "display_name",
    "userId": "user_id",
  },
  "WebAuthnRegisterBeginResp": {
    "options": "options",
    "sessionId": "session_id",
  },
  "WebAuthnRegisterCompleteReq": {
    "credential": "credential",
    "sessionId": "session_id",
  },
  "WebAuthnRegisterCompleteResp": {
    "aaguid": "aaguid",
    "credentialId": "credential_id",
    "transports": "transports",
  },
  "WebAuthnSetCredentialAliasReq": {
    "alias": "alias",
    "credentialId": "credentialId",
  },
  "WebAuthnSetCredentialAliasResp": {
    "ok": "ok",
  },
};

// schemaWireKeysReverse[schemaName] is the lazily-built inverse of
// schemaWireKeys[schemaName] (wireName -> camelCase field name), computed
// once per schema on first use and cached. Backs fromWire's schema-aware
// path (G-08).
const schemaWireKeysReverse: Record<string, Record<string, string>> = {};

// wireKeyMapFor returns the wireName->fieldName lookup for one schema, or
// undefined when the schema name is unknown (defensive; every schema
// referenced by generated code has an entry in schemaWireKeys).
function wireKeyMapFor(schemaName: string): Record<string, string> | undefined {
  const forward = schemaWireKeys[schemaName];
  if (!forward) {
    return undefined;
  }
  let reverse = schemaWireKeysReverse[schemaName];
  if (!reverse) {
    reverse = {};
    for (const [fieldName, wireName] of Object.entries(forward)) {
      reverse[wireName] = fieldName;
    }
    schemaWireKeysReverse[schemaName] = reverse;
  }
  return reverse;
}

// --- schema-aware camelCase boundary mapper (TSC-08) ---
// DTO interfaces expose TypeScript-friendly camelCase. Outbound request keys
// are mapped back to the original schema key for that specific request schema:
// legacy snake_case schemas still go out as snake_case, while schemas that
// already declare camelCase JSON keys stay camelCase on the wire. Free-form
// nested records are left verbatim. Inbound decoded responses still accept
// snake_case and normalize it to camelCase for consumers.
function snakeToCamel(key: string): string {
  return key.replace(/_+([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  if (value instanceof Date) {
    return false;
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return false;
  }
  if (typeof ArrayBuffer !== "undefined" && (value instanceof ArrayBuffer || ArrayBuffer.isView(value))) {
    return false;
  }
  return true;
}

function convertKeys(value: unknown, convert: (key: string) => string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => convertKeys(item, convert));
  }
  // FileUpload values ({blob, contentType?, filename?}) are opaque at the
  // boundary: their internal keys (contentType, filename) must not be renamed
  // by the camelCase<->snake_case mapper. The OUTER field KEY that points to a
  // FileUpload is still converted by the caller's loop (see the isPlainObject
  // branch below), but recursion does NOT descend into the FileUpload value.
  if (isFileUpload(value)) {
    return value;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[convert(key)] = convertKeys(item, convert);
    }
    return out;
  }
  return value;
}

function convertKeysToWire(value: unknown, keyMap?: Record<string, string>): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => convertKeysToWire(item));
  }
  if (isFileUpload(value)) {
    return value;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[keyMap?.[key] ?? key] = convertKeysToWire(item);
    }
    return out;
  }
  return value;
}

function toWire(value: unknown, schemaName?: string): unknown {
  if (schemaName) {
    return convertKeysToWire(value, schemaWireKeys[schemaName]);
  }
  // ENG-1645/ENG-1639/ENG-1638: a route with an empty/untyped requestSchema
  // (a body-less GET, or any schema-less mutation) has no schemaWireKeys
  // entry to consult here. This used to fall back to a blind
  // camelCase->snake_case conversion of every key, which broke two
  // contracts at once: (1) expandPath's pathParams tuple names its schemaKey
  // as the raw URL token for an untyped route (clientPathParamPairs has no
  // TypedPathParams to consult), so a multi-word camelCase path segment like
  // "objectFamily" got snake-cased to "object_family" here and expandPath's
  // lookup for "objectFamily" then threw "missing path parameter" against
  // the client's own request -- before any fetch ran; (2) the generated Go
  // dispatcher for an untyped route stores query/path keys VERBATIM
  // (api.go.tmpl: `req[k] = vs[0]` over r.URL.Query(), `req["tok"] =
  // r.PathValue("tok")`), so a snake-cased query key like "boundary_a_id"
  // silently didn't match a handler reading the documented camelCase
  // "boundaryAId". Passing keys through unchanged keeps toWire consistent
  // with expandPath and with the wire contract the generated server
  // actually implements.
  return convertKeysToWire(value);
}

// convertKeysFromWire is fromWire's schema-aware, shallow converter -- the
// structural mirror of convertKeysToWire above. keyMap applies ONLY to the
// keys of the object/array passed in directly; every recursive call drops
// the map (matching convertKeysToWire's own recursion), so a NESTED value
// -- an array item, a nested object, additionalProperties/free-form map
// data -- has its keys left completely unchanged rather than guessed via a
// blind textual transform. This is what stops G-08: arbitrary DATA nested
// under a declared field (e.g. AdminMetricsResp.breakdowns, an array of
// free-form objects) is never key-rewritten, only the schema's own
// top-level declared fields are. A key not present in keyMap (an
// additionalProperties/extra key alongside declared ones) also passes
// through verbatim, mirroring convertKeysToWire's own fallback.
function convertKeysFromWire(value: unknown, keyMap?: Record<string, string>): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => convertKeysFromWire(item));
  }
  if (isFileUpload(value)) {
    return value;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[keyMap?.[key] ?? key] = convertKeysFromWire(item);
    }
    return out;
  }
  return value;
}

// fromWire is toWire's inbound counterpart. When schemaName is known, it
// converts ONLY that schema's own declared top-level keys
// (wireKeyMapFor/schemaWireKeys, reversed) and leaves every nested value
// untouched -- symmetric with toWire's existing (already shallow) scope.
// Without a schema name (an untyped/schema-less response with no declared
// shape to consult) it falls back to the pre-G-08 behavior: a blind
// snake_case->camelCase conversion at every depth, since that remains the
// best available behavior when there is nothing to be schema-aware with.
function fromWire(value: unknown, schemaName?: string): unknown {
  if (schemaName) {
    const keyMap = wireKeyMapFor(schemaName);
    if (keyMap) {
      return convertKeysFromWire(value, keyMap);
    }
  }
  return convertKeys(value, snakeToCamel);
}

function isFileUpload(value: unknown): value is FileUpload {
  return Boolean(
    value &&
      typeof value === "object" &&
      "blob" in (value as Record<string, unknown>) &&
      (value as { blob?: unknown }).blob instanceof Blob,
  );
}

function isFileUploadArray(value: unknown): value is FileUpload[] {
  return Array.isArray(value) && value.every((item) => isFileUpload(item));
}

function appendSearchParam(params: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null) {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      appendSearchParam(params, key, item);
    }
    return;
  }
  if (typeof value === "object") {
    params.append(key, JSON.stringify(value));
    return;
  }
  params.append(key, String(value));
}

function expandPath(
  baseUrl: string,
  path: string,
  // apic#216: each entry is [urlToken, schemaKey]. The URL pattern uses
  // urlToken ({urlToken}); the request object holds the value under
  // schemaKey (the generated request types use JSON-schema field names,
  // which are typically snake_case while the URL token is camelCase).
  pathParams: readonly (readonly [string, string])[],
  request: Record<string, unknown>,
): { url: URL; payload: Record<string, unknown> } {
  const url = new URL(baseUrl);
  let resolvedPath = joinURLPath(url.pathname, path);
  const payload = { ...request };
  for (const [urlToken, schemaKey] of pathParams) {
    if (!(schemaKey in payload)) {
      throw new Error(`missing path parameter "${schemaKey}"`);
    }
    const value = payload[schemaKey];
    resolvedPath = resolvedPath.replace(`{${urlToken}}`, encodeURIComponent(String(value)));
    delete payload[schemaKey];
  }
  url.pathname = resolvedPath;
  return { url, payload };
}

function joinURLPath(basePath: string, path: string): string {
  const base = basePath.replace(/\/+$/, "");
  if (!path) {
    return base || "/";
  }
  const next = path.startsWith("/") ? path : `/${path}`;
  if (!base || base === "/") {
    return next;
  }
  return `${base}${next}`;
}

// --- RN-safe portable primitives (TSC-16) ---
// The generated client must IMPORT and run on web, Electron, AND React Native
// (Hermes). These replace module-load-time uses of web-only globals; APIs that
// are genuinely platform-bound (Blob, crypto.subtle, WebSocket) are
// capability-guarded at their call sites and throw a clear error only if
// actually exercised without the capability.
const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function encodeBase64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = i + 1 < bytes.length ? (bytes[i + 1] ?? 0) : 0;
    const b2 = i + 2 < bytes.length ? (bytes[i + 2] ?? 0) : 0;
    out += BASE64_ALPHABET[b0 >> 2];
    out += BASE64_ALPHABET[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? BASE64_ALPHABET[((b1 & 15) << 2) | (b2 >> 6)] : "=";
    out += i + 2 < bytes.length ? BASE64_ALPHABET[b2 & 63] : "=";
  }
  return out;
}

function utf8Encode(value: string): Uint8Array {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(value);
  }
  const out: number[] = [];
  for (let i = 0; i < value.length; i++) {
    let c = value.charCodeAt(i);
    if (c < 0x80) {
      out.push(c);
    } else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c >= 0xd800 && c <= 0xdbff) {
      const next = value.charCodeAt(++i);
      c = 0x10000 + ((c & 0x3ff) << 10) + (next & 0x3ff);
      out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    } else {
      out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return Uint8Array.from(out);
}

// randomToken returns a hex token for the multipart boundary. Prefers
// crypto.getRandomValues when present, else Math.random (the boundary only
// needs to be collision-improbable within one request body).
function randomToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    return [...buf].map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let s = "";
  for (let i = 0; i < 32; i++) {
    s += Math.floor(Math.random() * 16).toString(16);
  }
  return s;
}

function utf8Bytes(value: string): ArrayBuffer {
  const encoded = utf8Encode(value);
  const buffer = new ArrayBuffer(encoded.byteLength);
  new Uint8Array(buffer).set(encoded);
  return buffer;
}

function normalizeBaseURL(baseUrl: string): string {
  const trimmed = baseUrl.trim();
  if (!trimmed) {
    throw new Error("base URL is required");
  }
  const url = new URL(trimmed);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`base URL must use http or https, got "${url.protocol.replace(/:$/, "")}"`);
  }
  if (!url.host) {
    throw new Error("base URL host is required");
  }
  if (url.username || url.password) {
    throw new Error("base URL must not include embedded credentials");
  }
  if (url.search) {
    throw new Error("base URL must not include query parameters");
  }
  if (url.hash) {
    throw new Error("base URL must not include a fragment");
  }
  if (url.pathname !== "/") {
    url.pathname = url.pathname.replace(/\/+$/, "");
  }
  if (!url.pathname) {
    url.pathname = "/";
  }
  return url.toString();
}

function sanitizeMultipartDispositionValue(value: string): string {
  return value
    .trim()
    .replace(/[\r\n]+/g, "_")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
}

function sanitizeMultipartHeaderValue(value: string): string {
  return value.trim().replace(/[\r\n]+/g, " ").trim();
}

function sanitizeAuthValue(name: string, value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`missing ${name}`);
  }
  if (/[\r\n]/.test(trimmed)) {
    throw new Error(`invalid ${name}`);
  }
  return trimmed;
}

function bearerTokenFromAuthorization(authorization: string): string {
  const trimmed = sanitizeAuthValue("authorization", authorization);
  const parts = trimmed.split(/\s+/);
  const scheme = parts[0];
  const token = parts[1];
  if (parts.length !== 2 || scheme === undefined || token === undefined || scheme.toLowerCase() !== "bearer") {
    throw new Error("authorization must use Bearer token format");
  }
  return sanitizeAuthValue("authorization token", token);
}

function encodeWSAuthProtocol(kind: string, value: string): string {
  const encoded = encodeBase64(utf8Encode(value))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
  return `apic.auth.${kind}.${encoded}`;
}

async function signAPIKey(
  config: ClientConfig,
  method: string,
  path: string,
  bodyBytes: ArrayBuffer,
  transport: "http" | "ws",
): Promise<APIKeySigningResult> {
  if (!config.apiKeySigner) {
    throw new Error("missing API key signer");
  }
  const signed = await config.apiKeySigner({
    method: method.toUpperCase(),
    path,
    bodyBytes,
    transport,
  });
  return {
    authorization: sanitizeAuthValue("authorization", signed.authorization),
    nonce: sanitizeAuthValue("nonce", signed.nonce),
    timestamp: sanitizeAuthValue("timestamp", signed.timestamp),
  };
}

async function buildMultipartBody(payload: Record<string, unknown>): Promise<PreparedBody> {
  if (typeof Blob === "undefined") {
    throw new Error(
      "multipart upload requires Blob; it is unavailable in this runtime (provide a Blob polyfill to upload files on React Native)",
    );
  }
  const boundary = `apic-${randomToken()}`;
  const parts: BlobPart[] = [];

  const pushLine = (line: string): void => {
    parts.push(utf8Bytes(line));
  };

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (isFileUpload(value)) {
      const fieldName = sanitizeMultipartDispositionValue(key);
      const filename = sanitizeMultipartDispositionValue(value.filename ?? key) || fieldName;
      const contentType =
        sanitizeMultipartHeaderValue((value.contentType ?? value.blob.type) || "application/octet-stream") ||
        "application/octet-stream";
      pushLine(`--${boundary}\r\n`);
      pushLine(
        `Content-Disposition: form-data; name="${fieldName}"; filename="${filename}"\r\n`,
      );
      pushLine(`Content-Type: ${contentType}\r\n\r\n`);
      parts.push(value.blob);
      pushLine(`\r\n`);
      continue;
    }
    if (isFileUploadArray(value)) {
      for (const item of value) {
        const fieldName = sanitizeMultipartDispositionValue(key);
        const filename = sanitizeMultipartDispositionValue(item.filename ?? key) || fieldName;
        const contentType =
          sanitizeMultipartHeaderValue((item.contentType ?? item.blob.type) || "application/octet-stream") ||
          "application/octet-stream";
        pushLine(`--${boundary}\r\n`);
        pushLine(
          `Content-Disposition: form-data; name="${fieldName}"; filename="${filename}"\r\n`,
        );
        pushLine(`Content-Type: ${contentType}\r\n\r\n`);
        parts.push(item.blob);
        pushLine(`\r\n`);
      }
      continue;
    }
    if (Array.isArray(value)) {
      const fieldName = sanitizeMultipartDispositionValue(key);
      for (const item of value) {
        pushLine(`--${boundary}\r\n`);
        pushLine(`Content-Disposition: form-data; name="${fieldName}"\r\n\r\n`);
        pushLine(`${String(item)}\r\n`);
      }
      continue;
    }
    const fieldName = sanitizeMultipartDispositionValue(key);
    pushLine(`--${boundary}\r\n`);
    pushLine(`Content-Disposition: form-data; name="${fieldName}"\r\n\r\n`);
    pushLine(`${typeof value === "object" ? JSON.stringify(value) : String(value)}\r\n`);
  }

  pushLine(`--${boundary}--\r\n`);
  const blob = new Blob(parts);
  return {
    body: blob,
    contentType: `multipart/form-data; boundary=${boundary}`,
    bytes: await blob.arrayBuffer(),
  };
}

async function buildBody(mode: string, payload: Record<string, unknown>): Promise<PreparedBody> {
  switch (mode) {
    case "query":
      return { bytes: new ArrayBuffer(0) };
    case "form": {
      const pairs: string[] = [];
      const append = (key: string, value: unknown): void => {
        if (value === undefined || value === null) {
          return;
        }
        if (Array.isArray(value)) {
          for (const item of value) {
            append(key, item);
          }
          return;
        }
        const v = typeof value === "object" ? JSON.stringify(value) : String(value);
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      };
      for (const [key, value] of Object.entries(payload)) {
        append(key, value);
      }
      const bodyText = pairs.join("&");
      return {
        body: bodyText,
        contentType: "application/x-www-form-urlencoded",
        bytes: utf8Bytes(bodyText),
      };
    }
    case "multipart":
      return buildMultipartBody(payload);
    case "json":
    default: {
      if (Object.keys(payload).length === 0) {
        return {
          body: "{}",
          contentType: "application/json",
          bytes: utf8Bytes("{}"),
        };
      }
      const bodyText = JSON.stringify(payload);
      return {
        body: bodyText,
        contentType: "application/json",
        bytes: utf8Bytes(bodyText),
      };
    }
  }
}
const CSRF_COOKIE_NAME = "csrf_token";
const CSRF_HEADER_NAME = "X-CSRF-Token";
const CSRF_ENDPOINT = "/csrf";

let cachedCsrfToken: string | null = null;

/** Reads the CSRF token from the non-HttpOnly cookie. */
export function readCsrfCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + CSRF_COOKIE_NAME + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]!) : null;
}

/** Fetches a fresh CSRF token from the issuance endpoint and caches it. */
export async function refreshCsrfToken(): Promise<string | null> {
  try {
    const res = await fetch(CSRF_ENDPOINT, { method: "GET", credentials: "same-origin" });
    if (!res.ok) {
      cachedCsrfToken = null;
      return null;
    }
    const body = (await res.json().catch(() => ({}))) as { token?: string };
    cachedCsrfToken = body.token ?? readCsrfCookie();
    return cachedCsrfToken;
  } catch {
    cachedCsrfToken = null;
    return null;
  }
}

/** Returns the cached token, falling back to the cookie. */
export function getCsrfToken(): string | null {
  return cachedCsrfToken ?? readCsrfCookie();
}

/** Clears the cached token (call on logout). */
export function clearCsrfToken(): void {
  cachedCsrfToken = null;
}

/** Reports whether a method requires a CSRF token (mirrors the server's unsafe-method set). */
export function isCsrfRequired(method: string): boolean {
  const m = method.toUpperCase();
  return m !== "GET" && m !== "HEAD" && m !== "OPTIONS" && m !== "TRACE";
}

// resolveClientAuthMode reduces a route's auth expression to the single
// application-layer credential the client can present. Single-token values
// pass through unchanged. For a composite expression (e.g. "cac || jwt",
// "mtls && jwt", "(cac && jwt) || api_key") the client can only inject an
// app-layer credential into the request — mtls/cac/piv are TLS
// client-certificate gates satisfied by the transport (the browser's mTLS
// handshake), not by a header — so the client applies the first app-layer
// token it finds (jwt > cookie > api_key) and injects nothing for a
// transport-only expression. The server still enforces the full composite.
function resolveClientAuthMode(authMode: string): string {
  const raw = authMode.trim().toLowerCase();
  if (!/[|&()]/.test(raw)) {
    return raw;
  }
  const tokens = raw.split(/[|&()\s]+/).filter(Boolean);
  for (const pref of ["jwt", "cookie", "api_key"]) {
    if (tokens.includes(pref)) {
      return pref;
    }
  }
  return tokens.includes("public") || tokens.includes("none") ? "none" : "";
}

async function applyHTTPAuth(
  headers: Headers,
  authMode: string,
  url: URL,
  method: string,
  bodyBytes: BufferSource,
  config: ClientConfig,
): Promise<void> {
  const mode = resolveClientAuthMode(authMode);
  // No-auth modes inject nothing. "public" is a valid mode for unauthenticated
  // endpoints (e.g. the explorer's Try button on a public route) — treat it,
  // and the empty/none/unauthenticated variants, as a no-op rather than erroring.
  if (
    mode === "" ||
    mode === "none" ||
    mode === "public" ||
    mode === "unauthenticated"
  ) {
    return;
  }
  if (mode === "jwt") {
    const token = await resolveBearerToken(config.bearerToken);
    if (!token) {
      // No browser-side bearer configured (cookie-only BFF deployment).
      // Skip header injection; rely on credentials: 'same-origin' cookies
      // and let the server make the auth decision (and return 401 if the
      // session is missing or expired).
      return;
    }
    if (!headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return;
  }
  if (mode === "api_key") {
    if (headers.has("Authorization")) {
      return;
    }
    const signed = await signAPIKey(config, method, url.pathname, bodyBytes as ArrayBuffer, "http");
    headers.set("Authorization", signed.authorization);
    headers.set("X-Nonce", signed.nonce);
    headers.set("X-Timestamp", signed.timestamp);
    return;
  }
  if (mode === "cookie") {
    // Cookie/session auth: the session cookie is sent automatically with
    // credentials: 'same-origin' (set on the request). Nothing to inject.
    return;
  }
  throw new Error(`unsupported auth mode "${authMode}"`);
}

async function decodeJSON<T>(response: Response, schemaName?: string): Promise<T> {
  const text = await response.text();
  if (!text) {
    return null as T;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw invalidJSONResponseError(response, text);
  }
  return fromWire(parsed, schemaName) as T;
}

// invalidJSONResponseError builds a typed, actionable HTTPError when a 2xx
// response's body isn't valid JSON — e.g. a reverse proxy/gateway serving an
// HTML error or interstitial page for what the caller expected to be a JSON
// API response, or a static-file/SPA catch-all route unexpectedly matching
// an API path. Without this, JSON.parse's own SyntaxError (something like
// `Unexpected token '<', "<!doctype "... is not valid JSON`) propagated to
// callers verbatim, with no indication of the response's actual status,
// content-type, or body — indistinguishable from the calling page itself
// being broken rather than one specific request having failed.
function invalidJSONResponseError(response: Response, text: string): HTTPError {
  const contentType = response.headers.get("content-type") ?? "unknown";
  const snippet = text.length > 120 ? `${text.slice(0, 120)}…` : text;
  return new HTTPError(
    response.status,
    text,
    {
      error: `Expected a JSON response but received "${contentType}" (status ${response.status}): ${snippet}`,
    },
    "invalid_json_response",
  );
}

function createBufferedStream<T>() {
  const listeners = new Set<MessageHandler<T>>();
  const pending: T[] = [];

  return {
    emit(value: T): void {
      if (listeners.size === 0) {
        if (pending.length >= MAX_BUFFERED_MESSAGES) {
          pending.shift();
        }
        pending.push(value);
        return;
      }
      for (const listener of listeners) {
        listener(value);
      }
    },
    subscribe(handler: MessageHandler<T>): () => void {
      listeners.add(handler);
      if (pending.length > 0) {
        const queued = pending.splice(0, pending.length);
        for (const value of queued) {
          handler(value);
        }
      }
      return () => {
        listeners.delete(handler);
      };
    },
  };
}

// createJSONSocket wraps a raw WebSocket for JSON message exchange. msgSchema
// is the WS route's messageSchema name (empty for an untyped/Record<string,
// unknown> route): send() threads it through to toWire so outbound frames are
// mapped through the SAME schemaWireKeys table typed REST routes use, instead
// of always hitting toWire's untyped/verbatim fallback. Receive threads the
// same msgSchema into fromWire (G-08) so it converts only the messageSchema's
// own declared top-level keys and leaves nested/free-form message data
// untouched, instead of blindly snake_case->camelCasing every key at every
// depth.
function createJSONSocket<T>(socket: WebSocket, msgSchema?: string): JSONSocket<T> {
  const stream = createBufferedStream<T>();
  const listener = (event: MessageEvent<string>) => {
    stream.emit(fromWire(JSON.parse(String(event.data)), msgSchema) as T);
  };
  socket.addEventListener("message", listener as EventListener);
  return {
    socket,
    send(value: T) {
      socket.send(JSON.stringify(toWire(value, msgSchema)));
    },
    close() {
      socket.removeEventListener("message", listener as EventListener);
      socket.close();
    },
    onMessage(handler: (value: T) => void) {
      return stream.subscribe(handler);
    },
  };
}

function createBinarySocket(socket: WebSocket): BinarySocket {
  socket.binaryType = "arraybuffer";
  const stream = createBufferedStream<Uint8Array>();
  const listener = (event: MessageEvent<ArrayBuffer>) => {
    stream.emit(new Uint8Array(event.data));
  };
  socket.addEventListener("message", listener as EventListener);
  return {
    socket,
    send(value: Uint8Array) {
      // TS 5.7+ narrowed WebSocket.send(); widen Uint8Array<ArrayBufferLike>
      // to the accepted BufferSource shape.
      socket.send(value as unknown as BufferSource);
    },
    close() {
      socket.removeEventListener("message", listener as EventListener);
      socket.close();
    },
    onMessage(handler: (value: Uint8Array) => void) {
      return stream.subscribe(handler);
    },
  };
}

export type CacheKey = string;

export function makeCacheKey(method: string, path: string, req: unknown): CacheKey {
  return method.toUpperCase() + " " + path + " " + stableStringify(req);
}

// matchesResource reports whether a cache key belongs to a resource, used to
// scope post-mutation invalidation. The key is "METHOD PATH STABLE-JSON"; the
// PATH is the second space-delimited token. The resource (e.g. "/items") matches
// only at a PATH-SEGMENT boundary — the char immediately after the resource in
// the path must be a boundary (end-of-path, "/", "#" for GraphQL "/graphql#field"
// keys, or "?") — rather than a raw substring. So resource "/items" matches
// "/v1/items" and "/v1/items/{id}" but NOT a sibling like "/v1/items-archive".
// (Matching "/graphql" still invalidates EVERY GraphQL query: GraphQL is one
// path with a "#field" suffix, so this is coarse-but-safe; see the GraphQL
// mutation hooks below.)
function matchesResource(key: CacheKey, resource: string): boolean {
  const path = key.split(" ")[1] ?? "";
  const i = path.indexOf(resource);
  if (i < 0) {
    return false;
  }
  const after = path[i + resource.length];
  return after === undefined || after === "/" || after === "#" || after === "?";
}

// stableStringify produces a key-order-independent JSON string so two requests
// with the same fields in different orders share a cache key.
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }
  const rec = value as Record<string, unknown>;
  const keys = Object.keys(rec).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(rec[k])).join(",") + "}";
}

export type CacheEntry<T = unknown> = {
  data: T | null;
  error: Error | null;
  promise: Promise<T> | null; // in-flight de-dup handle
  updatedAt: number;
  lastAccess: number; // ms (Date.now()) of last get/set, for gcTime + LRU eviction
};

type CacheListener = () => void;

// QueryCache is a minimal dependency-free, BOUNDED keyed cache: single-flight
// dedup, an invalidate-by-key event bus (so a mutation can force dependent
// query hooks to refetch), and dehydrate/hydrate hook-points so a consumer CAN
// layer offline persistence (TSC-06). Memory is bounded two ways (TSC-03):
//   - gcTime: a SETTLED entry not accessed within `gcTimeMs` is treated as
//     absent on the next get (and deleted), and is swept on every set.
//   - LRU: when the settled-entry count exceeds `maxCacheEntries`, the
//     least-recently-used SETTLED entries are evicted until within budget.
// In-flight entries (a pending dedup promise) are NEVER evicted out from under
// a waiting caller — only settled entries are subject to gcTime/LRU. It is NOT
// a full react-query reimplementation. TSC-03/04.
export class QueryCache {
  private readonly entries = new Map<CacheKey, CacheEntry>();
  private readonly listeners = new Map<CacheKey, Set<CacheListener>>();
  private readonly gcTimeMs: number;
  private readonly maxCacheEntries: number;

  constructor(options?: { gcTimeMs?: number; maxCacheEntries?: number }) {
    this.gcTimeMs = options?.gcTimeMs ?? 30 * 60 * 1000;
    this.maxCacheEntries = options?.maxCacheEntries ?? 500;
  }

  get<T>(key: CacheKey): CacheEntry<T> | undefined {
    const entry = this.entries.get(key) as CacheEntry<T> | undefined;
    if (!entry) {
      return undefined;
    }
    // A settled (not in-flight) entry past its gcTime is treated as absent and
    // collected. In-flight entries are never expired (a caller is awaiting).
    if (!entry.promise && this.isExpired(entry)) {
      this.entries.delete(key);
      return undefined;
    }
    entry.lastAccess = Date.now();
    return entry;
  }

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.lastAccess > this.gcTimeMs;
  }

  subscribe(key: CacheKey, listener: CacheListener): () => void {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }

  private notify(key: CacheKey): void {
    const set = this.listeners.get(key);
    if (!set) {
      return;
    }
    for (const l of [...set]) {
      l();
    }
  }

  // fetch de-dups concurrent callers of the same key onto ONE in-flight
  // promise; the resolved value is cached and broadcast to subscribers.
  fetch<T>(key: CacheKey, factory: () => Promise<T>): Promise<T> {
    const existing = this.entries.get(key) as CacheEntry<T> | undefined;
    if (existing?.promise) {
      return existing.promise;
    }
    const now = Date.now();
    const promise = factory()
      .then((data) => {
        // Settle WITHOUT notifying: the initiating caller receives the value via
        // the returned promise (the hook then calls setData). Notifying here
        // would re-fire the very subscriber whose refetch is settling — an
        // infinite fetch->set->notify->refetch loop. Only invalidate() notifies.
        this.set(key, { data, error: null, promise: null, updatedAt: Date.now(), lastAccess: Date.now() }, false);
        return data;
      })
      .catch((err: unknown) => {
        const e = err instanceof Error ? err : new Error(String(err));
        const prev = this.entries.get(key) as CacheEntry<T> | undefined;
        this.set(
          key,
          {
            data: prev?.data ?? null,
            error: e,
            promise: null,
            updatedAt: Date.now(),
            lastAccess: Date.now(),
          },
          false,
        );
        throw e;
      });
    this.set(
      key,
      {
        data: existing?.data ?? null,
        error: null,
        promise,
        updatedAt: existing?.updatedAt ?? 0,
        lastAccess: now,
      },
      false,
    );
    return promise;
  }

  private set<T>(key: CacheKey, entry: CacheEntry<T>, notify = true): void {
    this.entries.set(key, entry as CacheEntry);
    this.evict();
    if (notify) {
      this.notify(key);
    }
  }

  // evict enforces the cache bounds: first drop any settled entry past its
  // gcTime, then — if still over budget — evict least-recently-used SETTLED
  // entries (lowest lastAccess) until at/under maxCacheEntries. In-flight
  // entries are exempt from both passes.
  private evict(): void {
    for (const [k, e] of this.entries) {
      if (!e.promise && this.isExpired(e)) {
        this.entries.delete(k);
      }
    }
    if (this.entries.size <= this.maxCacheEntries) {
      return;
    }
    const settled: Array<[CacheKey, CacheEntry]> = [];
    for (const pair of this.entries) {
      if (!pair[1].promise) {
        settled.push(pair);
      }
    }
    settled.sort((a, b) => a[1].lastAccess - b[1].lastAccess);
    let overBy = this.entries.size - this.maxCacheEntries;
    for (const [k] of settled) {
      if (overBy <= 0) {
        break;
      }
      this.entries.delete(k);
      overBy -= 1;
    }
  }

  // invalidate marks every entry whose key matches the predicate stale
  // (updatedAt = 0) and notifies its subscribers so dependent query hooks
  // refetch. A mutation calls this in its success path. TSC-04.
  invalidate(predicate: (key: CacheKey) => boolean): void {
    for (const key of [...this.listeners.keys()]) {
      if (!predicate(key)) {
        continue;
      }
      const e = this.entries.get(key);
      if (e) {
        this.set(key, { ...e, updatedAt: 0 });
      } else {
        this.notify(key);
      }
    }
  }

  // dehydrate/hydrate are the persistence hook-points (TSC-06): a consumer
  // can serialize the resolved data entries to offline storage and rehydrate
  // on cold start. In-flight promises and errors are never serialized.
  dehydrate(): Array<[CacheKey, { data: unknown; updatedAt: number }]> {
    const out: Array<[CacheKey, { data: unknown; updatedAt: number }]> = [];
    for (const [k, e] of this.entries) {
      if (e.data !== null) {
        out.push([k, { data: e.data, updatedAt: e.updatedAt }]);
      }
    }
    return out;
  }

  hydrate(snapshot: ReadonlyArray<readonly [CacheKey, { data: unknown; updatedAt: number }]>): void {
    for (const [k, v] of snapshot) {
      this.set(k, { data: v.data, error: null, promise: null, updatedAt: v.updatedAt, lastAccess: Date.now() });
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// isRetryableError reports whether a failed request should be retried. Only
// transient failures are retryable: a network/transport error (HTTPError with
// status 0 / code "network_error", TSC-02), an HTTP 5xx, or the two transient
// 4xx the server may emit (408 Request Timeout, 429 Too Many Requests). Any
// other 4xx (400/401/403/404/422/…) is a deterministic client error: retrying
// re-sends the same bad request and, for a destructive method, risks repeating
// a side effect — so it is rethrown immediately. A non-HTTPError throwable is
// treated as non-retryable (it never reached the retry-worthy transport layer).
function isRetryableError(err: unknown): boolean {
  if (!(err instanceof HTTPError)) {
    return false;
  }
  if (err.status === 0) {
    return true; // network/transport failure (offline/DNS/CORS/abort)
  }
  if (err.status >= 500) {
    return true; // server-side transient
  }
  return err.status === 408 || err.status === 429;
}

// fetchWithRetry retries a RETRYABLE failed factory up to `retry` times with
// exponential backoff (backoffMs * 2^attempt). Non-retryable errors (most 4xx)
// are rethrown immediately without a retry. TSC-15.
async function fetchWithRetry<T>(factory: () => Promise<T>, retry: number, backoffMs: number): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await factory();
    } catch (err) {
      if (attempt >= retry || !isRetryableError(err)) {
        throw err;
      }
      await delay(backoffMs * 2 ** attempt);
      attempt += 1;
    }
  }
}

function usePromise<T>(
  client: ApicClient,
  method: string,
  path: string,
  req: unknown,
  factory: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
  options?: QueryHookOptions,
): RequestState<T> {
  const enabled = options?.enabled ?? true;
  const cacheKey = makeCacheKey(method, path, req);
  const cached = client.cache.get<T>(cacheKey);
  const [data, setData] = useState<T | null>(cached?.data ?? null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled && (cached?.data ?? null) === null);
  const { retry, backoffMs } = client.retryConfig;

  const refetch = async (): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      // Route through the shared cache so concurrent identical requests share
      // one in-flight promise (dedup) and one cache entry. TSC-03.
      const value = await client.cache.fetch(cacheKey, () => fetchWithRetry(factory, retry, backoffMs));
      setData(value);
      return value;
    } catch (err) {
      const next = err instanceof Error ? err : new Error(String(err));
      setError(next);
      throw next;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Swallow the effect-path rejection: the error is already captured into hook
    // state (setError -> `error`/`isError`). Leaving it as `void refetch()` would
    // surface an auto-fired query's failure as an UNHANDLED promise rejection
    // (which crashes strict runtimes). The returned `refetch` still rejects for
    // callers that await it manually.
    refetch().catch(() => {});
    // Subscribe so a mutation's invalidate(cacheKey) triggers a refetch. TSC-04.
    return client.cache.subscribe(cacheKey, () => {
      refetch().catch(() => {});
    });
  }, [enabled, cacheKey, ...deps]);

  const isFetching = enabled && loading;
  const isPending = !enabled ? true : loading && data === null;
  const isSuccess = enabled && !loading && error === null && data !== null;
  const isError = error !== null;

  return {
    data,
    error,
    loading,
    isPending,
    isLoading: isPending,
    isFetching,
    isSuccess,
    isError,
    refetch,
  };
}

function useMutationState<TReq, TResp>(
  client: ApicClient,
  mutator: (req: TReq) => Promise<TResp>,
  invalidate?: (cache: QueryCache, req: TReq, resp: TResp) => void,
): MutationState<TReq, TResp> {
  const [data, setData] = useState<TResp | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [settled, setSettled] = useState<boolean>(false);

  const mutateAsync = async (req: TReq): Promise<TResp> => {
    setLoading(true);
    setError(null);
    setSettled(false);
    try {
      const value = await mutator(req);
      setData(value);
      setSettled(true);
      // TSC-04: signal dependent query hooks to refetch.
      invalidate?.(client.cache, req, value);
      return value;
    } catch (err) {
      const next = err instanceof Error ? err : new Error(String(err));
      setError(next);
      setSettled(true);
      throw next;
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    error,
    loading,
    isPending: loading,
    isIdle: !loading && !settled,
    isSuccess: settled && error === null,
    isError: error !== null,
    // mutate is the alias of mutateAsync; both return the same Promise.
    mutate: mutateAsync,
    mutateAsync,
  };
}

function useStream<T>(connect: () => Promise<MessageStream<T>>, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): StreamState<T> {
  const enabled = options?.enabled ?? true;
  const [connected, setConnected] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastMessage, setLastMessage] = useState<T | null>(null);
  const streamRef = useRef<MessageStream<T> | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    let detach: (() => void) | null = null;
    void connect()
      .then((stream) => {
        if (cancelled) {
          stream.close();
          return;
        }
        streamRef.current = stream;
        setConnected(true);
        detach = stream.onMessage((value) => {
          if (!cancelled) {
            setLastMessage(value);
          }
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      });

    return () => {
      cancelled = true;
      setConnected(false);
      detach?.();
      streamRef.current?.close();
      streamRef.current = null;
    };
  }, [enabled, ...deps]);

  return {
    connected,
    error,
    lastMessage,
    close() {
      streamRef.current?.close();
      streamRef.current = null;
      setConnected(false);
    },
  };
}

// DEFAULT_CORRELATION_HEADER is the header carrying an end-to-end correlation
// id on every outbound REST/GraphQL call. Its name mirrors the server's
// observability.tracing.correlation_header; override per-client via
// ClientConfig.correlationHeader.
const DEFAULT_CORRELATION_HEADER = "X-Correlation-ID";

// A correlation id must be a header-safe token: 1..128 chars, starting with an
// alphanumeric and continuing with alphanumerics, '.', '_' or '-'. This grammar
// is CRLF-safe by construction, so a caller-supplied id can never inject a
// header; a value that fails it is dropped and a fresh id generated instead.
const CORRELATION_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
// A W3C traceparent: "00-<32hex>-<16hex>-<2hex>". Forwarded verbatim only when
// well-formed; the client never synthesizes one (no local tracer).
const TRACEPARENT_RE = /^00-[0-9a-fA-F]{32}-[0-9a-fA-F]{16}-[0-9a-fA-F]{2}$/;

function isValidCorrelationId(value: string | undefined): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 128 &&
    CORRELATION_ID_RE.test(value)
  );
}

function isValidTraceparent(value: string | undefined): value is string {
  return typeof value === "string" && TRACEPARENT_RE.test(value);
}

// newCorrelationId mints a random correlation id via crypto.randomUUID (a
// browser/Node stdlib CSPRNG), falling back to a random-hex/time-based token
// when randomUUID is unavailable (older runtimes) — no external dependency.
function newCorrelationId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  if (c && typeof c.getRandomValues === "function") {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`;
}

// injectTracePropagation sets the correlation header (per-call id when valid,
// else a fresh id) and, when the caller supplied a well-formed one, the
// traceparent header. It never clobbers a header already present (so an
// explicitly-set correlation/auth header wins).
function injectTracePropagation(
  headers: Headers,
  options: RequestOptions | undefined,
  config: ClientConfig,
): void {
  const headerName = config.correlationHeader ?? DEFAULT_CORRELATION_HEADER;
  if (!headers.has(headerName)) {
    const cid = isValidCorrelationId(options?.correlationId)
      ? options.correlationId
      : newCorrelationId();
    headers.set(headerName, cid);
  }
  if (isValidTraceparent(options?.traceparent) && !headers.has("traceparent")) {
    headers.set("traceparent", options.traceparent);
  }
}

export class ApicClient {
  private readonly fetchImpl: typeof fetch;
  private readonly config: ClientConfig;
  private readonly queryCache: QueryCache;
  /** Shared query cache (bounded dedup + invalidate + persistence). TSC-03/04/06. */
  get cache(): QueryCache {
    return this.queryCache;
  }
  /** Retry knobs surfaced to hooks (TSC-15). */
  get retryConfig(): { retry: number; backoffMs: number } {
    return { retry: this.config.retry ?? 0, backoffMs: this.config.retryBackoffMs ?? 250 };
  }

  constructor(config: ClientConfig) {
    this.config = { ...config, baseUrl: normalizeBaseURL(config.baseUrl) };
    // Bound the shared cache by gcTime + LRU so it cannot grow unbounded. TSC-03.
    this.queryCache = new QueryCache({
      ...(this.config.gcTimeMs === undefined ? {} : { gcTimeMs: this.config.gcTimeMs }),
      ...(this.config.maxCacheEntries === undefined ? {} : { maxCacheEntries: this.config.maxCacheEntries }),
    });
    // Native `fetch` must be invoked with `this === window`; calling it
    // as `this.fetchImpl(...)` would bind `this` to the class instance
    // and Chrome throws "Illegal invocation". Bind to globalThis when
    // the caller didn't supply an override.
    this.fetchImpl = this.config.fetchImpl ?? fetch.bind(globalThis);
  }

  // sendRequest runs the full request pipeline (path expansion, body build,
  // auth, CSRF 403-retry, 401-retry) and returns the resolved Response WITHOUT
  // the ok-check, so request<T> and requestRaw can share one pipeline. L-32b.
  private async sendRequest(
    method: string,
    path: string,
    authMode: string,
    bodyMode: string,
    pathParams: readonly (readonly [string, string])[],
    request: Record<string, unknown>,
    requestSchema: string,
    options?: RequestOptions,
  ): Promise<Response> {
    // Convert caller camelCase keys to the schema's original wire keys BEFORE
    // path expansion so the path-param lookup (expandPath uses schemaKey, which
    // is the schema property name) and body/query serialization both see the
    // correct wire keys. apic#216: for camelCase schema fields (e.g. tenantId)
    // the schema-aware toWire preserves the key unchanged so expandPath can find
    // it; for snake_case schema fields (e.g. tenant_id) it maps the camelCase
    // caller key back to the snake_case wire key that the pathParams tuple names.
    const wireRequest = toWire(request, requestSchema) as Record<string, unknown>;
    const { url, payload } = expandPath(this.config.baseUrl, path, pathParams, wireRequest);
    if (bodyMode === "query") {
      for (const [key, value] of Object.entries(payload)) {
        appendSearchParam(url.searchParams, key, value);
      }
    }
    const prepared = await buildBody(bodyMode, payload);
    const headers = new Headers(this.config.headers ?? {});
    if (prepared.contentType) {
      headers.set("Content-Type", prepared.contentType);
    }
    // Caller headers override config defaults but are applied BEFORE auth so
    // auth/CSRF headers (set below) always win — a caller cannot strip or
    // spoof Authorization/CSRF via options.headers.
    if (options?.headers) {
      const extra = new Headers(options.headers);
      extra.forEach((value, key) => headers.set(key, value));
    }
    // Correlation (+ optional traceparent) propagation runs before auth so it
    // composes with (never replaces) the auth/CSRF headers set below.
    injectTracePropagation(headers, options, this.config);
    headers.set("Accept", "application/json");
    await applyHTTPAuth(headers, authMode, url, method, prepared.bytes, this.config);
    const merged = mergeAbortSignal(options?.signal, options?.timeoutMs);
    try {
    if (isCsrfRequired(method)) {
      const tok = getCsrfToken();
      if (tok) {
        headers.set(CSRF_HEADER_NAME, tok);
      }
    }
    const requestInit: RequestInit = {
      method,
      headers,
      body: bodyMode === "query" ? null : (prepared.body ?? null),
      credentials: "same-origin",
      signal: merged.signal ?? null,
    };
    let response: Response;
    try {
      response = await this.fetchImpl(url, requestInit);
    } catch (err) {
      throw networkError(err);
    }
    // CSRF is a cookie-session concern only: the double-submit token is
    // bound to the session cookie, so a stale/missing token can only ever
    // explain a 403 on a COOKIE-authenticated call. For jwt/api_key/mtls
    // auth modes there is no CSRF cookie in play at all, so a 403 there is
    // necessarily an application-level rejection (e.g. an authorization or
    // business-rule check unrelated to CSRF). Retrying those blindly
    // resubmits the same mutating request a second time purely because the
    // heuristic misread an unrelated 403 as CSRF — for a one-shot/idempotency-
    // sensitive endpoint (e.g. a ceremony session consumed on first use) the
    // retry can silently reuse a resource the FIRST attempt already
    // consumed, surfacing a confusing secondary error instead of the real one.
    if (
      response.status === 403 &&
      isCsrfRequired(method) &&
      resolveClientAuthMode(authMode) === "cookie"
    ) {
      const fresh = await refreshCsrfToken();
      if (fresh) {
        headers.set(CSRF_HEADER_NAME, fresh);
        try {
          response = await this.fetchImpl(url, { ...requestInit, headers });
        } catch (err) {
          throw networkError(err);
        }
      }
    }
    if (response.status === 401 && this.config.onUnauthorized) {
      let refreshed = false;
      try {
        refreshed = (await this.config.onUnauthorized()) === true;
      } catch {
        refreshed = false;
      }
      if (refreshed) {
        try {
          // Re-apply auth so a refreshed bearer token is picked up.
          const retryHeaders = new Headers(headers);
          retryHeaders.delete("Authorization");
          await applyHTTPAuth(retryHeaders, authMode, url, method, prepared.bytes, this.config);
          response = await this.fetchImpl(url, {
            method,
            headers: retryHeaders,
            body: bodyMode === "query" ? null : (prepared.body ?? null),
            credentials: "same-origin",
            signal: merged.signal ?? null,
          });
        } catch (err) {
          throw networkError(err);
        }
      }
    }
      return response;
    } finally {
      merged.cleanup();
    }
  }

  private async request<T>(
    method: string,
    path: string,
    authMode: string,
    bodyMode: string,
    pathParams: readonly (readonly [string, string])[],
    request: Record<string, unknown>,
    responseKind: "json" | "blob" | "arrayBuffer",
    requestSchema: string,
    options?: RequestOptions,
    responseSchema?: string,
  ): Promise<T> {
    const response = await this.sendRequest(method, path, authMode, bodyMode, pathParams, request, requestSchema, options);
    if (!response.ok) {
      throw await httpErrorFromResponse(response);
    }
    if (responseKind === "blob") {
      return (await response.blob()) as T;
    }
    if (responseKind === "arrayBuffer") {
      return (await response.arrayBuffer()) as T;
    }
    return decodeJSON<T>(response, responseSchema);
  }

  // requestRaw runs the full request pipeline and returns the raw Response
  // (after the ok-check) for callers that need streaming/Blob/headers. L-32b.
  // Public so consumers can access raw Response (headers, streaming) directly.
  async requestRaw(
    method: string,
    path: string,
    authMode: string,
    bodyMode: string,
    pathParams: readonly (readonly [string, string])[],
    request: Record<string, unknown>,
    requestSchema = "",
    options?: RequestOptions,
  ): Promise<Response> {
    const response = await this.sendRequest(method, path, authMode, bodyMode, pathParams, request, requestSchema, options);
    if (!response.ok) {
      throw await httpErrorFromResponse(response);
    }
    return response;
  }

  private async graphQL<T>(
    query: string,
    field: string,
    authMode: string,
    variables?: Record<string, unknown>,
    requestSchema = "",
    responseSchema = "",
  ): Promise<T> {
    const url = new URL(this.config.baseUrl);
    url.pathname = joinURLPath(url.pathname, "/graphql");
    const bodyText = JSON.stringify({ query, variables: variables === undefined ? undefined : toWire(variables, requestSchema) });
    const bodyBytes = utf8Bytes(bodyText);
    const headers = new Headers(this.config.headers ?? {});
    headers.set("Content-Type", "application/json");
    // Correlation (+ optional traceparent) propagation runs before auth so it
    // composes with (never replaces) the auth/CSRF headers set below — same
    // ordering as sendRequest()'s REST path. graphQL<T> has no RequestOptions
    // parameter (unlike the per-route REST methods), so `options` below is
    // always undefined: injectTracePropagation's isValidCorrelationId check
    // always fails and every GraphQL call mints a brand-new id via
    // newCorrelationId() instead. There is no per-call override AND no
    // config-level default to fall back to first — ClientConfig carries no
    // correlation-id (or traceparent) field, only correlationHeader (which
    // overrides the header NAME, not the value) — so a traceparent is never
    // sent on this surface either, since isValidTraceparent(undefined) is
    // always false.
    injectTracePropagation(headers, undefined, this.config);
    headers.set("Accept", "application/json");
    await applyHTTPAuth(headers, authMode, url, "POST", bodyBytes, this.config);
    {
      const tok = getCsrfToken();
      if (tok) {
        headers.set(CSRF_HEADER_NAME, tok);
      }
    }
    const requestInit: RequestInit = {
      method: "POST",
      headers,
      body: bodyText,
      credentials: "same-origin",
    };
    let response: Response;
    try {
      response = await this.fetchImpl(url, requestInit);
    } catch (err) {
      throw networkError(err);
    }
    // See the analogous guard in request() above: CSRF only applies to
    // cookie-authenticated calls, so a 403 on any other auth mode is an
    // application-level rejection, not a stale token — retrying it would
    // blindly resubmit the same GraphQL mutation a second time.
    if (response.status === 403 && resolveClientAuthMode(authMode) === "cookie") {
      const fresh = await refreshCsrfToken();
      if (fresh) {
        headers.set(CSRF_HEADER_NAME, fresh);
        try {
          response = await this.fetchImpl(url, { ...requestInit, headers });
        } catch (err) {
          throw networkError(err);
        }
      }
    }
    if (response.status === 401 && this.config.onUnauthorized) {
      let refreshed = false;
      try { refreshed = (await this.config.onUnauthorized()) === true; } catch { refreshed = false; }
      if (refreshed) {
        try {
          const retryHeaders = new Headers(headers);
          retryHeaders.delete("Authorization");
          // retryHeaders is a copy of headers (which already carries the
          // correlation/traceparent pair set above), so this is a no-op in
          // practice — injectTracePropagation never clobbers an existing
          // header. Asserted explicitly so the retried request is
          // self-sufficient even if a future refactor changes how
          // retryHeaders is built.
          injectTracePropagation(retryHeaders, undefined, this.config);
          await applyHTTPAuth(retryHeaders, authMode, url, "POST", bodyBytes, this.config);
          response = await this.fetchImpl(url, { method: "POST", headers: retryHeaders, body: bodyText, credentials: "same-origin" });
        } catch (err) { throw networkError(err); }
      }
    }
    if (!response.ok) {
      throw await httpErrorFromResponse(response);
    }
    const envelope = (await response.json()) as GraphQLEnvelope;
    if (envelope.errors && envelope.errors.length > 0) {
      const firstError = envelope.errors[0];
      throw new Error(firstError ? firstError.message : "graphql error");
    }
    return (fromWire(envelope.data?.[field] ?? null, responseSchema)) as T;
  }

  private async websocketURL(path: string): Promise<string> {
    const url = new URL(this.config.baseUrl);
    url.pathname = joinURLPath(url.pathname, path);
    if (url.protocol === "https:") {
      url.protocol = "wss:";
    } else if (url.protocol === "http:") {
      url.protocol = "ws:";
    }
    return url.toString();
  }

  private async websocketProtocols(
    path: string,
    authMode: string,
    protocols?: string | string[],
  ): Promise<string | string[] | undefined> {
    const nextProtocols = Array.isArray(protocols)
      ? [...protocols]
      : protocols
        ? [protocols]
        : [];
    // L-19: always offer the reserved non-secret sentinel so the server can
    // echo a Sec-WebSocket-Protocol on the 101 even for endpoints with no
    // configured protocols. Strict clients (node-24 undici) fail the handshake
    // when they offer subprotocols but the server selects none. The server
    // prefers a real configured protocol (e.g. binary-v1) and only falls back
    // to this sentinel, so existing negotiation is unaffected.
    if (!nextProtocols.includes("apic.ws.v1")) {
      nextProtocols.push("apic.ws.v1");
    }
    const mode = resolveClientAuthMode(authMode);
    if (mode === "jwt") {
      const token = await resolveBearerToken(this.config.bearerToken);
      if (token) {
        nextProtocols.push(encodeWSAuthProtocol("bearer", token));
      }
      // No browser-side bearer configured (cookie-only BFF deployment).
      // Omit the bearer.<token> Sec-WebSocket-Protocol entry; the
      // session cookie sent on the WebSocket handshake (when origin
      // matches) carries the auth, and the server returns 401 if it
      // isn't present.
    } else if (mode === "api_key") {
      const signed = await signAPIKey(this.config, "GET", path, new ArrayBuffer(0), "ws");
      nextProtocols.push(encodeWSAuthProtocol("sig", bearerTokenFromAuthorization(signed.authorization)));
      nextProtocols.push(encodeWSAuthProtocol("nonce", signed.nonce));
      nextProtocols.push(encodeWSAuthProtocol("ts", signed.timestamp));
    } else if (!(mode === "" || mode === "none" || mode === "unauthenticated")) {
      throw new Error(`unsupported auth mode "${authMode}"`);
    }
    if (nextProtocols.length === 0) {
      return undefined;
    }
    if (nextProtocols.length === 1) {
      return nextProtocols[0];
    }
    return nextProtocols;
  }

  private openSocket(url: string, protocols?: string | string[]): Promise<WebSocket> {
    const factory =
      this.config.webSocketFactory ??
      ((nextUrl: string, nextProtocols?: string | string[]) => {
        if (typeof WebSocket === "undefined") {
          throw new Error(
            "WebSocket is unavailable in this runtime; provide config.webSocketFactory to open sockets on React Native",
          );
        }
        return new WebSocket(nextUrl, nextProtocols);
      });
    return new Promise((resolve, reject) => {
      const socket = factory(url, protocols);
      const onOpen = () => {
        cleanup();
        resolve(socket);
      };
      const onError = () => {
        cleanup();
        reject(new Error("websocket connection failed"));
      };
      const cleanup = () => {
        socket.removeEventListener("open", onOpen);
        socket.removeEventListener("error", onError);
      };
      socket.addEventListener("open", onOpen);
      socket.addEventListener("error", onError);
    });
  }
  async adminRotateSigningKeys(req: AdminRotateSigningKeysReq,options?: RequestOptions): Promise<AdminRotateSigningKeysResp> {
    return this.request<AdminRotateSigningKeysResp>(
      "POST",
      "/v1/admin/keys/rotate",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "AdminRotateSigningKeysReq",
      options,
      "AdminRotateSigningKeysResp",
    );
  }
  async adminListClientCAs(req: AdminMTLSEmptyReq,options?: RequestOptions): Promise<AdminClientCAListResp> {
    return this.request<AdminClientCAListResp>(
      "GET",
      "/v1/admin/mtls/cas",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "AdminMTLSEmptyReq",
      options,
      "AdminClientCAListResp",
    );
  }
  async adminCreateClientCA(req: AdminCreateClientCAReq,options?: RequestOptions): Promise<AdminClientCAResp> {
    return this.request<AdminClientCAResp>(
      "POST",
      "/v1/admin/mtls/cas",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "AdminCreateClientCAReq",
      options,
      "AdminClientCAResp",
    );
  }
  async adminDeleteClientCA(req: AdminGetClientCAReq,options?: RequestOptions): Promise<AdminClientCAResp> {
    return this.request<AdminClientCAResp>(
      "DELETE",
      "/v1/admin/mtls/cas/{id}",
      "",
      "query",
      [["id", "id"]], copyRecord(req),
      "json",
      "AdminGetClientCAReq",
      options,
      "AdminClientCAResp",
    );
  }
  async adminGetClientCA(req: AdminGetClientCAReq,options?: RequestOptions): Promise<AdminClientCAResp> {
    return this.request<AdminClientCAResp>(
      "GET",
      "/v1/admin/mtls/cas/{id}",
      "",
      "query",
      [["id", "id"]], copyRecord(req),
      "json",
      "AdminGetClientCAReq",
      options,
      "AdminClientCAResp",
    );
  }
  async adminUpdateClientCA(req: AdminUpdateClientCAReq,options?: RequestOptions): Promise<AdminClientCAResp> {
    return this.request<AdminClientCAResp>(
      "PATCH",
      "/v1/admin/mtls/cas/{id}",
      "",
      "json",
      [["id", "id"]], copyRecord(req),
      "json",
      "AdminUpdateClientCAReq",
      options,
      "AdminClientCAResp",
    );
  }
  async adminMTLSStatus(req: AdminMTLSEmptyReq,options?: RequestOptions): Promise<AdminMTLSStatusResp> {
    return this.request<AdminMTLSStatusResp>(
      "GET",
      "/v1/admin/mtls/status",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "AdminMTLSEmptyReq",
      options,
      "AdminMTLSStatusResp",
    );
  }
  async adminListTenants(req: AdminListTenantsReq,options?: RequestOptions): Promise<AdminListTenantsResp> {
    return this.request<AdminListTenantsResp>(
      "GET",
      "/v1/admin/tenants",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "AdminListTenantsReq",
      options,
      "AdminListTenantsResp",
    );
  }
  async adminCreateTenant(req: AdminCreateTenantReq,options?: RequestOptions): Promise<AdminTenantResp> {
    return this.request<AdminTenantResp>(
      "POST",
      "/v1/admin/tenants",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "AdminCreateTenantReq",
      options,
      "AdminTenantResp",
    );
  }
  async adminDeleteTenant(req: AdminGetTenantReq,options?: RequestOptions): Promise<AdminTenantResp> {
    return this.request<AdminTenantResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetTenantReq",
      options,
      "AdminTenantResp",
    );
  }
  async adminGetTenant(req: AdminGetTenantReq,options?: RequestOptions): Promise<AdminTenantResp> {
    return this.request<AdminTenantResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetTenantReq",
      options,
      "AdminTenantResp",
    );
  }
  async adminUpdateTenant(req: AdminUpdateTenantReq,options?: RequestOptions): Promise<AdminTenantResp> {
    return this.request<AdminTenantResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminUpdateTenantReq",
      options,
      "AdminTenantResp",
    );
  }
  async adminListAccessRequests(req: AdminListAccessRequestsReq,options?: RequestOptions): Promise<AdminListAccessRequestsResp> {
    return this.request<AdminListAccessRequestsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/access-requests/",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListAccessRequestsReq",
      options,
      "AdminListAccessRequestsResp",
    );
  }
  async adminCreateAccessRequest(req: AdminCreateAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminGetAccessRequest(req: AdminGetAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminActivateAccessRequest(req: AdminGetAccessRequestReq,options?: RequestOptions): Promise<PamAccessRequestActivationResp> {
    return this.request<PamAccessRequestActivationResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/activate",
      "",
      "json",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAccessRequestReq",
      options,
      "PamAccessRequestActivationResp",
    );
  }
  async adminApproveAccessRequest(req: AdminApproveAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/approve",
      "",
      "json",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminApproveAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminCancelAccessRequest(req: AdminGetAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/cancel",
      "",
      "json",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminDenyAccessRequest(req: AdminDenyAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/deny",
      "",
      "json",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminDenyAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminGetAccessRequestEvidence(req: AdminGetAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/evidence",
      "",
      "query",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminGetAccessRequestEvidencePackage(req: AdminGetAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/evidence-package",
      "",
      "query",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminExtendAccessRequest(req: AdminExtendAccessRequestReq,options?: RequestOptions): Promise<AdminAccessRequestResp> {
    return this.request<AdminAccessRequestResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/extend",
      "",
      "json",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminExtendAccessRequestReq",
      options,
      "AdminAccessRequestResp",
    );
  }
  async adminListAPIKeys(req: AdminListAPIKeysReq,options?: RequestOptions): Promise<AdminListAPIKeysResp> {
    return this.request<AdminListAPIKeysResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apikeys",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListAPIKeysReq",
      options,
      "AdminListAPIKeysResp",
    );
  }
  async adminMintAPIKey(req: AdminMintAPIKeyReq,options?: RequestOptions): Promise<AdminAPIKeySecretResp> {
    return this.request<AdminAPIKeySecretResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apikeys",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminMintAPIKeyReq",
      options,
      "AdminAPIKeySecretResp",
    );
  }
  async adminRevokeAPIKey(req: AdminGetAPIKeyReq,options?: RequestOptions): Promise<AdminAPIKeyResp> {
    return this.request<AdminAPIKeyResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/apikeys/{keyId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["keyId", "keyId"]], copyRecord(req),
      "json",
      "AdminGetAPIKeyReq",
      options,
      "AdminAPIKeyResp",
    );
  }
  async adminGetAPIKey(req: AdminGetAPIKeyReq,options?: RequestOptions): Promise<AdminAPIKeyResp> {
    return this.request<AdminAPIKeyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apikeys/{keyId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["keyId", "keyId"]], copyRecord(req),
      "json",
      "AdminGetAPIKeyReq",
      options,
      "AdminAPIKeyResp",
    );
  }
  async adminRotateAPIKey(req: AdminRotateAPIKeyReq,options?: RequestOptions): Promise<AdminAPIKeySecretResp> {
    return this.request<AdminAPIKeySecretResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apikeys/{keyId}/rotate",
      "",
      "json",
      [["tenantId", "tenantId"], ["keyId", "keyId"]], copyRecord(req),
      "json",
      "AdminRotateAPIKeyReq",
      options,
      "AdminAPIKeySecretResp",
    );
  }
  async adminListApps(req: AdminListAppsReq,options?: RequestOptions): Promise<AdminListAppsResp> {
    return this.request<AdminListAppsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListAppsReq",
      options,
      "AdminListAppsResp",
    );
  }
  async adminCreateApp(req: AdminCreateAppReq,options?: RequestOptions): Promise<AdminAppResp> {
    return this.request<AdminAppResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apps",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateAppReq",
      options,
      "AdminAppResp",
    );
  }
  async adminDeleteApp(req: AdminGetAppReq,options?: RequestOptions): Promise<AdminAppResp> {
    return this.request<AdminAppResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/apps/{appId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminGetAppReq",
      options,
      "AdminAppResp",
    );
  }
  async adminGetApp(req: AdminGetAppReq,options?: RequestOptions): Promise<AdminAppResp> {
    return this.request<AdminAppResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminGetAppReq",
      options,
      "AdminAppResp",
    );
  }
  async adminUpdateApp(req: AdminUpdateAppReq,options?: RequestOptions): Promise<AdminAppResp> {
    return this.request<AdminAppResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/apps/{appId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminUpdateAppReq",
      options,
      "AdminAppResp",
    );
  }
  async adminListAppChildren(req: AdminListAppChildrenReq,options?: RequestOptions): Promise<AdminListAppsResp> {
    return this.request<AdminListAppsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/children",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminListAppChildrenReq",
      options,
      "AdminListAppsResp",
    );
  }
  async adminDeleteAppClaimsSurface(req: AdminGetAppReq,options?: RequestOptions): Promise<AdminAppClaimsSurfaceResp> {
    return this.request<AdminAppClaimsSurfaceResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/claims-surface",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminGetAppReq",
      options,
      "AdminAppClaimsSurfaceResp",
    );
  }
  async adminGetAppClaimsSurface(req: AdminGetAppClaimsSurfaceReq,options?: RequestOptions): Promise<AdminAppClaimsSurfaceResp> {
    return this.request<AdminAppClaimsSurfaceResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/claims-surface",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminGetAppClaimsSurfaceReq",
      options,
      "AdminAppClaimsSurfaceResp",
    );
  }
  async adminPutAppClaimsSurface(req: AdminPutAppClaimsSurfaceReq,options?: RequestOptions): Promise<AdminAppClaimsSurfaceResp> {
    return this.request<AdminAppClaimsSurfaceResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/claims-surface",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminPutAppClaimsSurfaceReq",
      options,
      "AdminAppClaimsSurfaceResp",
    );
  }
  async adminPreviewAppClaimsSurface(req: AdminPreviewAppClaimsSurfaceReq,options?: RequestOptions): Promise<AdminAppClaimsSurfacePreviewResp> {
    return this.request<AdminAppClaimsSurfacePreviewResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/claims-surface/preview",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminPreviewAppClaimsSurfaceReq",
      options,
      "AdminAppClaimsSurfacePreviewResp",
    );
  }
  async adminListAppRoles(req: AdminListAppRolesReq,options?: RequestOptions): Promise<AdminListAppRolesResp> {
    return this.request<AdminListAppRolesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminListAppRolesReq",
      options,
      "AdminListAppRolesResp",
    );
  }
  async adminCreateAppRole(req: AdminCreateAppRoleReq,options?: RequestOptions): Promise<AdminAppRoleResp> {
    return this.request<AdminAppRoleResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"]], copyRecord(req),
      "json",
      "AdminCreateAppRoleReq",
      options,
      "AdminAppRoleResp",
    );
  }
  async adminDeleteAppRole(req: AdminGetAppRoleReq,options?: RequestOptions): Promise<AdminAppRoleResp> {
    return this.request<AdminAppRoleResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminGetAppRoleReq",
      options,
      "AdminAppRoleResp",
    );
  }
  async adminGetAppRole(req: AdminGetAppRoleReq,options?: RequestOptions): Promise<AdminAppRoleResp> {
    return this.request<AdminAppRoleResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminGetAppRoleReq",
      options,
      "AdminAppRoleResp",
    );
  }
  async adminUpdateAppRole(req: AdminUpdateAppRoleReq,options?: RequestOptions): Promise<AdminAppRoleResp> {
    return this.request<AdminAppRoleResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminUpdateAppRoleReq",
      options,
      "AdminAppRoleResp",
    );
  }
  async adminListAppRoleAssignments(req: AdminListAppRoleAssignmentsReq,options?: RequestOptions): Promise<AdminListAppRoleAssignmentsResp> {
    return this.request<AdminListAppRoleAssignmentsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}/assignments",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminListAppRoleAssignmentsReq",
      options,
      "AdminListAppRoleAssignmentsResp",
    );
  }
  async adminCreateAppRoleAssignment(req: AdminCreateAppRoleAssignmentReq,options?: RequestOptions): Promise<AdminAppRoleAssignmentResp> {
    return this.request<AdminAppRoleAssignmentResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}/assignments",
      "",
      "json",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminCreateAppRoleAssignmentReq",
      options,
      "AdminAppRoleAssignmentResp",
    );
  }
  async adminDeleteAppRoleAssignment(req: AdminDeleteAppRoleAssignmentReq,options?: RequestOptions): Promise<AdminAppRoleAssignmentResp> {
    return this.request<AdminAppRoleAssignmentResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}/assignments/{assignmentId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["appId", "appId"], ["roleId", "roleId"], ["assignmentId", "assignmentId"]], copyRecord(req),
      "json",
      "AdminDeleteAppRoleAssignmentReq",
      options,
      "AdminAppRoleAssignmentResp",
    );
  }
  async adminQueryAudit(req: AdminQueryAuditReq,options?: RequestOptions): Promise<AdminQueryAuditResp> {
    return this.request<AdminQueryAuditResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/audit",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminQueryAuditReq",
      options,
      "AdminQueryAuditResp",
    );
  }
  async adminGetAuditCaseFile(req: AdminGetAuditCaseFileReq,options?: RequestOptions): Promise<AdminQueryAuditResp> {
    return this.request<AdminQueryAuditResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/audit/case-file/{requestId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["requestId", "requestId"]], copyRecord(req),
      "json",
      "AdminGetAuditCaseFileReq",
      options,
      "AdminQueryAuditResp",
    );
  }
  async adminGetAuditChainIntegrity(req: AdminGetAuditChainIntegrityReq,options?: RequestOptions): Promise<AdminGetAuditChainIntegrityResp> {
    return this.request<AdminGetAuditChainIntegrityResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/audit/chain-integrity",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetAuditChainIntegrityReq",
      options,
      "AdminGetAuditChainIntegrityResp",
    );
  }
  async adminRebaselineAuditChain(req: AdminRebaselineAuditChainReq,options?: RequestOptions): Promise<AdminRebaselineAuditChainResp> {
    return this.request<AdminRebaselineAuditChainResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/audit/chain-rebaseline",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminRebaselineAuditChainReq",
      options,
      "AdminRebaselineAuditChainResp",
    );
  }
  async adminListBreakGlassEvents(req: AdminListBreakGlassEventsReq,options?: RequestOptions): Promise<AdminListBreakGlassEventsResp> {
    return this.request<AdminListBreakGlassEventsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/break-glass/",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListBreakGlassEventsReq",
      options,
      "AdminListBreakGlassEventsResp",
    );
  }
  async adminActivateBreakGlass(req: AdminActivateBreakGlassReq,options?: RequestOptions): Promise<AdminBreakGlassEventResp> {
    return this.request<AdminBreakGlassEventResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/break-glass/",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminActivateBreakGlassReq",
      options,
      "AdminBreakGlassEventResp",
    );
  }
  async adminListUnreviewedBreakGlassEvents(req: AdminListBreakGlassEventsReq,options?: RequestOptions): Promise<AdminListBreakGlassEventsResp> {
    return this.request<AdminListBreakGlassEventsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/break-glass/unreviewed",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListBreakGlassEventsReq",
      options,
      "AdminListBreakGlassEventsResp",
    );
  }
  async adminGetBreakGlassEvent(req: AdminGetBreakGlassEventReq,options?: RequestOptions): Promise<AdminBreakGlassEventResp> {
    return this.request<AdminBreakGlassEventResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/break-glass/{eventId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["eventId", "eventId"]], copyRecord(req),
      "json",
      "AdminGetBreakGlassEventReq",
      options,
      "AdminBreakGlassEventResp",
    );
  }
  async adminCompleteBreakGlassReview(req: AdminCompleteBreakGlassReviewReq,options?: RequestOptions): Promise<AdminBreakGlassEventResp> {
    return this.request<AdminBreakGlassEventResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/break-glass/{eventId}/review",
      "",
      "json",
      [["tenantId", "tenantId"], ["eventId", "eventId"]], copyRecord(req),
      "json",
      "AdminCompleteBreakGlassReviewReq",
      options,
      "AdminBreakGlassEventResp",
    );
  }
  async adminListBrokeredSessions(req: AdminListBrokeredSessionsReq,options?: RequestOptions): Promise<AdminListBrokeredSessionsResp> {
    return this.request<AdminListBrokeredSessionsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/brokered-sessions",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListBrokeredSessionsReq",
      options,
      "AdminListBrokeredSessionsResp",
    );
  }
  async adminInitiateBrokeredSession(req: AdminInitiateBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/brokered-sessions",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminInitiateBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminKillBrokeredSession(req: AdminKillBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminKillBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminGetBrokeredSession(req: AdminGetBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminGetBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminMonitorBrokeredSession(req: AdminGetBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/monitor",
      "",
      "query",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminGetBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminPauseBrokeredSession(req: AdminGetBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/pause",
      "",
      "json",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminGetBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminGetBrokeredSessionRecordings(req: AdminGetBrokeredSessionRecordingsReq,options?: RequestOptions): Promise<AdminGetBrokeredSessionRecordingsResp> {
    return this.request<AdminGetBrokeredSessionRecordingsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/recordings",
      "",
      "query",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminGetBrokeredSessionRecordingsReq",
      options,
      "AdminGetBrokeredSessionRecordingsResp",
    );
  }
  async adminResumeBrokeredSession(req: AdminResumeBrokeredSessionReq,options?: RequestOptions): Promise<AdminBrokeredSessionResp> {
    return this.request<AdminBrokeredSessionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/resume",
      "",
      "json",
      [["tenantId", "tenantId"], ["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "AdminResumeBrokeredSessionReq",
      options,
      "AdminBrokeredSessionResp",
    );
  }
  async adminGetCAEConfig(req: AdminGetCAEConfigReq,options?: RequestOptions): Promise<AdminCAEConfigResp> {
    return this.request<AdminCAEConfigResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/cae/config",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetCAEConfigReq",
      options,
      "AdminCAEConfigResp",
    );
  }
  async adminUpdateCAEConfig(req: AdminUpdateCAEConfigReq,options?: RequestOptions): Promise<AdminUpdateCAEConfigResp> {
    return this.request<AdminUpdateCAEConfigResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/cae/config",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminUpdateCAEConfigReq",
      options,
      "AdminUpdateCAEConfigResp",
    );
  }
  async adminTriggerCAEEvaluate(req: AdminTriggerCAEEvaluateReq,options?: RequestOptions): Promise<AdminTriggerCAEEvaluateResp> {
    return this.request<AdminTriggerCAEEvaluateResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/cae/evaluate/{subjectId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminTriggerCAEEvaluateReq",
      options,
      "AdminTriggerCAEEvaluateResp",
    );
  }
  async adminListCAEEvents(req: AdminListCAEEventsReq,options?: RequestOptions): Promise<AdminListCAEEventsResp> {
    return this.request<AdminListCAEEventsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/cae/events",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListCAEEventsReq",
      options,
      "AdminListCAEEventsResp",
    );
  }
  async adminGetCAEEvent(req: AdminGetCAEEventReq,options?: RequestOptions): Promise<AdminCAEEventResp> {
    return this.request<AdminCAEEventResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/cae/events/{eventId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["eventId", "eventId"]], copyRecord(req),
      "json",
      "AdminGetCAEEventReq",
      options,
      "AdminCAEEventResp",
    );
  }
  async adminListHighRiskSubjects(req: AdminListHighRiskSubjectsReq,options?: RequestOptions): Promise<AdminListHighRiskSubjectsResp> {
    return this.request<AdminListHighRiskSubjectsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/cae/risk",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListHighRiskSubjectsReq",
      options,
      "AdminListHighRiskSubjectsResp",
    );
  }
  async adminGetCAERiskState(req: AdminGetCAERiskStateReq,options?: RequestOptions): Promise<AdminCAERiskStateResp> {
    return this.request<AdminCAERiskStateResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/cae/risk/{subjectId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminGetCAERiskStateReq",
      options,
      "AdminCAERiskStateResp",
    );
  }
  async adminOverrideCAERisk(req: AdminOverrideCAERiskReq,options?: RequestOptions): Promise<AdminOverrideCAERiskResp> {
    return this.request<AdminOverrideCAERiskResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/cae/risk/{subjectId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminOverrideCAERiskReq",
      options,
      "AdminOverrideCAERiskResp",
    );
  }
  async adminListClients(req: AdminListClientsReq,options?: RequestOptions): Promise<AdminListClientsResp> {
    return this.request<AdminListClientsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/clients",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListClientsReq",
      options,
      "AdminListClientsResp",
    );
  }
  async adminCreateClient(req: AdminCreateClientReq,options?: RequestOptions): Promise<AdminCreateClientResp> {
    return this.request<AdminCreateClientResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/clients",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateClientReq",
      options,
      "AdminCreateClientResp",
    );
  }
  async adminDeleteClient(req: AdminGetClientReq,options?: RequestOptions): Promise<AdminClientResp> {
    return this.request<AdminClientResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/clients/{clientId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["clientId", "clientId"]], copyRecord(req),
      "json",
      "AdminGetClientReq",
      options,
      "AdminClientResp",
    );
  }
  async adminGetClient(req: AdminGetClientReq,options?: RequestOptions): Promise<AdminClientResp> {
    return this.request<AdminClientResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/clients/{clientId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["clientId", "clientId"]], copyRecord(req),
      "json",
      "AdminGetClientReq",
      options,
      "AdminClientResp",
    );
  }
  async adminUpdateClient(req: AdminUpdateClientReq,options?: RequestOptions): Promise<AdminClientResp> {
    return this.request<AdminClientResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/clients/{clientId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["clientId", "clientId"]], copyRecord(req),
      "json",
      "AdminUpdateClientReq",
      options,
      "AdminClientResp",
    );
  }
  async adminRotateClientSecret(req: AdminRotateClientSecretReq,options?: RequestOptions): Promise<AdminRotateClientSecretResp> {
    return this.request<AdminRotateClientSecretResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/clients/{clientId}/rotate-secret",
      "",
      "json",
      [["tenantId", "tenantId"], ["clientId", "clientId"]], copyRecord(req),
      "json",
      "AdminRotateClientSecretReq",
      options,
      "AdminRotateClientSecretResp",
    );
  }
  async adminListConnectors(req: AdminListConnectorsReq,options?: RequestOptions): Promise<AdminListConnectorsResp> {
    return this.request<AdminListConnectorsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/connectors",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListConnectorsReq",
      options,
      "AdminListConnectorsResp",
    );
  }
  async adminCreateConnector(req: AdminCreateConnectorReq,options?: RequestOptions): Promise<AdminConnectorResp> {
    return this.request<AdminConnectorResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/connectors",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateConnectorReq",
      options,
      "AdminConnectorResp",
    );
  }
  async adminDeleteConnector(req: AdminGetConnectorReq,options?: RequestOptions): Promise<AdminConnectorResp> {
    return this.request<AdminConnectorResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/connectors/{connectorId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["connectorId", "connectorId"]], copyRecord(req),
      "json",
      "AdminGetConnectorReq",
      options,
      "AdminConnectorResp",
    );
  }
  async adminGetConnector(req: AdminGetConnectorReq,options?: RequestOptions): Promise<AdminConnectorResp> {
    return this.request<AdminConnectorResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/connectors/{connectorId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["connectorId", "connectorId"]], copyRecord(req),
      "json",
      "AdminGetConnectorReq",
      options,
      "AdminConnectorResp",
    );
  }
  async adminUpdateConnector(req: AdminUpdateConnectorReq,options?: RequestOptions): Promise<AdminConnectorResp> {
    return this.request<AdminConnectorResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/connectors/{connectorId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["connectorId", "connectorId"]], copyRecord(req),
      "json",
      "AdminUpdateConnectorReq",
      options,
      "AdminConnectorResp",
    );
  }
  async adminUpsertDevicePosture(req: AdminUpsertDevicePostureReq,options?: RequestOptions): Promise<AdminDevicePostureResp> {
    return this.request<AdminDevicePostureResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/device-posture",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminUpsertDevicePostureReq",
      options,
      "AdminDevicePostureResp",
    );
  }
  async adminGetDevicePosture(req: AdminGetDevicePostureReq,options?: RequestOptions): Promise<AdminDevicePostureResp> {
    return this.request<AdminDevicePostureResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/device-posture/{deviceId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["deviceId", "deviceId"]], copyRecord(req),
      "json",
      "AdminGetDevicePostureReq",
      options,
      "AdminDevicePostureResp",
    );
  }
  async adminListEmergencyRoles(req: AdminListEmergencyRolesReq,options?: RequestOptions): Promise<AdminListEmergencyRolesResp> {
    return this.request<AdminListEmergencyRolesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/emergency-roles/",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListEmergencyRolesReq",
      options,
      "AdminListEmergencyRolesResp",
    );
  }
  async adminCreateEmergencyRole(req: AdminCreateEmergencyRoleReq,options?: RequestOptions): Promise<AdminEmergencyRoleResp> {
    return this.request<AdminEmergencyRoleResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/emergency-roles/",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateEmergencyRoleReq",
      options,
      "AdminEmergencyRoleResp",
    );
  }
  async adminDeleteEmergencyRole(req: AdminGetEmergencyRoleReq,options?: RequestOptions): Promise<AdminEmergencyRoleResp> {
    return this.request<AdminEmergencyRoleResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/emergency-roles/{roleId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminGetEmergencyRoleReq",
      options,
      "AdminEmergencyRoleResp",
    );
  }
  async adminGetEmergencyRole(req: AdminGetEmergencyRoleReq,options?: RequestOptions): Promise<AdminEmergencyRoleResp> {
    return this.request<AdminEmergencyRoleResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/emergency-roles/{roleId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminGetEmergencyRoleReq",
      options,
      "AdminEmergencyRoleResp",
    );
  }
  async adminUpdateEmergencyRole(req: AdminUpdateEmergencyRoleReq,options?: RequestOptions): Promise<AdminEmergencyRoleResp> {
    return this.request<AdminEmergencyRoleResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/emergency-roles/{roleId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["roleId", "roleId"]], copyRecord(req),
      "json",
      "AdminUpdateEmergencyRoleReq",
      options,
      "AdminEmergencyRoleResp",
    );
  }
  async adminListEntitlements(req: AdminListEntitlementsReq,options?: RequestOptions): Promise<AdminListEntitlementsResp> {
    return this.request<AdminListEntitlementsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/entitlements",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListEntitlementsReq",
      options,
      "AdminListEntitlementsResp",
    );
  }
  async adminCreateEntitlement(req: AdminCreateEntitlementReq,options?: RequestOptions): Promise<AdminEntitlementResp> {
    return this.request<AdminEntitlementResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/entitlements",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateEntitlementReq",
      options,
      "AdminEntitlementResp",
    );
  }
  async adminDeleteEntitlement(req: AdminGetEntitlementReq,options?: RequestOptions): Promise<AdminEntitlementResp> {
    return this.request<AdminEntitlementResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/entitlements/{entitlementId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["entitlementId", "entitlementId"]], copyRecord(req),
      "json",
      "AdminGetEntitlementReq",
      options,
      "AdminEntitlementResp",
    );
  }
  async adminGetEntitlement(req: AdminGetEntitlementReq,options?: RequestOptions): Promise<AdminEntitlementResp> {
    return this.request<AdminEntitlementResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/entitlements/{entitlementId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["entitlementId", "entitlementId"]], copyRecord(req),
      "json",
      "AdminGetEntitlementReq",
      options,
      "AdminEntitlementResp",
    );
  }
  async adminUpdateEntitlement(req: AdminUpdateEntitlementReq,options?: RequestOptions): Promise<AdminEntitlementResp> {
    return this.request<AdminEntitlementResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/entitlements/{entitlementId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["entitlementId", "entitlementId"]], copyRecord(req),
      "json",
      "AdminUpdateEntitlementReq",
      options,
      "AdminEntitlementResp",
    );
  }
  async adminIssueEphemeralCred(req: AdminIssueEphemeralCredReq,options?: RequestOptions): Promise<AdminEphemeralCredResp> {
    return this.request<AdminEphemeralCredResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/issue",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminIssueEphemeralCredReq",
      options,
      "AdminEphemeralCredResp",
    );
  }
  async adminListCredentialLeases(req: AdminListCredentialLeasesReq,options?: RequestOptions): Promise<AdminListCredentialLeasesResp> {
    return this.request<AdminListCredentialLeasesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/leases",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListCredentialLeasesReq",
      options,
      "AdminListCredentialLeasesResp",
    );
  }
  async adminGetCredentialLease(req: AdminGetCredentialLeaseReq,options?: RequestOptions): Promise<AdminCredentialLeaseResp> {
    return this.request<AdminCredentialLeaseResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/leases/{id}",
      "",
      "query",
      [["tenantId", "tenantId"], ["id", "id"]], copyRecord(req),
      "json",
      "AdminGetCredentialLeaseReq",
      options,
      "AdminCredentialLeaseResp",
    );
  }
  async adminRevokeCredentialLease(req: AdminGetCredentialLeaseReq,options?: RequestOptions): Promise<AdminRevokeCredentialLeaseResp> {
    return this.request<AdminRevokeCredentialLeaseResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/leases/{id}/revoke",
      "",
      "json",
      [["tenantId", "tenantId"], ["id", "id"]], copyRecord(req),
      "json",
      "AdminGetCredentialLeaseReq",
      options,
      "AdminRevokeCredentialLeaseResp",
    );
  }
  async adminListCredentialProviders(req: AdminListCredentialProvidersReq,options?: RequestOptions): Promise<AdminListCredentialProvidersResp> {
    return this.request<AdminListCredentialProvidersResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/providers",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListCredentialProvidersReq",
      options,
      "AdminListCredentialProvidersResp",
    );
  }
  async adminRotateCredentialKey(req: AdminRotateCredentialKeyReq,options?: RequestOptions): Promise<AdminRotateCredentialKeyResp> {
    return this.request<AdminRotateCredentialKeyResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/ephemeral-creds/rotate-key",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminRotateCredentialKeyReq",
      options,
      "AdminRotateCredentialKeyResp",
    );
  }
  async adminGovernanceListCampaigns(req: AdminListDiscoveryReq,options?: RequestOptions): Promise<AdminListCampaignsResp> {
    return this.request<AdminListCampaignsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/campaigns",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListDiscoveryReq",
      options,
      "AdminListCampaignsResp",
    );
  }
  async adminGovernanceCreateCampaign(req: AdminCreateCampaignReq,options?: RequestOptions): Promise<AdminCampaignResp> {
    return this.request<AdminCampaignResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/campaigns",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateCampaignReq",
      options,
      "AdminCampaignResp",
    );
  }
  async adminGovernanceGetCampaign(req: AdminGetCampaignReq,options?: RequestOptions): Promise<AdminCampaignResp> {
    return this.request<AdminCampaignResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["campaignId", "campaignId"]], copyRecord(req),
      "json",
      "AdminGetCampaignReq",
      options,
      "AdminCampaignResp",
    );
  }
  async adminGovernanceListAttestations(req: AdminListAttestationsReq,options?: RequestOptions): Promise<AdminListAttestationsResp> {
    return this.request<AdminListAttestationsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}/attestations",
      "",
      "query",
      [["tenantId", "tenantId"], ["campaignId", "campaignId"]], copyRecord(req),
      "json",
      "AdminListAttestationsReq",
      options,
      "AdminListAttestationsResp",
    );
  }
  async adminGovernanceSubmitAttestation(req: AdminSubmitAttestationReq,options?: RequestOptions): Promise<AdminAttestationResp> {
    return this.request<AdminAttestationResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}/attestations",
      "",
      "json",
      [["tenantId", "tenantId"], ["campaignId", "campaignId"]], copyRecord(req),
      "json",
      "AdminSubmitAttestationReq",
      options,
      "AdminAttestationResp",
    );
  }
  async adminGovernanceCloseCampaign(req: AdminCloseCampaignReq,options?: RequestOptions): Promise<AdminCampaignResp> {
    return this.request<AdminCampaignResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}/close",
      "",
      "json",
      [["tenantId", "tenantId"], ["campaignId", "campaignId"]], copyRecord(req),
      "json",
      "AdminCloseCampaignReq",
      options,
      "AdminCampaignResp",
    );
  }
  async adminGovernanceLaunchCampaign(req: AdminLaunchCampaignReq,options?: RequestOptions): Promise<AdminCampaignResp> {
    return this.request<AdminCampaignResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}/launch",
      "",
      "json",
      [["tenantId", "tenantId"], ["campaignId", "campaignId"]], copyRecord(req),
      "json",
      "AdminLaunchCampaignReq",
      options,
      "AdminCampaignResp",
    );
  }
  async adminGovernanceListDiscovery(req: AdminListDiscoveryReq,options?: RequestOptions): Promise<AdminListDiscoveryResp> {
    return this.request<AdminListDiscoveryResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/discovery",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListDiscoveryReq",
      options,
      "AdminListDiscoveryResp",
    );
  }
  async adminGovernanceRunDiscovery(req: AdminGovernanceRunDiscoveryReq,options?: RequestOptions): Promise<AdminGovernanceRunDiscoveryResp> {
    return this.request<AdminGovernanceRunDiscoveryResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/discovery/run",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGovernanceRunDiscoveryReq",
      options,
      "AdminGovernanceRunDiscoveryResp",
    );
  }
  async adminGovernanceDriftReport(req: AdminDriftReportReq,options?: RequestOptions): Promise<AdminDriftReportResp> {
    return this.request<AdminDriftReportResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/drift",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminDriftReportReq",
      options,
      "AdminDriftReportResp",
    );
  }
  async adminGovernanceListActions(req: AdminGovernanceListActionsReq,options?: RequestOptions): Promise<AdminGovernanceListActionsResp> {
    return this.request<AdminGovernanceListActionsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/dual-auth",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGovernanceListActionsReq",
      options,
      "AdminGovernanceListActionsResp",
    );
  }
  async adminGovernancePropose(req: AdminGovernanceProposeReq,options?: RequestOptions): Promise<AdminGovernanceActionResp> {
    return this.request<AdminGovernanceActionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/dual-auth/propose",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGovernanceProposeReq",
      options,
      "AdminGovernanceActionResp",
    );
  }
  async adminGovernanceApprove(req: AdminGovernanceApproveReq,options?: RequestOptions): Promise<AdminGovernanceActionResp> {
    return this.request<AdminGovernanceActionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/dual-auth/{actionId}/approve",
      "",
      "json",
      [["tenantId", "tenantId"], ["actionId", "actionId"]], copyRecord(req),
      "json",
      "AdminGovernanceApproveReq",
      options,
      "AdminGovernanceActionResp",
    );
  }
  async adminGovernanceReject(req: AdminGovernanceRejectReq,options?: RequestOptions): Promise<AdminGovernanceActionResp> {
    return this.request<AdminGovernanceActionResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/dual-auth/{actionId}/reject",
      "",
      "json",
      [["tenantId", "tenantId"], ["actionId", "actionId"]], copyRecord(req),
      "json",
      "AdminGovernanceRejectReq",
      options,
      "AdminGovernanceActionResp",
    );
  }
  async adminListRoleDefs(req: AdminListRoleDefsReq,options?: RequestOptions): Promise<AdminListRoleDefsResp> {
    return this.request<AdminListRoleDefsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/role-defs",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListRoleDefsReq",
      options,
      "AdminListRoleDefsResp",
    );
  }
  async adminCreateRoleDef(req: AdminCreateRoleDefReq,options?: RequestOptions): Promise<AdminRoleDefResp> {
    return this.request<AdminRoleDefResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/governance/role-defs",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateRoleDefReq",
      options,
      "AdminRoleDefResp",
    );
  }
  async adminDeleteRoleDef(req: AdminGetRoleDefReq,options?: RequestOptions): Promise<AdminRoleDefResp> {
    return this.request<AdminRoleDefResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/governance/role-defs/{name}",
      "",
      "query",
      [["tenantId", "tenantId"], ["name", "name"]], copyRecord(req),
      "json",
      "AdminGetRoleDefReq",
      options,
      "AdminRoleDefResp",
    );
  }
  async adminGetRoleDef(req: AdminGetRoleDefReq,options?: RequestOptions): Promise<AdminRoleDefResp> {
    return this.request<AdminRoleDefResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/role-defs/{name}",
      "",
      "query",
      [["tenantId", "tenantId"], ["name", "name"]], copyRecord(req),
      "json",
      "AdminGetRoleDefReq",
      options,
      "AdminRoleDefResp",
    );
  }
  async adminUpdateRoleDef(req: AdminUpdateRoleDefReq,options?: RequestOptions): Promise<AdminRoleDefResp> {
    return this.request<AdminRoleDefResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/governance/role-defs/{name}",
      "",
      "json",
      [["tenantId", "tenantId"], ["name", "name"]], copyRecord(req),
      "json",
      "AdminUpdateRoleDefReq",
      options,
      "AdminRoleDefResp",
    );
  }
  async adminGovernanceListRoles(req: AdminGovernanceListRolesReq,options?: RequestOptions): Promise<AdminGovernanceListRolesResp> {
    return this.request<AdminGovernanceListRolesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/governance/roles",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGovernanceListRolesReq",
      options,
      "AdminGovernanceListRolesResp",
    );
  }
  async adminGovernanceAssignRole(req: AdminGovernanceAssignRoleReq,options?: RequestOptions): Promise<AdminGovernanceActionResp> {
    return this.request<AdminGovernanceActionResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/governance/roles/{subjectId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminGovernanceAssignRoleReq",
      options,
      "AdminGovernanceActionResp",
    );
  }
  async adminDeleteMailProvider(req: AdminGetMailProviderReq,options?: RequestOptions): Promise<AdminMailProviderResp> {
    return this.request<AdminMailProviderResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/mail-provider",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetMailProviderReq",
      options,
      "AdminMailProviderResp",
    );
  }
  async adminGetMailProvider(req: AdminGetMailProviderReq,options?: RequestOptions): Promise<AdminMailProviderResp> {
    return this.request<AdminMailProviderResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/mail-provider",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetMailProviderReq",
      options,
      "AdminMailProviderResp",
    );
  }
  async adminPutMailProvider(req: AdminPutMailProviderReq,options?: RequestOptions): Promise<AdminMailProviderResp> {
    return this.request<AdminMailProviderResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/mail-provider",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminPutMailProviderReq",
      options,
      "AdminMailProviderResp",
    );
  }
  async adminGetMFAPolicy(req: AdminGetMFAPolicyReq,options?: RequestOptions): Promise<AdminMFAPolicyResp> {
    return this.request<AdminMFAPolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/mfa-policy",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetMFAPolicyReq",
      options,
      "AdminMFAPolicyResp",
    );
  }
  async adminSetMFAPolicy(req: AdminSetMFAPolicyReq,options?: RequestOptions): Promise<AdminMFAPolicyResp> {
    return this.request<AdminMFAPolicyResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/mfa-policy",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminSetMFAPolicyReq",
      options,
      "AdminMFAPolicyResp",
    );
  }
  async adminListTenantClientCAs(req: AdminListTenantClientCAsReq,options?: RequestOptions): Promise<AdminClientCAListResp> {
    return this.request<AdminClientCAListResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/mtls/cas",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListTenantClientCAsReq",
      options,
      "AdminClientCAListResp",
    );
  }
  async adminPAMResilienceHealth(req: AdminPAMResilienceHealthReq,options?: RequestOptions): Promise<AdminPAMResilienceHealthResp> {
    return this.request<AdminPAMResilienceHealthResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/pam/resilience/health",
      "",
      "query",
      [["tenantId", "tenant_id"]], copyRecord(req),
      "json",
      "AdminPAMResilienceHealthReq",
      options,
      "AdminPAMResilienceHealthResp",
    );
  }
  async adminPAMMaintenanceToggle(req: AdminPAMMaintenanceToggleReq,options?: RequestOptions): Promise<AdminPAMMaintenanceToggleResp> {
    return this.request<AdminPAMMaintenanceToggleResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/pam/resilience/maintenance",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminPAMMaintenanceToggleReq",
      options,
      "AdminPAMMaintenanceToggleResp",
    );
  }
  async adminPAMSLODashboard(req: AdminPAMSLODashboardReq,options?: RequestOptions): Promise<AdminPAMSLODashboardResp> {
    return this.request<AdminPAMSLODashboardResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/pam/resilience/slo",
      "",
      "query",
      [["tenantId", "tenant_id"]], copyRecord(req),
      "json",
      "AdminPAMSLODashboardReq",
      options,
      "AdminPAMSLODashboardResp",
    );
  }
  async adminPAMResilienceStatus(req: AdminPAMResilienceStatusReq,options?: RequestOptions): Promise<AdminPAMResilienceStatusResp> {
    return this.request<AdminPAMResilienceStatusResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/pam/resilience/status",
      "",
      "query",
      [["tenantId", "tenant_id"]], copyRecord(req),
      "json",
      "AdminPAMResilienceStatusReq",
      options,
      "AdminPAMResilienceStatusResp",
    );
  }
  async adminListPolicies(req: AdminListPoliciesReq,options?: RequestOptions): Promise<AdminListPoliciesResp> {
    return this.request<AdminListPoliciesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/policies",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListPoliciesReq",
      options,
      "AdminListPoliciesResp",
    );
  }
  async adminCreatePolicy(req: AdminCreatePolicyReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/policies",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreatePolicyReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminSimulatePolicy(req: AdminSimulatePolicyReq,options?: RequestOptions): Promise<AdminSimulatePolicyResp> {
    return this.request<AdminSimulatePolicyResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/policies/simulate",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminSimulatePolicyReq",
      options,
      "AdminSimulatePolicyResp",
    );
  }
  async adminDeletePolicy(req: AdminGetPolicyReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["policyId", "policyId"]], copyRecord(req),
      "json",
      "AdminGetPolicyReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminGetPolicy(req: AdminGetPolicyReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["policyId", "policyId"]], copyRecord(req),
      "json",
      "AdminGetPolicyReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminUpdatePolicy(req: AdminCreatePolicyReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["policyId", "policyId"]], copyRecord(req),
      "json",
      "AdminCreatePolicyReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminRollbackPolicy(req: AdminRollbackPolicyReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}/rollback",
      "",
      "json",
      [["tenantId", "tenantId"], ["policyId", "policyId"]], copyRecord(req),
      "json",
      "AdminRollbackPolicyReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminListPolicyVersions(req: AdminPolicyVersionsReq,options?: RequestOptions): Promise<AdminPolicyVersionsResp> {
    return this.request<AdminPolicyVersionsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}/versions",
      "",
      "query",
      [["tenantId", "tenantId"], ["policyId", "policyId"]], copyRecord(req),
      "json",
      "AdminPolicyVersionsReq",
      options,
      "AdminPolicyVersionsResp",
    );
  }
  async adminGetPolicyVersion(req: AdminPolicyVersionReq,options?: RequestOptions): Promise<AdminPolicyResp> {
    return this.request<AdminPolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/policies/{policyId}/versions/{version}",
      "",
      "query",
      [["tenantId", "tenantId"], ["policyId", "policyId"], ["version", "version"]], copyRecord(req),
      "json",
      "AdminPolicyVersionReq",
      options,
      "AdminPolicyResp",
    );
  }
  async adminGetProfilePolicy(req: AdminGetProfilePolicyReq,options?: RequestOptions): Promise<AdminProfilePolicyResp> {
    return this.request<AdminProfilePolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/profile-policy",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetProfilePolicyReq",
      options,
      "AdminProfilePolicyResp",
    );
  }
  async adminPutProfilePolicy(req: AdminPutProfilePolicyReq,options?: RequestOptions): Promise<AdminProfilePolicyResp> {
    return this.request<AdminProfilePolicyResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/profile-policy",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminPutProfilePolicyReq",
      options,
      "AdminProfilePolicyResp",
    );
  }
  async adminListResources(req: AdminListResourcesReq,options?: RequestOptions): Promise<AdminListResourcesResp> {
    return this.request<AdminListResourcesResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/resources",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListResourcesReq",
      options,
      "AdminListResourcesResp",
    );
  }
  async adminCreateResource(req: AdminCreateResourceReq,options?: RequestOptions): Promise<AdminResourceResp> {
    return this.request<AdminResourceResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/resources",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateResourceReq",
      options,
      "AdminResourceResp",
    );
  }
  async adminDeleteResource(req: AdminGetResourceReq,options?: RequestOptions): Promise<AdminResourceResp> {
    return this.request<AdminResourceResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/resources/{resourceId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminGetResourceReq",
      options,
      "AdminResourceResp",
    );
  }
  async adminGetResource(req: AdminGetResourceReq,options?: RequestOptions): Promise<AdminResourceResp> {
    return this.request<AdminResourceResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/resources/{resourceId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminGetResourceReq",
      options,
      "AdminResourceResp",
    );
  }
  async adminUpdateResource(req: AdminUpdateResourceReq,options?: RequestOptions): Promise<AdminResourceResp> {
    return this.request<AdminResourceResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/resources/{resourceId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminUpdateResourceReq",
      options,
      "AdminResourceResp",
    );
  }
  async adminDeleteSecretsProvider(req: AdminGetSecretsProviderReq,options?: RequestOptions): Promise<AdminSecretsProviderResp> {
    return this.request<AdminSecretsProviderResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/secrets-provider",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetSecretsProviderReq",
      options,
      "AdminSecretsProviderResp",
    );
  }
  async adminGetSecretsProvider(req: AdminGetSecretsProviderReq,options?: RequestOptions): Promise<AdminSecretsProviderResp> {
    return this.request<AdminSecretsProviderResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/secrets-provider",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminGetSecretsProviderReq",
      options,
      "AdminSecretsProviderResp",
    );
  }
  async adminPutSecretsProvider(req: AdminPutSecretsProviderReq,options?: RequestOptions): Promise<AdminSecretsProviderResp> {
    return this.request<AdminSecretsProviderResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/secrets-provider",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminPutSecretsProviderReq",
      options,
      "AdminSecretsProviderResp",
    );
  }
  async adminDeleteSessionControlPolicy(req: AdminGetSessionControlPolicyReq,options?: RequestOptions): Promise<AdminSessionControlPolicyResp> {
    return this.request<AdminSessionControlPolicyResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/session-control-policies/{resourceId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminGetSessionControlPolicyReq",
      options,
      "AdminSessionControlPolicyResp",
    );
  }
  async adminGetSessionControlPolicy(req: AdminGetSessionControlPolicyReq,options?: RequestOptions): Promise<AdminSessionControlPolicyResp> {
    return this.request<AdminSessionControlPolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/session-control-policies/{resourceId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminGetSessionControlPolicyReq",
      options,
      "AdminSessionControlPolicyResp",
    );
  }
  async adminSetSessionControlPolicy(req: AdminSetSessionControlPolicyReq,options?: RequestOptions): Promise<AdminSessionControlPolicyResp> {
    return this.request<AdminSessionControlPolicyResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/session-control-policies/{resourceId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["resourceId", "resourceId"]], copyRecord(req),
      "json",
      "AdminSetSessionControlPolicyReq",
      options,
      "AdminSessionControlPolicyResp",
    );
  }
  async adminGetStepUpPolicy(req: AdminGetStepUpPolicyReq,options?: RequestOptions): Promise<AdminGetStepUpPolicyResp> {
    return this.request<AdminGetStepUpPolicyResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/stepup/policy",
      "",
      "query",
      [["tenantId", "tenant_id"]], copyRecord(req),
      "json",
      "AdminGetStepUpPolicyReq",
      options,
      "AdminGetStepUpPolicyResp",
    );
  }
  async adminListSubjects(req: AdminListSubjectsReq,options?: RequestOptions): Promise<AdminListSubjectsResp> {
    return this.request<AdminListSubjectsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/subjects",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListSubjectsReq",
      options,
      "AdminListSubjectsResp",
    );
  }
  async adminCreateSubject(req: AdminCreateSubjectReq,options?: RequestOptions): Promise<AdminSubjectResp> {
    return this.request<AdminSubjectResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/subjects",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateSubjectReq",
      options,
      "AdminSubjectResp",
    );
  }
  async adminDeleteSubject(req: AdminGetSubjectReq,options?: RequestOptions): Promise<AdminSubjectResp> {
    return this.request<AdminSubjectResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/subjects/{subjectId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminGetSubjectReq",
      options,
      "AdminSubjectResp",
    );
  }
  async adminGetSubject(req: AdminGetSubjectReq,options?: RequestOptions): Promise<AdminSubjectResp> {
    return this.request<AdminSubjectResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/subjects/{subjectId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminGetSubjectReq",
      options,
      "AdminSubjectResp",
    );
  }
  async adminUpdateSubject(req: AdminUpdateSubjectReq,options?: RequestOptions): Promise<AdminSubjectResp> {
    return this.request<AdminSubjectResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/subjects/{subjectId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminUpdateSubjectReq",
      options,
      "AdminSubjectResp",
    );
  }
  async adminGetSubjectSecurityHistory(req: AdminGetSubjectSecurityHistoryReq,options?: RequestOptions): Promise<AdminGetUserSecurityHistoryResp> {
    return this.request<AdminGetUserSecurityHistoryResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/subjects/{subjectId}/security-history",
      "",
      "query",
      [["tenantId", "tenantId"], ["subjectId", "subjectId"]], copyRecord(req),
      "json",
      "AdminGetSubjectSecurityHistoryReq",
      options,
      "AdminGetUserSecurityHistoryResp",
    );
  }
  async adminListUsers(req: AdminListUsersReq,options?: RequestOptions): Promise<AdminListUsersResp> {
    return this.request<AdminListUsersResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminListUsersReq",
      options,
      "AdminListUsersResp",
    );
  }
  async adminCreateUser(req: AdminCreateUserReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/users",
      "",
      "json",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "AdminCreateUserReq",
      options,
      "AdminUserResp",
    );
  }
  async adminDeleteUser(req: AdminGetUserReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/users/{userId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminGetUserReq",
      options,
      "AdminUserResp",
    );
  }
  async adminGetUser(req: AdminGetUserReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users/{userId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminGetUserReq",
      options,
      "AdminUserResp",
    );
  }
  async adminUpdateUser(req: AdminUpdateUserReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "PATCH",
      "/v1/admin/tenants/{tenantId}/users/{userId}",
      "",
      "json",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminUpdateUserReq",
      options,
      "AdminUserResp",
    );
  }
  async adminListUserAppPasswords(req: AdminListUserAppPasswordsReq,options?: RequestOptions): Promise<AdminAppPasswordsResp> {
    return this.request<AdminAppPasswordsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users/{userId}/app-passwords",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminListUserAppPasswordsReq",
      options,
      "AdminAppPasswordsResp",
    );
  }
  async adminRevokeUserAppPassword(req: AdminRevokeUserAppPasswordReq,options?: RequestOptions): Promise<AdminRevokeUserAppPasswordResp> {
    return this.request<AdminRevokeUserAppPasswordResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/users/{userId}/app-passwords/{appPasswordId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"], ["appPasswordId", "appPasswordId"]], copyRecord(req),
      "json",
      "AdminRevokeUserAppPasswordReq",
      options,
      "AdminRevokeUserAppPasswordResp",
    );
  }
  async adminListUserFactors(req: AdminListUserFactorsReq,options?: RequestOptions): Promise<AdminListUserFactorsResp> {
    return this.request<AdminListUserFactorsResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users/{userId}/factors",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminListUserFactorsReq",
      options,
      "AdminListUserFactorsResp",
    );
  }
  async adminRevokeUserFactor(req: AdminRevokeUserFactorReq,options?: RequestOptions): Promise<AdminRevokeUserFactorResp> {
    return this.request<AdminRevokeUserFactorResp>(
      "DELETE",
      "/v1/admin/tenants/{tenantId}/users/{userId}/factors/{factorKind}/{factorId}",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"], ["factorKind", "factorKind"], ["factorId", "factorId"]], copyRecord(req),
      "json",
      "AdminRevokeUserFactorReq",
      options,
      "AdminRevokeUserFactorResp",
    );
  }
  async adminUserPasswordReset(req: AdminUserPasswordResetReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/users/{userId}/password",
      "",
      "json",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminUserPasswordResetReq",
      options,
      "AdminUserResp",
    );
  }
  async adminGetUserProfile(req: AdminGetUserProfileReq,options?: RequestOptions): Promise<AdminUserProfileResp> {
    return this.request<AdminUserProfileResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users/{userId}/profile",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminGetUserProfileReq",
      options,
      "AdminUserProfileResp",
    );
  }
  async adminSetUserProfile(req: AdminSetUserProfileReq,options?: RequestOptions): Promise<AdminUserProfileResp> {
    return this.request<AdminUserProfileResp>(
      "PUT",
      "/v1/admin/tenants/{tenantId}/users/{userId}/profile",
      "",
      "json",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminSetUserProfileReq",
      options,
      "AdminUserProfileResp",
    );
  }
  async adminUserRevokeSessions(req: AdminUserRevokeSessionsReq,options?: RequestOptions): Promise<AdminUserResp> {
    return this.request<AdminUserResp>(
      "POST",
      "/v1/admin/tenants/{tenantId}/users/{userId}/revoke-sessions",
      "",
      "json",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminUserRevokeSessionsReq",
      options,
      "AdminUserResp",
    );
  }
  async adminGetUserSecurityHistory(req: AdminGetUserSecurityHistoryReq,options?: RequestOptions): Promise<AdminGetUserSecurityHistoryResp> {
    return this.request<AdminGetUserSecurityHistoryResp>(
      "GET",
      "/v1/admin/tenants/{tenantId}/users/{userId}/security-history",
      "",
      "query",
      [["tenantId", "tenantId"], ["userId", "userId"]], copyRecord(req),
      "json",
      "AdminGetUserSecurityHistoryReq",
      options,
      "AdminGetUserSecurityHistoryResp",
    );
  }
  async adminWhoAmI(req: AdminMTLSEmptyReq,options?: RequestOptions): Promise<AdminWhoAmIResp> {
    return this.request<AdminWhoAmIResp>(
      "GET",
      "/v1/admin/whoami",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "AdminMTLSEmptyReq",
      options,
      "AdminWhoAmIResp",
    );
  }
  async initCreateAdmin(req: InitCreateAdminReq,options?: RequestOptions): Promise<InitCreateAdminResp> {
    return this.request<InitCreateAdminResp>(
      "POST",
      "/v1/init/admin",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "InitCreateAdminReq",
      options,
      "InitCreateAdminResp",
    );
  }
  async initBegin(req: InitBeginReq,options?: RequestOptions): Promise<InitBeginResp> {
    return this.request<InitBeginResp>(
      "POST",
      "/v1/init/begin",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "InitBeginReq",
      options,
      "InitBeginResp",
    );
  }
  async initComplete(req: InitCompleteReq,options?: RequestOptions): Promise<InitCompleteResp> {
    return this.request<InitCompleteResp>(
      "POST",
      "/v1/init/complete",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "InitCompleteReq",
      options,
      "InitCompleteResp",
    );
  }
  async initStatus(req: InitStatusReq,options?: RequestOptions): Promise<InitStatusResp> {
    return this.request<InitStatusResp>(
      "GET",
      "/v1/init/status",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "InitStatusReq",
      options,
      "InitStatusResp",
    );
  }
  async discover(req: DiscoverReq,options?: RequestOptions): Promise<DiscoverResp> {
    return this.request<DiscoverResp>(
      "GET",
      "/v1/oidc/discover",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "DiscoverReq",
      options,
      "DiscoverResp",
    );
  }
  async secondaryEmailVerifyConfirm(req: SecondaryEmailVerifyConfirmReq,options?: RequestOptions): Promise<SecondaryEmailVerifyConfirmResp> {
    return this.request<SecondaryEmailVerifyConfirmResp>(
      "POST",
      "/v1/oidc/email/verify-secondary/confirm",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "SecondaryEmailVerifyConfirmReq",
      options,
      "SecondaryEmailVerifyConfirmResp",
    );
  }
  async postEmailVerifyConfirm(req: EmailVerifyConfirmReq,options?: RequestOptions): Promise<EmailVerifyConfirmResp> {
    return this.request<EmailVerifyConfirmResp>(
      "POST",
      "/v1/oidc/email/verify/confirm",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "EmailVerifyConfirmReq",
      options,
      "EmailVerifyConfirmResp",
    );
  }
  async postEmailVerifyRequest(req: EmailVerifyRequestReq,options?: RequestOptions): Promise<EmailVerifyRequestResp> {
    return this.request<EmailVerifyRequestResp>(
      "POST",
      "/v1/oidc/email/verify/request",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "EmailVerifyRequestReq",
      options,
      "EmailVerifyRequestResp",
    );
  }
  async health(req: HealthReq,options?: RequestOptions): Promise<HealthResp> {
    return this.request<HealthResp>(
      "GET",
      "/v1/oidc/health",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "HealthReq",
      options,
      "HealthResp",
    );
  }
  async jwks(req: JWKSReq,options?: RequestOptions): Promise<JWKSResp> {
    return this.request<JWKSResp>(
      "GET",
      "/v1/oidc/jwks",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "JWKSReq",
      options,
      "JWKSResp",
    );
  }
  async postMagicLinkRequest(req: MagicLinkRequestReq,options?: RequestOptions): Promise<MagicLinkRequestResp> {
    return this.request<MagicLinkRequestResp>(
      "POST",
      "/v1/oidc/magic-link/request",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MagicLinkRequestReq",
      options,
      "MagicLinkRequestResp",
    );
  }
  async postMagicLinkVerify(req: MagicLinkVerifyReq,options?: RequestOptions): Promise<MagicLinkVerifyResp> {
    return this.request<MagicLinkVerifyResp>(
      "POST",
      "/v1/oidc/magic-link/verify",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MagicLinkVerifyReq",
      options,
      "MagicLinkVerifyResp",
    );
  }
  async meListAppPasswords(req: MeListAppPasswordsReq,options?: RequestOptions): Promise<MeAppPasswordsResp> {
    return this.request<MeAppPasswordsResp>(
      "GET",
      "/v1/oidc/me/app-passwords",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListAppPasswordsReq",
      options,
      "MeAppPasswordsResp",
    );
  }
  async meCreateAppPassword(req: MeCreateAppPasswordReq,options?: RequestOptions): Promise<MeCreateAppPasswordResp> {
    return this.request<MeCreateAppPasswordResp>(
      "POST",
      "/v1/oidc/me/app-passwords",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeCreateAppPasswordReq",
      options,
      "MeCreateAppPasswordResp",
    );
  }
  async meRevokeAppPassword(req: MeRevokeAppPasswordReq,options?: RequestOptions): Promise<MeRevokeAppPasswordResp> {
    return this.request<MeRevokeAppPasswordResp>(
      "DELETE",
      "/v1/oidc/me/app-passwords/{appPasswordId}",
      "",
      "query",
      [["appPasswordId", "appPasswordId"]], copyRecord(req),
      "json",
      "MeRevokeAppPasswordReq",
      options,
      "MeRevokeAppPasswordResp",
    );
  }
  async meListEmails(req: MeListEmailsReq,options?: RequestOptions): Promise<MeEmailsResp> {
    return this.request<MeEmailsResp>(
      "GET",
      "/v1/oidc/me/emails",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListEmailsReq",
      options,
      "MeEmailsResp",
    );
  }
  async meAddEmail(req: MeAddEmailReq,options?: RequestOptions): Promise<MeAddEmailResp> {
    return this.request<MeAddEmailResp>(
      "POST",
      "/v1/oidc/me/emails",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeAddEmailReq",
      options,
      "MeAddEmailResp",
    );
  }
  async meDeleteEmail(req: MeDeleteEmailReq,options?: RequestOptions): Promise<MeDeleteEmailResp> {
    return this.request<MeDeleteEmailResp>(
      "DELETE",
      "/v1/oidc/me/emails/{emailId}",
      "",
      "query",
      [["emailId", "emailId"]], copyRecord(req),
      "json",
      "MeDeleteEmailReq",
      options,
      "MeDeleteEmailResp",
    );
  }
  async meSetPrimaryEmail(req: MeSetPrimaryEmailReq,options?: RequestOptions): Promise<MeEmailsResp> {
    return this.request<MeEmailsResp>(
      "POST",
      "/v1/oidc/me/emails/{emailId}/primary",
      "",
      "json",
      [["emailId", "emailId"]], copyRecord(req),
      "json",
      "MeSetPrimaryEmailReq",
      options,
      "MeEmailsResp",
    );
  }
  async meGetProfile(req: MeGetProfileReq,options?: RequestOptions): Promise<MeProfileResp> {
    return this.request<MeProfileResp>(
      "GET",
      "/v1/oidc/me/profile",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeGetProfileReq",
      options,
      "MeProfileResp",
    );
  }
  async meUpdateProfile(req: MeUpdateProfileReq,options?: RequestOptions): Promise<MeProfileResp> {
    return this.request<MeProfileResp>(
      "PATCH",
      "/v1/oidc/me/profile",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeUpdateProfileReq",
      options,
      "MeProfileResp",
    );
  }
  async tOTPDisable(req: TOTPDisableReq,options?: RequestOptions): Promise<TOTPDisableResp> {
    return this.request<TOTPDisableResp>(
      "POST",
      "/v1/oidc/mfa/totp/disable",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "TOTPDisableReq",
      options,
      "TOTPDisableResp",
    );
  }
  async tOTPEnrollBegin(req: TOTPEnrollBeginReq,options?: RequestOptions): Promise<TOTPEnrollBeginResp> {
    return this.request<TOTPEnrollBeginResp>(
      "POST",
      "/v1/oidc/mfa/totp/enroll/begin",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "TOTPEnrollBeginReq",
      options,
      "TOTPEnrollBeginResp",
    );
  }
  async tOTPEnrollVerify(req: TOTPEnrollVerifyReq,options?: RequestOptions): Promise<TOTPEnrollVerifyResp> {
    return this.request<TOTPEnrollVerifyResp>(
      "POST",
      "/v1/oidc/mfa/totp/enroll/verify",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "TOTPEnrollVerifyReq",
      options,
      "TOTPEnrollVerifyResp",
    );
  }
  async postPasswordForgot(req: PasswordForgotReq,options?: RequestOptions): Promise<PasswordForgotResp> {
    return this.request<PasswordForgotResp>(
      "POST",
      "/v1/oidc/password/forgot",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "PasswordForgotReq",
      options,
      "PasswordForgotResp",
    );
  }
  async postPasswordReset(req: PasswordResetReq,options?: RequestOptions): Promise<PasswordResetResp> {
    return this.request<PasswordResetResp>(
      "POST",
      "/v1/oidc/password/reset",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "PasswordResetReq",
      options,
      "PasswordResetResp",
    );
  }
  async recoveryKeyGenerate(req: RecoveryKeyGenerateReq,options?: RequestOptions): Promise<RecoveryKeyGenerateResp> {
    return this.request<RecoveryKeyGenerateResp>(
      "POST",
      "/v1/oidc/recovery-key/generate",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "RecoveryKeyGenerateReq",
      options,
      "RecoveryKeyGenerateResp",
    );
  }
  async recoveryKeyRedeem(req: RecoveryKeyRedeemReq,options?: RequestOptions): Promise<RecoveryKeyRedeemResp> {
    return this.request<RecoveryKeyRedeemResp>(
      "POST",
      "/v1/oidc/recovery-key/redeem",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "RecoveryKeyRedeemReq",
      options,
      "RecoveryKeyRedeemResp",
    );
  }
  async postOIDCRegister(req: RegisterReq,options?: RequestOptions): Promise<RegisterResp> {
    return this.request<RegisterResp>(
      "POST",
      "/v1/oidc/register",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "RegisterReq",
      options,
      "RegisterResp",
    );
  }
  async postSecurityQuestionsSet(req: SecurityQuestionsSetReq,options?: RequestOptions): Promise<SecurityQuestionsSetResp> {
    return this.request<SecurityQuestionsSetResp>(
      "POST",
      "/v1/oidc/security-questions/set",
      "jwt",
      "json",
      [], copyRecord(req),
      "json",
      "SecurityQuestionsSetReq",
      options,
      "SecurityQuestionsSetResp",
    );
  }
  async postSecurityQuestionsVerify(req: SecurityQuestionsVerifyReq,options?: RequestOptions): Promise<SecurityQuestionsVerifyResp> {
    return this.request<SecurityQuestionsVerifyResp>(
      "POST",
      "/v1/oidc/security-questions/verify",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "SecurityQuestionsVerifyReq",
      options,
      "SecurityQuestionsVerifyResp",
    );
  }
  async postAuthenticateBegin(req: WebAuthnAuthenticateBeginReq,options?: RequestOptions): Promise<WebAuthnAuthenticateBeginResp> {
    return this.request<WebAuthnAuthenticateBeginResp>(
      "POST",
      "/v1/oidc/webauthn/authenticate/begin",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "WebAuthnAuthenticateBeginReq",
      options,
      "WebAuthnAuthenticateBeginResp",
    );
  }
  async postAuthenticateComplete(req: WebAuthnAuthenticateCompleteReq,options?: RequestOptions): Promise<WebAuthnAuthenticateCompleteResp> {
    return this.request<WebAuthnAuthenticateCompleteResp>(
      "POST",
      "/v1/oidc/webauthn/authenticate/complete",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "WebAuthnAuthenticateCompleteReq",
      options,
      "WebAuthnAuthenticateCompleteResp",
    );
  }
  async webAuthnListCredentials(req: WebAuthnListCredentialsReq,options?: RequestOptions): Promise<WebAuthnListCredentialsResp> {
    return this.request<WebAuthnListCredentialsResp>(
      "GET",
      "/v1/oidc/webauthn/credentials",
      "jwt",
      "query",
      [], copyRecord(req),
      "json",
      "WebAuthnListCredentialsReq",
      options,
      "WebAuthnListCredentialsResp",
    );
  }
  async webAuthnDeleteCredential(req: WebAuthnDeleteCredentialReq,options?: RequestOptions): Promise<WebAuthnDeleteCredentialResp> {
    return this.request<WebAuthnDeleteCredentialResp>(
      "DELETE",
      "/v1/oidc/webauthn/credentials/{credentialId}",
      "jwt",
      "query",
      [["credentialId", "credentialId"]], copyRecord(req),
      "json",
      "WebAuthnDeleteCredentialReq",
      options,
      "WebAuthnDeleteCredentialResp",
    );
  }
  async webAuthnSetCredentialAlias(req: WebAuthnSetCredentialAliasReq,options?: RequestOptions): Promise<WebAuthnSetCredentialAliasResp> {
    return this.request<WebAuthnSetCredentialAliasResp>(
      "PATCH",
      "/v1/oidc/webauthn/credentials/{credentialId}",
      "jwt",
      "json",
      [["credentialId", "credentialId"]], copyRecord(req),
      "json",
      "WebAuthnSetCredentialAliasReq",
      options,
      "WebAuthnSetCredentialAliasResp",
    );
  }
  async webAuthnEnrollmentStatus(req: WebAuthnEnrollmentStatusReq,options?: RequestOptions): Promise<WebAuthnEnrollmentStatusResp> {
    return this.request<WebAuthnEnrollmentStatusResp>(
      "GET",
      "/v1/oidc/webauthn/enrollment-status",
      "jwt",
      "query",
      [], copyRecord(req),
      "json",
      "WebAuthnEnrollmentStatusReq",
      options,
      "WebAuthnEnrollmentStatusResp",
    );
  }
  async postRegisterBegin(req: WebAuthnRegisterBeginReq,options?: RequestOptions): Promise<WebAuthnRegisterBeginResp> {
    return this.request<WebAuthnRegisterBeginResp>(
      "POST",
      "/v1/oidc/webauthn/register/begin",
      "jwt",
      "json",
      [], copyRecord(req),
      "json",
      "WebAuthnRegisterBeginReq",
      options,
      "WebAuthnRegisterBeginResp",
    );
  }
  async postRegisterComplete(req: WebAuthnRegisterCompleteReq,options?: RequestOptions): Promise<WebAuthnRegisterCompleteResp> {
    return this.request<WebAuthnRegisterCompleteResp>(
      "POST",
      "/v1/oidc/webauthn/register/complete",
      "jwt",
      "json",
      [], copyRecord(req),
      "json",
      "WebAuthnRegisterCompleteReq",
      options,
      "WebAuthnRegisterCompleteResp",
    );
  }
  async meListAccessRequests(req: MeListAccessRequestsReq,options?: RequestOptions): Promise<MeListAccessRequestsResp> {
    return this.request<MeListAccessRequestsResp>(
      "GET",
      "/v1/pam/me/access-requests",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListAccessRequestsReq",
      options,
      "MeListAccessRequestsResp",
    );
  }
  async meCreateAccessRequest(req: MeCreateAccessRequestReq,options?: RequestOptions): Promise<MeAccessRequestResp> {
    return this.request<MeAccessRequestResp>(
      "POST",
      "/v1/pam/me/access-requests",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeCreateAccessRequestReq",
      options,
      "MeAccessRequestResp",
    );
  }
  async meGetAccessRequest(req: MeGetAccessRequestReq,options?: RequestOptions): Promise<MeAccessRequestResp> {
    return this.request<MeAccessRequestResp>(
      "GET",
      "/v1/pam/me/access-requests/{requestId}",
      "",
      "query",
      [["requestId", "requestId"]], copyRecord(req),
      "json",
      "MeGetAccessRequestReq",
      options,
      "MeAccessRequestResp",
    );
  }
  async meActivateAccessRequest(req: MeActivateAccessRequestReq,options?: RequestOptions): Promise<PamAccessRequestActivationResp> {
    return this.request<PamAccessRequestActivationResp>(
      "POST",
      "/v1/pam/me/access-requests/{requestId}/activate",
      "",
      "json",
      [["requestId", "requestId"]], copyRecord(req),
      "json",
      "MeActivateAccessRequestReq",
      options,
      "PamAccessRequestActivationResp",
    );
  }
  async meCancelAccessRequest(req: MeGetAccessRequestReq,options?: RequestOptions): Promise<MeAccessRequestResp> {
    return this.request<MeAccessRequestResp>(
      "POST",
      "/v1/pam/me/access-requests/{requestId}/cancel",
      "",
      "json",
      [["requestId", "requestId"]], copyRecord(req),
      "json",
      "MeGetAccessRequestReq",
      options,
      "MeAccessRequestResp",
    );
  }
  async meListApprovals(req: MeListApprovalsReq,options?: RequestOptions): Promise<MeListApprovalsResp> {
    return this.request<MeListApprovalsResp>(
      "GET",
      "/v1/pam/me/approvals",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListApprovalsReq",
      options,
      "MeListApprovalsResp",
    );
  }
  async meApproveRequest(req: MeApproveRequestReq,options?: RequestOptions): Promise<MeAccessRequestResp> {
    return this.request<MeAccessRequestResp>(
      "POST",
      "/v1/pam/me/approvals/{requestId}/approve",
      "",
      "json",
      [["requestId", "requestId"]], copyRecord(req),
      "json",
      "MeApproveRequestReq",
      options,
      "MeAccessRequestResp",
    );
  }
  async meDenyRequest(req: MeDenyRequestReq,options?: RequestOptions): Promise<MeAccessRequestResp> {
    return this.request<MeAccessRequestResp>(
      "POST",
      "/v1/pam/me/approvals/{requestId}/deny",
      "",
      "json",
      [["requestId", "requestId"]], copyRecord(req),
      "json",
      "MeDenyRequestReq",
      options,
      "MeAccessRequestResp",
    );
  }
  async meListCredentials(req: MeListCredentialsReq,options?: RequestOptions): Promise<MeListCredentialsResp> {
    return this.request<MeListCredentialsResp>(
      "GET",
      "/v1/pam/me/credentials",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListCredentialsReq",
      options,
      "MeListCredentialsResp",
    );
  }
  async meIssueCredential(req: MeIssueCredentialReq,options?: RequestOptions): Promise<MeEphemeralCredResp> {
    return this.request<MeEphemeralCredResp>(
      "POST",
      "/v1/pam/me/credentials/issue",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeIssueCredentialReq",
      options,
      "MeEphemeralCredResp",
    );
  }
  async meGetCredential(req: MeGetCredentialReq,options?: RequestOptions): Promise<MeCredentialLeaseResp> {
    return this.request<MeCredentialLeaseResp>(
      "GET",
      "/v1/pam/me/credentials/{leaseId}",
      "",
      "query",
      [["leaseId", "leaseId"]], copyRecord(req),
      "json",
      "MeGetCredentialReq",
      options,
      "MeCredentialLeaseResp",
    );
  }
  async meRevokeCredential(req: MeRevokeCredentialReq,options?: RequestOptions): Promise<MeRevokeCredentialResp> {
    return this.request<MeRevokeCredentialResp>(
      "POST",
      "/v1/pam/me/credentials/{leaseId}/revoke",
      "",
      "json",
      [["leaseId", "leaseId"]], copyRecord(req),
      "json",
      "MeRevokeCredentialReq",
      options,
      "MeRevokeCredentialResp",
    );
  }
  async meListEntitlements(req: MeListEntitlementsReq,options?: RequestOptions): Promise<MeListEntitlementsResp> {
    return this.request<MeListEntitlementsResp>(
      "GET",
      "/v1/pam/me/entitlements",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListEntitlementsReq",
      options,
      "MeListEntitlementsResp",
    );
  }
  async meListGrants(req: MeListGrantsReq,options?: RequestOptions): Promise<MeListGrantsResp> {
    return this.request<MeListGrantsResp>(
      "GET",
      "/v1/pam/me/grants",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListGrantsReq",
      options,
      "MeListGrantsResp",
    );
  }
  async meGetGrant(req: MeGetGrantReq,options?: RequestOptions): Promise<MeGetGrantResp> {
    return this.request<MeGetGrantResp>(
      "GET",
      "/v1/pam/me/grants/{grantId}",
      "",
      "query",
      [["grantId", "grantId"]], copyRecord(req),
      "json",
      "MeGetGrantReq",
      options,
      "MeGetGrantResp",
    );
  }
  async meListSessions(req: MeListSessionsReq,options?: RequestOptions): Promise<MeListSessionsResp> {
    return this.request<MeListSessionsResp>(
      "GET",
      "/v1/pam/me/sessions",
      "",
      "query",
      [], copyRecord(req),
      "json",
      "MeListSessionsReq",
      options,
      "MeListSessionsResp",
    );
  }
  async meInitiateSession(req: MeInitiateSessionReq,options?: RequestOptions): Promise<MeBrokeredSessionResp> {
    return this.request<MeBrokeredSessionResp>(
      "POST",
      "/v1/pam/me/sessions",
      "",
      "json",
      [], copyRecord(req),
      "json",
      "MeInitiateSessionReq",
      options,
      "MeBrokeredSessionResp",
    );
  }
  async meGetSession(req: MeGetSessionReq,options?: RequestOptions): Promise<MeBrokeredSessionResp> {
    return this.request<MeBrokeredSessionResp>(
      "GET",
      "/v1/pam/me/sessions/{sessionId}",
      "",
      "query",
      [["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "MeGetSessionReq",
      options,
      "MeBrokeredSessionResp",
    );
  }
  async meEndSession(req: MeEndSessionReq,options?: RequestOptions): Promise<MeBrokeredSessionResp> {
    return this.request<MeBrokeredSessionResp>(
      "POST",
      "/v1/pam/me/sessions/{sessionId}/end",
      "",
      "json",
      [["sessionId", "sessionId"]], copyRecord(req),
      "json",
      "MeEndSessionReq",
      options,
      "MeBrokeredSessionResp",
    );
  }
  async listRevokedAPIKeys(req: ListRevokedAPIKeysReq,options?: RequestOptions): Promise<ListRevokedAPIKeysResp> {
    return this.request<ListRevokedAPIKeysResp>(
      "GET",
      "/v1/tenants/{tenantId}/apikeys/revoked",
      "",
      "query",
      [["tenantId", "tenantId"]], copyRecord(req),
      "json",
      "ListRevokedAPIKeysReq",
      options,
      "ListRevokedAPIKeysResp",
    );
  }
  async connectEvents(): Promise<JSONSocket<OIDCEventMsg>> {
    const path = "/ws/oidc/events";
    const socket = await this.openSocket(
      await this.websocketURL(path),
      await this.websocketProtocols(path, "jwt", this.config.wsProtocols?.[path] ?? []),
    );
    return createJSONSocket<OIDCEventMsg>(socket, "OIDCEventMsg");
  }
}

export function createClient(config: ClientConfig): ApicClient {
  return new ApicClient(config);
}

export function useApicClient(config: ClientConfig): ApicClient {
  const clientRef = useRef<ApicClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = new ApicClient(config);
  }
  useEffect(() => {
    clientRef.current = new ApicClient(config);
  }, [config.baseUrl, config.bearerToken, config.apiKeySigner, config.fetchImpl, config.headers, config.webSocketFactory, config.wsOrigin, config.wsProtocols, config.onUnauthorized]);
  return clientRef.current;
}
export function useAdminRotateSigningKeysMutation(client: ApicClient): MutationState<AdminRotateSigningKeysReq, AdminRotateSigningKeysResp> {
  return useMutationState(client, (req) => client.adminRotateSigningKeys(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListClientCAs(client: ApicClient, req: AdminMTLSEmptyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminClientCAListResp> {
  return usePromise(client, "GET", "/v1/admin/mtls/cas", req, () => client.adminListClientCAs(req), deps, options);
}
export function useAdminCreateClientCAMutation(client: ApicClient): MutationState<AdminCreateClientCAReq, AdminClientCAResp> {
  return useMutationState(client, (req) => client.adminCreateClientCA(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteClientCAMutation(client: ApicClient): MutationState<AdminGetClientCAReq, AdminClientCAResp> {
  return useMutationState(client, (req) => client.adminDeleteClientCA(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetClientCA(client: ApicClient, req: AdminGetClientCAReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminClientCAResp> {
  return usePromise(client, "GET", "/v1/admin/mtls/cas/{id}", req, () => client.adminGetClientCA(req), deps, options);
}
export function useAdminUpdateClientCAMutation(client: ApicClient): MutationState<AdminUpdateClientCAReq, AdminClientCAResp> {
  return useMutationState(client, (req) => client.adminUpdateClientCA(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminMTLSStatus(client: ApicClient, req: AdminMTLSEmptyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminMTLSStatusResp> {
  return usePromise(client, "GET", "/v1/admin/mtls/status", req, () => client.adminMTLSStatus(req), deps, options);
}
export function useAdminListTenants(client: ApicClient, req: AdminListTenantsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListTenantsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants", req, () => client.adminListTenants(req), deps, options);
}
export function useAdminCreateTenantMutation(client: ApicClient): MutationState<AdminCreateTenantReq, AdminTenantResp> {
  return useMutationState(client, (req) => client.adminCreateTenant(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteTenantMutation(client: ApicClient): MutationState<AdminGetTenantReq, AdminTenantResp> {
  return useMutationState(client, (req) => client.adminDeleteTenant(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetTenant(client: ApicClient, req: AdminGetTenantReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminTenantResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}", req, () => client.adminGetTenant(req), deps, options);
}
export function useAdminUpdateTenantMutation(client: ApicClient): MutationState<AdminUpdateTenantReq, AdminTenantResp> {
  return useMutationState(client, (req) => client.adminUpdateTenant(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListAccessRequests(client: ApicClient, req: AdminListAccessRequestsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAccessRequestsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/access-requests/", req, () => client.adminListAccessRequests(req), deps, options);
}
export function useAdminCreateAccessRequestMutation(client: ApicClient): MutationState<AdminCreateAccessRequestReq, AdminAccessRequestResp> {
  return useMutationState(client, (req) => client.adminCreateAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetAccessRequest(client: ApicClient, req: AdminGetAccessRequestReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAccessRequestResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/access-requests/{requestId}", req, () => client.adminGetAccessRequest(req), deps, options);
}
export function useAdminActivateAccessRequestMutation(client: ApicClient): MutationState<AdminGetAccessRequestReq, PamAccessRequestActivationResp> {
  return useMutationState(client, (req) => client.adminActivateAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminApproveAccessRequestMutation(client: ApicClient): MutationState<AdminApproveAccessRequestReq, AdminAccessRequestResp> {
  return useMutationState(client, (req) => client.adminApproveAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminCancelAccessRequestMutation(client: ApicClient): MutationState<AdminGetAccessRequestReq, AdminAccessRequestResp> {
  return useMutationState(client, (req) => client.adminCancelAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDenyAccessRequestMutation(client: ApicClient): MutationState<AdminDenyAccessRequestReq, AdminAccessRequestResp> {
  return useMutationState(client, (req) => client.adminDenyAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetAccessRequestEvidence(client: ApicClient, req: AdminGetAccessRequestReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAccessRequestResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/evidence", req, () => client.adminGetAccessRequestEvidence(req), deps, options);
}
export function useAdminGetAccessRequestEvidencePackage(client: ApicClient, req: AdminGetAccessRequestReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAccessRequestResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/access-requests/{requestId}/evidence-package", req, () => client.adminGetAccessRequestEvidencePackage(req), deps, options);
}
export function useAdminExtendAccessRequestMutation(client: ApicClient): MutationState<AdminExtendAccessRequestReq, AdminAccessRequestResp> {
  return useMutationState(client, (req) => client.adminExtendAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListAPIKeys(client: ApicClient, req: AdminListAPIKeysReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAPIKeysResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apikeys", req, () => client.adminListAPIKeys(req), deps, options);
}
export function useAdminMintAPIKeyMutation(client: ApicClient): MutationState<AdminMintAPIKeyReq, AdminAPIKeySecretResp> {
  return useMutationState(client, (req) => client.adminMintAPIKey(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminRevokeAPIKeyMutation(client: ApicClient): MutationState<AdminGetAPIKeyReq, AdminAPIKeyResp> {
  return useMutationState(client, (req) => client.adminRevokeAPIKey(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetAPIKey(client: ApicClient, req: AdminGetAPIKeyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAPIKeyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apikeys/{keyId}", req, () => client.adminGetAPIKey(req), deps, options);
}
export function useAdminRotateAPIKeyMutation(client: ApicClient): MutationState<AdminRotateAPIKeyReq, AdminAPIKeySecretResp> {
  return useMutationState(client, (req) => client.adminRotateAPIKey(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListApps(client: ApicClient, req: AdminListAppsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAppsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps", req, () => client.adminListApps(req), deps, options);
}
export function useAdminCreateAppMutation(client: ApicClient): MutationState<AdminCreateAppReq, AdminAppResp> {
  return useMutationState(client, (req) => client.adminCreateApp(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteAppMutation(client: ApicClient): MutationState<AdminGetAppReq, AdminAppResp> {
  return useMutationState(client, (req) => client.adminDeleteApp(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetApp(client: ApicClient, req: AdminGetAppReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAppResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}", req, () => client.adminGetApp(req), deps, options);
}
export function useAdminUpdateAppMutation(client: ApicClient): MutationState<AdminUpdateAppReq, AdminAppResp> {
  return useMutationState(client, (req) => client.adminUpdateApp(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListAppChildren(client: ApicClient, req: AdminListAppChildrenReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAppsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}/children", req, () => client.adminListAppChildren(req), deps, options);
}
export function useAdminDeleteAppClaimsSurfaceMutation(client: ApicClient): MutationState<AdminGetAppReq, AdminAppClaimsSurfaceResp> {
  return useMutationState(client, (req) => client.adminDeleteAppClaimsSurface(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetAppClaimsSurface(client: ApicClient, req: AdminGetAppClaimsSurfaceReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAppClaimsSurfaceResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}/claims-surface", req, () => client.adminGetAppClaimsSurface(req), deps, options);
}
export function useAdminPutAppClaimsSurfaceMutation(client: ApicClient): MutationState<AdminPutAppClaimsSurfaceReq, AdminAppClaimsSurfaceResp> {
  return useMutationState(client, (req) => client.adminPutAppClaimsSurface(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminPreviewAppClaimsSurfaceMutation(client: ApicClient): MutationState<AdminPreviewAppClaimsSurfaceReq, AdminAppClaimsSurfacePreviewResp> {
  return useMutationState(client, (req) => client.adminPreviewAppClaimsSurface(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListAppRoles(client: ApicClient, req: AdminListAppRolesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAppRolesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}/roles", req, () => client.adminListAppRoles(req), deps, options);
}
export function useAdminCreateAppRoleMutation(client: ApicClient): MutationState<AdminCreateAppRoleReq, AdminAppRoleResp> {
  return useMutationState(client, (req) => client.adminCreateAppRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteAppRoleMutation(client: ApicClient): MutationState<AdminGetAppRoleReq, AdminAppRoleResp> {
  return useMutationState(client, (req) => client.adminDeleteAppRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetAppRole(client: ApicClient, req: AdminGetAppRoleReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAppRoleResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}", req, () => client.adminGetAppRole(req), deps, options);
}
export function useAdminUpdateAppRoleMutation(client: ApicClient): MutationState<AdminUpdateAppRoleReq, AdminAppRoleResp> {
  return useMutationState(client, (req) => client.adminUpdateAppRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListAppRoleAssignments(client: ApicClient, req: AdminListAppRoleAssignmentsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAppRoleAssignmentsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/apps/{appId}/roles/{roleId}/assignments", req, () => client.adminListAppRoleAssignments(req), deps, options);
}
export function useAdminCreateAppRoleAssignmentMutation(client: ApicClient): MutationState<AdminCreateAppRoleAssignmentReq, AdminAppRoleAssignmentResp> {
  return useMutationState(client, (req) => client.adminCreateAppRoleAssignment(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteAppRoleAssignmentMutation(client: ApicClient): MutationState<AdminDeleteAppRoleAssignmentReq, AdminAppRoleAssignmentResp> {
  return useMutationState(client, (req) => client.adminDeleteAppRoleAssignment(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminQueryAudit(client: ApicClient, req: AdminQueryAuditReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminQueryAuditResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/audit", req, () => client.adminQueryAudit(req), deps, options);
}
export function useAdminGetAuditCaseFile(client: ApicClient, req: AdminGetAuditCaseFileReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminQueryAuditResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/audit/case-file/{requestId}", req, () => client.adminGetAuditCaseFile(req), deps, options);
}
export function useAdminGetAuditChainIntegrity(client: ApicClient, req: AdminGetAuditChainIntegrityReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGetAuditChainIntegrityResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/audit/chain-integrity", req, () => client.adminGetAuditChainIntegrity(req), deps, options);
}
export function useAdminRebaselineAuditChainMutation(client: ApicClient): MutationState<AdminRebaselineAuditChainReq, AdminRebaselineAuditChainResp> {
  return useMutationState(client, (req) => client.adminRebaselineAuditChain(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListBreakGlassEvents(client: ApicClient, req: AdminListBreakGlassEventsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListBreakGlassEventsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/break-glass/", req, () => client.adminListBreakGlassEvents(req), deps, options);
}
export function useAdminActivateBreakGlassMutation(client: ApicClient): MutationState<AdminActivateBreakGlassReq, AdminBreakGlassEventResp> {
  return useMutationState(client, (req) => client.adminActivateBreakGlass(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListUnreviewedBreakGlassEvents(client: ApicClient, req: AdminListBreakGlassEventsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListBreakGlassEventsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/break-glass/unreviewed", req, () => client.adminListUnreviewedBreakGlassEvents(req), deps, options);
}
export function useAdminGetBreakGlassEvent(client: ApicClient, req: AdminGetBreakGlassEventReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminBreakGlassEventResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/break-glass/{eventId}", req, () => client.adminGetBreakGlassEvent(req), deps, options);
}
export function useAdminCompleteBreakGlassReviewMutation(client: ApicClient): MutationState<AdminCompleteBreakGlassReviewReq, AdminBreakGlassEventResp> {
  return useMutationState(client, (req) => client.adminCompleteBreakGlassReview(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListBrokeredSessions(client: ApicClient, req: AdminListBrokeredSessionsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListBrokeredSessionsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/brokered-sessions", req, () => client.adminListBrokeredSessions(req), deps, options);
}
export function useAdminInitiateBrokeredSessionMutation(client: ApicClient): MutationState<AdminInitiateBrokeredSessionReq, AdminBrokeredSessionResp> {
  return useMutationState(client, (req) => client.adminInitiateBrokeredSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminKillBrokeredSessionMutation(client: ApicClient): MutationState<AdminKillBrokeredSessionReq, AdminBrokeredSessionResp> {
  return useMutationState(client, (req) => client.adminKillBrokeredSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetBrokeredSession(client: ApicClient, req: AdminGetBrokeredSessionReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminBrokeredSessionResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}", req, () => client.adminGetBrokeredSession(req), deps, options);
}
export function useAdminMonitorBrokeredSession(client: ApicClient, req: AdminGetBrokeredSessionReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminBrokeredSessionResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/monitor", req, () => client.adminMonitorBrokeredSession(req), deps, options);
}
export function useAdminPauseBrokeredSessionMutation(client: ApicClient): MutationState<AdminGetBrokeredSessionReq, AdminBrokeredSessionResp> {
  return useMutationState(client, (req) => client.adminPauseBrokeredSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetBrokeredSessionRecordings(client: ApicClient, req: AdminGetBrokeredSessionRecordingsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGetBrokeredSessionRecordingsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/brokered-sessions/{sessionId}/recordings", req, () => client.adminGetBrokeredSessionRecordings(req), deps, options);
}
export function useAdminResumeBrokeredSessionMutation(client: ApicClient): MutationState<AdminResumeBrokeredSessionReq, AdminBrokeredSessionResp> {
  return useMutationState(client, (req) => client.adminResumeBrokeredSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetCAEConfig(client: ApicClient, req: AdminGetCAEConfigReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminCAEConfigResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/cae/config", req, () => client.adminGetCAEConfig(req), deps, options);
}
export function useAdminUpdateCAEConfigMutation(client: ApicClient): MutationState<AdminUpdateCAEConfigReq, AdminUpdateCAEConfigResp> {
  return useMutationState(client, (req) => client.adminUpdateCAEConfig(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminTriggerCAEEvaluateMutation(client: ApicClient): MutationState<AdminTriggerCAEEvaluateReq, AdminTriggerCAEEvaluateResp> {
  return useMutationState(client, (req) => client.adminTriggerCAEEvaluate(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListCAEEvents(client: ApicClient, req: AdminListCAEEventsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListCAEEventsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/cae/events", req, () => client.adminListCAEEvents(req), deps, options);
}
export function useAdminGetCAEEvent(client: ApicClient, req: AdminGetCAEEventReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminCAEEventResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/cae/events/{eventId}", req, () => client.adminGetCAEEvent(req), deps, options);
}
export function useAdminListHighRiskSubjects(client: ApicClient, req: AdminListHighRiskSubjectsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListHighRiskSubjectsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/cae/risk", req, () => client.adminListHighRiskSubjects(req), deps, options);
}
export function useAdminGetCAERiskState(client: ApicClient, req: AdminGetCAERiskStateReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminCAERiskStateResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/cae/risk/{subjectId}", req, () => client.adminGetCAERiskState(req), deps, options);
}
export function useAdminOverrideCAERiskMutation(client: ApicClient): MutationState<AdminOverrideCAERiskReq, AdminOverrideCAERiskResp> {
  return useMutationState(client, (req) => client.adminOverrideCAERisk(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListClients(client: ApicClient, req: AdminListClientsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListClientsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/clients", req, () => client.adminListClients(req), deps, options);
}
export function useAdminCreateClientMutation(client: ApicClient): MutationState<AdminCreateClientReq, AdminCreateClientResp> {
  return useMutationState(client, (req) => client.adminCreateClient(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteClientMutation(client: ApicClient): MutationState<AdminGetClientReq, AdminClientResp> {
  return useMutationState(client, (req) => client.adminDeleteClient(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetClient(client: ApicClient, req: AdminGetClientReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminClientResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/clients/{clientId}", req, () => client.adminGetClient(req), deps, options);
}
export function useAdminUpdateClientMutation(client: ApicClient): MutationState<AdminUpdateClientReq, AdminClientResp> {
  return useMutationState(client, (req) => client.adminUpdateClient(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminRotateClientSecretMutation(client: ApicClient): MutationState<AdminRotateClientSecretReq, AdminRotateClientSecretResp> {
  return useMutationState(client, (req) => client.adminRotateClientSecret(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListConnectors(client: ApicClient, req: AdminListConnectorsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListConnectorsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/connectors", req, () => client.adminListConnectors(req), deps, options);
}
export function useAdminCreateConnectorMutation(client: ApicClient): MutationState<AdminCreateConnectorReq, AdminConnectorResp> {
  return useMutationState(client, (req) => client.adminCreateConnector(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteConnectorMutation(client: ApicClient): MutationState<AdminGetConnectorReq, AdminConnectorResp> {
  return useMutationState(client, (req) => client.adminDeleteConnector(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetConnector(client: ApicClient, req: AdminGetConnectorReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminConnectorResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/connectors/{connectorId}", req, () => client.adminGetConnector(req), deps, options);
}
export function useAdminUpdateConnectorMutation(client: ApicClient): MutationState<AdminUpdateConnectorReq, AdminConnectorResp> {
  return useMutationState(client, (req) => client.adminUpdateConnector(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminUpsertDevicePostureMutation(client: ApicClient): MutationState<AdminUpsertDevicePostureReq, AdminDevicePostureResp> {
  return useMutationState(client, (req) => client.adminUpsertDevicePosture(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetDevicePosture(client: ApicClient, req: AdminGetDevicePostureReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminDevicePostureResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/device-posture/{deviceId}", req, () => client.adminGetDevicePosture(req), deps, options);
}
export function useAdminListEmergencyRoles(client: ApicClient, req: AdminListEmergencyRolesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListEmergencyRolesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/emergency-roles/", req, () => client.adminListEmergencyRoles(req), deps, options);
}
export function useAdminCreateEmergencyRoleMutation(client: ApicClient): MutationState<AdminCreateEmergencyRoleReq, AdminEmergencyRoleResp> {
  return useMutationState(client, (req) => client.adminCreateEmergencyRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteEmergencyRoleMutation(client: ApicClient): MutationState<AdminGetEmergencyRoleReq, AdminEmergencyRoleResp> {
  return useMutationState(client, (req) => client.adminDeleteEmergencyRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetEmergencyRole(client: ApicClient, req: AdminGetEmergencyRoleReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminEmergencyRoleResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/emergency-roles/{roleId}", req, () => client.adminGetEmergencyRole(req), deps, options);
}
export function useAdminUpdateEmergencyRoleMutation(client: ApicClient): MutationState<AdminUpdateEmergencyRoleReq, AdminEmergencyRoleResp> {
  return useMutationState(client, (req) => client.adminUpdateEmergencyRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListEntitlements(client: ApicClient, req: AdminListEntitlementsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListEntitlementsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/entitlements", req, () => client.adminListEntitlements(req), deps, options);
}
export function useAdminCreateEntitlementMutation(client: ApicClient): MutationState<AdminCreateEntitlementReq, AdminEntitlementResp> {
  return useMutationState(client, (req) => client.adminCreateEntitlement(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteEntitlementMutation(client: ApicClient): MutationState<AdminGetEntitlementReq, AdminEntitlementResp> {
  return useMutationState(client, (req) => client.adminDeleteEntitlement(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetEntitlement(client: ApicClient, req: AdminGetEntitlementReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminEntitlementResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/entitlements/{entitlementId}", req, () => client.adminGetEntitlement(req), deps, options);
}
export function useAdminUpdateEntitlementMutation(client: ApicClient): MutationState<AdminUpdateEntitlementReq, AdminEntitlementResp> {
  return useMutationState(client, (req) => client.adminUpdateEntitlement(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminIssueEphemeralCredMutation(client: ApicClient): MutationState<AdminIssueEphemeralCredReq, AdminEphemeralCredResp> {
  return useMutationState(client, (req) => client.adminIssueEphemeralCred(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListCredentialLeases(client: ApicClient, req: AdminListCredentialLeasesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListCredentialLeasesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/ephemeral-creds/leases", req, () => client.adminListCredentialLeases(req), deps, options);
}
export function useAdminGetCredentialLease(client: ApicClient, req: AdminGetCredentialLeaseReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminCredentialLeaseResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/ephemeral-creds/leases/{id}", req, () => client.adminGetCredentialLease(req), deps, options);
}
export function useAdminRevokeCredentialLeaseMutation(client: ApicClient): MutationState<AdminGetCredentialLeaseReq, AdminRevokeCredentialLeaseResp> {
  return useMutationState(client, (req) => client.adminRevokeCredentialLease(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListCredentialProviders(client: ApicClient, req: AdminListCredentialProvidersReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListCredentialProvidersResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/ephemeral-creds/providers", req, () => client.adminListCredentialProviders(req), deps, options);
}
export function useAdminRotateCredentialKeyMutation(client: ApicClient): MutationState<AdminRotateCredentialKeyReq, AdminRotateCredentialKeyResp> {
  return useMutationState(client, (req) => client.adminRotateCredentialKey(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceListCampaigns(client: ApicClient, req: AdminListDiscoveryReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListCampaignsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/campaigns", req, () => client.adminGovernanceListCampaigns(req), deps, options);
}
export function useAdminGovernanceCreateCampaignMutation(client: ApicClient): MutationState<AdminCreateCampaignReq, AdminCampaignResp> {
  return useMutationState(client, (req) => client.adminGovernanceCreateCampaign(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceGetCampaign(client: ApicClient, req: AdminGetCampaignReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminCampaignResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}", req, () => client.adminGovernanceGetCampaign(req), deps, options);
}
export function useAdminGovernanceListAttestations(client: ApicClient, req: AdminListAttestationsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListAttestationsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/campaigns/{campaignId}/attestations", req, () => client.adminGovernanceListAttestations(req), deps, options);
}
export function useAdminGovernanceSubmitAttestationMutation(client: ApicClient): MutationState<AdminSubmitAttestationReq, AdminAttestationResp> {
  return useMutationState(client, (req) => client.adminGovernanceSubmitAttestation(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceCloseCampaignMutation(client: ApicClient): MutationState<AdminCloseCampaignReq, AdminCampaignResp> {
  return useMutationState(client, (req) => client.adminGovernanceCloseCampaign(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceLaunchCampaignMutation(client: ApicClient): MutationState<AdminLaunchCampaignReq, AdminCampaignResp> {
  return useMutationState(client, (req) => client.adminGovernanceLaunchCampaign(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceListDiscovery(client: ApicClient, req: AdminListDiscoveryReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListDiscoveryResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/discovery", req, () => client.adminGovernanceListDiscovery(req), deps, options);
}
export function useAdminGovernanceRunDiscoveryMutation(client: ApicClient): MutationState<AdminGovernanceRunDiscoveryReq, AdminGovernanceRunDiscoveryResp> {
  return useMutationState(client, (req) => client.adminGovernanceRunDiscovery(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceDriftReport(client: ApicClient, req: AdminDriftReportReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminDriftReportResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/drift", req, () => client.adminGovernanceDriftReport(req), deps, options);
}
export function useAdminGovernanceListActions(client: ApicClient, req: AdminGovernanceListActionsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGovernanceListActionsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/dual-auth", req, () => client.adminGovernanceListActions(req), deps, options);
}
export function useAdminGovernanceProposeMutation(client: ApicClient): MutationState<AdminGovernanceProposeReq, AdminGovernanceActionResp> {
  return useMutationState(client, (req) => client.adminGovernancePropose(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceApproveMutation(client: ApicClient): MutationState<AdminGovernanceApproveReq, AdminGovernanceActionResp> {
  return useMutationState(client, (req) => client.adminGovernanceApprove(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceRejectMutation(client: ApicClient): MutationState<AdminGovernanceRejectReq, AdminGovernanceActionResp> {
  return useMutationState(client, (req) => client.adminGovernanceReject(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListRoleDefs(client: ApicClient, req: AdminListRoleDefsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListRoleDefsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/role-defs", req, () => client.adminListRoleDefs(req), deps, options);
}
export function useAdminCreateRoleDefMutation(client: ApicClient): MutationState<AdminCreateRoleDefReq, AdminRoleDefResp> {
  return useMutationState(client, (req) => client.adminCreateRoleDef(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteRoleDefMutation(client: ApicClient): MutationState<AdminGetRoleDefReq, AdminRoleDefResp> {
  return useMutationState(client, (req) => client.adminDeleteRoleDef(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetRoleDef(client: ApicClient, req: AdminGetRoleDefReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminRoleDefResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/role-defs/{name}", req, () => client.adminGetRoleDef(req), deps, options);
}
export function useAdminUpdateRoleDefMutation(client: ApicClient): MutationState<AdminUpdateRoleDefReq, AdminRoleDefResp> {
  return useMutationState(client, (req) => client.adminUpdateRoleDef(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGovernanceListRoles(client: ApicClient, req: AdminGovernanceListRolesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGovernanceListRolesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/governance/roles", req, () => client.adminGovernanceListRoles(req), deps, options);
}
export function useAdminGovernanceAssignRoleMutation(client: ApicClient): MutationState<AdminGovernanceAssignRoleReq, AdminGovernanceActionResp> {
  return useMutationState(client, (req) => client.adminGovernanceAssignRole(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteMailProviderMutation(client: ApicClient): MutationState<AdminGetMailProviderReq, AdminMailProviderResp> {
  return useMutationState(client, (req) => client.adminDeleteMailProvider(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetMailProvider(client: ApicClient, req: AdminGetMailProviderReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminMailProviderResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/mail-provider", req, () => client.adminGetMailProvider(req), deps, options);
}
export function useAdminPutMailProviderMutation(client: ApicClient): MutationState<AdminPutMailProviderReq, AdminMailProviderResp> {
  return useMutationState(client, (req) => client.adminPutMailProvider(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetMFAPolicy(client: ApicClient, req: AdminGetMFAPolicyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminMFAPolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/mfa-policy", req, () => client.adminGetMFAPolicy(req), deps, options);
}
export function useAdminSetMFAPolicyMutation(client: ApicClient): MutationState<AdminSetMFAPolicyReq, AdminMFAPolicyResp> {
  return useMutationState(client, (req) => client.adminSetMFAPolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListTenantClientCAs(client: ApicClient, req: AdminListTenantClientCAsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminClientCAListResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/mtls/cas", req, () => client.adminListTenantClientCAs(req), deps, options);
}
export function useAdminPAMResilienceHealth(client: ApicClient, req: AdminPAMResilienceHealthReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPAMResilienceHealthResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/pam/resilience/health", req, () => client.adminPAMResilienceHealth(req), deps, options);
}
export function useAdminPAMMaintenanceToggleMutation(client: ApicClient): MutationState<AdminPAMMaintenanceToggleReq, AdminPAMMaintenanceToggleResp> {
  return useMutationState(client, (req) => client.adminPAMMaintenanceToggle(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminPAMSLODashboard(client: ApicClient, req: AdminPAMSLODashboardReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPAMSLODashboardResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/pam/resilience/slo", req, () => client.adminPAMSLODashboard(req), deps, options);
}
export function useAdminPAMResilienceStatus(client: ApicClient, req: AdminPAMResilienceStatusReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPAMResilienceStatusResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/pam/resilience/status", req, () => client.adminPAMResilienceStatus(req), deps, options);
}
export function useAdminListPolicies(client: ApicClient, req: AdminListPoliciesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListPoliciesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/policies", req, () => client.adminListPolicies(req), deps, options);
}
export function useAdminCreatePolicyMutation(client: ApicClient): MutationState<AdminCreatePolicyReq, AdminPolicyResp> {
  return useMutationState(client, (req) => client.adminCreatePolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminSimulatePolicyMutation(client: ApicClient): MutationState<AdminSimulatePolicyReq, AdminSimulatePolicyResp> {
  return useMutationState(client, (req) => client.adminSimulatePolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeletePolicyMutation(client: ApicClient): MutationState<AdminGetPolicyReq, AdminPolicyResp> {
  return useMutationState(client, (req) => client.adminDeletePolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetPolicy(client: ApicClient, req: AdminGetPolicyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/policies/{policyId}", req, () => client.adminGetPolicy(req), deps, options);
}
export function useAdminUpdatePolicyMutation(client: ApicClient): MutationState<AdminCreatePolicyReq, AdminPolicyResp> {
  return useMutationState(client, (req) => client.adminUpdatePolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminRollbackPolicyMutation(client: ApicClient): MutationState<AdminRollbackPolicyReq, AdminPolicyResp> {
  return useMutationState(client, (req) => client.adminRollbackPolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListPolicyVersions(client: ApicClient, req: AdminPolicyVersionsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPolicyVersionsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/policies/{policyId}/versions", req, () => client.adminListPolicyVersions(req), deps, options);
}
export function useAdminGetPolicyVersion(client: ApicClient, req: AdminPolicyVersionReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminPolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/policies/{policyId}/versions/{version}", req, () => client.adminGetPolicyVersion(req), deps, options);
}
export function useAdminGetProfilePolicy(client: ApicClient, req: AdminGetProfilePolicyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminProfilePolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/profile-policy", req, () => client.adminGetProfilePolicy(req), deps, options);
}
export function useAdminPutProfilePolicyMutation(client: ApicClient): MutationState<AdminPutProfilePolicyReq, AdminProfilePolicyResp> {
  return useMutationState(client, (req) => client.adminPutProfilePolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListResources(client: ApicClient, req: AdminListResourcesReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListResourcesResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/resources", req, () => client.adminListResources(req), deps, options);
}
export function useAdminCreateResourceMutation(client: ApicClient): MutationState<AdminCreateResourceReq, AdminResourceResp> {
  return useMutationState(client, (req) => client.adminCreateResource(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteResourceMutation(client: ApicClient): MutationState<AdminGetResourceReq, AdminResourceResp> {
  return useMutationState(client, (req) => client.adminDeleteResource(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetResource(client: ApicClient, req: AdminGetResourceReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminResourceResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/resources/{resourceId}", req, () => client.adminGetResource(req), deps, options);
}
export function useAdminUpdateResourceMutation(client: ApicClient): MutationState<AdminUpdateResourceReq, AdminResourceResp> {
  return useMutationState(client, (req) => client.adminUpdateResource(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteSecretsProviderMutation(client: ApicClient): MutationState<AdminGetSecretsProviderReq, AdminSecretsProviderResp> {
  return useMutationState(client, (req) => client.adminDeleteSecretsProvider(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetSecretsProvider(client: ApicClient, req: AdminGetSecretsProviderReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminSecretsProviderResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/secrets-provider", req, () => client.adminGetSecretsProvider(req), deps, options);
}
export function useAdminPutSecretsProviderMutation(client: ApicClient): MutationState<AdminPutSecretsProviderReq, AdminSecretsProviderResp> {
  return useMutationState(client, (req) => client.adminPutSecretsProvider(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteSessionControlPolicyMutation(client: ApicClient): MutationState<AdminGetSessionControlPolicyReq, AdminSessionControlPolicyResp> {
  return useMutationState(client, (req) => client.adminDeleteSessionControlPolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetSessionControlPolicy(client: ApicClient, req: AdminGetSessionControlPolicyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminSessionControlPolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/session-control-policies/{resourceId}", req, () => client.adminGetSessionControlPolicy(req), deps, options);
}
export function useAdminSetSessionControlPolicyMutation(client: ApicClient): MutationState<AdminSetSessionControlPolicyReq, AdminSessionControlPolicyResp> {
  return useMutationState(client, (req) => client.adminSetSessionControlPolicy(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetStepUpPolicy(client: ApicClient, req: AdminGetStepUpPolicyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGetStepUpPolicyResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/stepup/policy", req, () => client.adminGetStepUpPolicy(req), deps, options);
}
export function useAdminListSubjects(client: ApicClient, req: AdminListSubjectsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListSubjectsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/subjects", req, () => client.adminListSubjects(req), deps, options);
}
export function useAdminCreateSubjectMutation(client: ApicClient): MutationState<AdminCreateSubjectReq, AdminSubjectResp> {
  return useMutationState(client, (req) => client.adminCreateSubject(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteSubjectMutation(client: ApicClient): MutationState<AdminGetSubjectReq, AdminSubjectResp> {
  return useMutationState(client, (req) => client.adminDeleteSubject(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetSubject(client: ApicClient, req: AdminGetSubjectReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminSubjectResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/subjects/{subjectId}", req, () => client.adminGetSubject(req), deps, options);
}
export function useAdminUpdateSubjectMutation(client: ApicClient): MutationState<AdminUpdateSubjectReq, AdminSubjectResp> {
  return useMutationState(client, (req) => client.adminUpdateSubject(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetSubjectSecurityHistory(client: ApicClient, req: AdminGetSubjectSecurityHistoryReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGetUserSecurityHistoryResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/subjects/{subjectId}/security-history", req, () => client.adminGetSubjectSecurityHistory(req), deps, options);
}
export function useAdminListUsers(client: ApicClient, req: AdminListUsersReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListUsersResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users", req, () => client.adminListUsers(req), deps, options);
}
export function useAdminCreateUserMutation(client: ApicClient): MutationState<AdminCreateUserReq, AdminUserResp> {
  return useMutationState(client, (req) => client.adminCreateUser(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminDeleteUserMutation(client: ApicClient): MutationState<AdminGetUserReq, AdminUserResp> {
  return useMutationState(client, (req) => client.adminDeleteUser(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetUser(client: ApicClient, req: AdminGetUserReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminUserResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users/{userId}", req, () => client.adminGetUser(req), deps, options);
}
export function useAdminUpdateUserMutation(client: ApicClient): MutationState<AdminUpdateUserReq, AdminUserResp> {
  return useMutationState(client, (req) => client.adminUpdateUser(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListUserAppPasswords(client: ApicClient, req: AdminListUserAppPasswordsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminAppPasswordsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users/{userId}/app-passwords", req, () => client.adminListUserAppPasswords(req), deps, options);
}
export function useAdminRevokeUserAppPasswordMutation(client: ApicClient): MutationState<AdminRevokeUserAppPasswordReq, AdminRevokeUserAppPasswordResp> {
  return useMutationState(client, (req) => client.adminRevokeUserAppPassword(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminListUserFactors(client: ApicClient, req: AdminListUserFactorsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminListUserFactorsResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users/{userId}/factors", req, () => client.adminListUserFactors(req), deps, options);
}
export function useAdminRevokeUserFactorMutation(client: ApicClient): MutationState<AdminRevokeUserFactorReq, AdminRevokeUserFactorResp> {
  return useMutationState(client, (req) => client.adminRevokeUserFactor(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminUserPasswordResetMutation(client: ApicClient): MutationState<AdminUserPasswordResetReq, AdminUserResp> {
  return useMutationState(client, (req) => client.adminUserPasswordReset(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetUserProfile(client: ApicClient, req: AdminGetUserProfileReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminUserProfileResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users/{userId}/profile", req, () => client.adminGetUserProfile(req), deps, options);
}
export function useAdminSetUserProfileMutation(client: ApicClient): MutationState<AdminSetUserProfileReq, AdminUserProfileResp> {
  return useMutationState(client, (req) => client.adminSetUserProfile(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminUserRevokeSessionsMutation(client: ApicClient): MutationState<AdminUserRevokeSessionsReq, AdminUserResp> {
  return useMutationState(client, (req) => client.adminUserRevokeSessions(req), (cache) => cache.invalidate((key) => matchesResource(key, "/admin")));
}
export function useAdminGetUserSecurityHistory(client: ApicClient, req: AdminGetUserSecurityHistoryReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminGetUserSecurityHistoryResp> {
  return usePromise(client, "GET", "/v1/admin/tenants/{tenantId}/users/{userId}/security-history", req, () => client.adminGetUserSecurityHistory(req), deps, options);
}
export function useAdminWhoAmI(client: ApicClient, req: AdminMTLSEmptyReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<AdminWhoAmIResp> {
  return usePromise(client, "GET", "/v1/admin/whoami", req, () => client.adminWhoAmI(req), deps, options);
}
export function useInitCreateAdminMutation(client: ApicClient): MutationState<InitCreateAdminReq, InitCreateAdminResp> {
  return useMutationState(client, (req) => client.initCreateAdmin(req), (cache) => cache.invalidate((key) => matchesResource(key, "/init")));
}
export function useInitBeginMutation(client: ApicClient): MutationState<InitBeginReq, InitBeginResp> {
  return useMutationState(client, (req) => client.initBegin(req), (cache) => cache.invalidate((key) => matchesResource(key, "/init")));
}
export function useInitCompleteMutation(client: ApicClient): MutationState<InitCompleteReq, InitCompleteResp> {
  return useMutationState(client, (req) => client.initComplete(req), (cache) => cache.invalidate((key) => matchesResource(key, "/init")));
}
export function useInitStatus(client: ApicClient, req: InitStatusReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<InitStatusResp> {
  return usePromise(client, "GET", "/v1/init/status", req, () => client.initStatus(req), deps, options);
}
export function useDiscover(client: ApicClient, req: DiscoverReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<DiscoverResp> {
  return usePromise(client, "GET", "/v1/oidc/discover", req, () => client.discover(req), deps, options);
}
export function useSecondaryEmailVerifyConfirmMutation(client: ApicClient): MutationState<SecondaryEmailVerifyConfirmReq, SecondaryEmailVerifyConfirmResp> {
  return useMutationState(client, (req) => client.secondaryEmailVerifyConfirm(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostEmailVerifyConfirmMutation(client: ApicClient): MutationState<EmailVerifyConfirmReq, EmailVerifyConfirmResp> {
  return useMutationState(client, (req) => client.postEmailVerifyConfirm(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostEmailVerifyRequestMutation(client: ApicClient): MutationState<EmailVerifyRequestReq, EmailVerifyRequestResp> {
  return useMutationState(client, (req) => client.postEmailVerifyRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useHealth(client: ApicClient, req: HealthReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<HealthResp> {
  return usePromise(client, "GET", "/v1/oidc/health", req, () => client.health(req), deps, options);
}
export function useJwks(client: ApicClient, req: JWKSReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<JWKSResp> {
  return usePromise(client, "GET", "/v1/oidc/jwks", req, () => client.jwks(req), deps, options);
}
export function usePostMagicLinkRequestMutation(client: ApicClient): MutationState<MagicLinkRequestReq, MagicLinkRequestResp> {
  return useMutationState(client, (req) => client.postMagicLinkRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostMagicLinkVerifyMutation(client: ApicClient): MutationState<MagicLinkVerifyReq, MagicLinkVerifyResp> {
  return useMutationState(client, (req) => client.postMagicLinkVerify(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeListAppPasswords(client: ApicClient, req: MeListAppPasswordsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeAppPasswordsResp> {
  return usePromise(client, "GET", "/v1/oidc/me/app-passwords", req, () => client.meListAppPasswords(req), deps, options);
}
export function useMeCreateAppPasswordMutation(client: ApicClient): MutationState<MeCreateAppPasswordReq, MeCreateAppPasswordResp> {
  return useMutationState(client, (req) => client.meCreateAppPassword(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeRevokeAppPasswordMutation(client: ApicClient): MutationState<MeRevokeAppPasswordReq, MeRevokeAppPasswordResp> {
  return useMutationState(client, (req) => client.meRevokeAppPassword(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeListEmails(client: ApicClient, req: MeListEmailsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeEmailsResp> {
  return usePromise(client, "GET", "/v1/oidc/me/emails", req, () => client.meListEmails(req), deps, options);
}
export function useMeAddEmailMutation(client: ApicClient): MutationState<MeAddEmailReq, MeAddEmailResp> {
  return useMutationState(client, (req) => client.meAddEmail(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeDeleteEmailMutation(client: ApicClient): MutationState<MeDeleteEmailReq, MeDeleteEmailResp> {
  return useMutationState(client, (req) => client.meDeleteEmail(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeSetPrimaryEmailMutation(client: ApicClient): MutationState<MeSetPrimaryEmailReq, MeEmailsResp> {
  return useMutationState(client, (req) => client.meSetPrimaryEmail(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeGetProfile(client: ApicClient, req: MeGetProfileReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeProfileResp> {
  return usePromise(client, "GET", "/v1/oidc/me/profile", req, () => client.meGetProfile(req), deps, options);
}
export function useMeUpdateProfileMutation(client: ApicClient): MutationState<MeUpdateProfileReq, MeProfileResp> {
  return useMutationState(client, (req) => client.meUpdateProfile(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useTOTPDisableMutation(client: ApicClient): MutationState<TOTPDisableReq, TOTPDisableResp> {
  return useMutationState(client, (req) => client.tOTPDisable(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useTOTPEnrollBeginMutation(client: ApicClient): MutationState<TOTPEnrollBeginReq, TOTPEnrollBeginResp> {
  return useMutationState(client, (req) => client.tOTPEnrollBegin(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useTOTPEnrollVerifyMutation(client: ApicClient): MutationState<TOTPEnrollVerifyReq, TOTPEnrollVerifyResp> {
  return useMutationState(client, (req) => client.tOTPEnrollVerify(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostPasswordForgotMutation(client: ApicClient): MutationState<PasswordForgotReq, PasswordForgotResp> {
  return useMutationState(client, (req) => client.postPasswordForgot(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostPasswordResetMutation(client: ApicClient): MutationState<PasswordResetReq, PasswordResetResp> {
  return useMutationState(client, (req) => client.postPasswordReset(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useRecoveryKeyGenerateMutation(client: ApicClient): MutationState<RecoveryKeyGenerateReq, RecoveryKeyGenerateResp> {
  return useMutationState(client, (req) => client.recoveryKeyGenerate(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useRecoveryKeyRedeemMutation(client: ApicClient): MutationState<RecoveryKeyRedeemReq, RecoveryKeyRedeemResp> {
  return useMutationState(client, (req) => client.recoveryKeyRedeem(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostOIDCRegisterMutation(client: ApicClient): MutationState<RegisterReq, RegisterResp> {
  return useMutationState(client, (req) => client.postOIDCRegister(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostSecurityQuestionsSetMutation(client: ApicClient): MutationState<SecurityQuestionsSetReq, SecurityQuestionsSetResp> {
  return useMutationState(client, (req) => client.postSecurityQuestionsSet(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostSecurityQuestionsVerifyMutation(client: ApicClient): MutationState<SecurityQuestionsVerifyReq, SecurityQuestionsVerifyResp> {
  return useMutationState(client, (req) => client.postSecurityQuestionsVerify(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostAuthenticateBeginMutation(client: ApicClient): MutationState<WebAuthnAuthenticateBeginReq, WebAuthnAuthenticateBeginResp> {
  return useMutationState(client, (req) => client.postAuthenticateBegin(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostAuthenticateCompleteMutation(client: ApicClient): MutationState<WebAuthnAuthenticateCompleteReq, WebAuthnAuthenticateCompleteResp> {
  return useMutationState(client, (req) => client.postAuthenticateComplete(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useWebAuthnListCredentials(client: ApicClient, req: WebAuthnListCredentialsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<WebAuthnListCredentialsResp> {
  return usePromise(client, "GET", "/v1/oidc/webauthn/credentials", req, () => client.webAuthnListCredentials(req), deps, options);
}
export function useWebAuthnDeleteCredentialMutation(client: ApicClient): MutationState<WebAuthnDeleteCredentialReq, WebAuthnDeleteCredentialResp> {
  return useMutationState(client, (req) => client.webAuthnDeleteCredential(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useWebAuthnSetCredentialAliasMutation(client: ApicClient): MutationState<WebAuthnSetCredentialAliasReq, WebAuthnSetCredentialAliasResp> {
  return useMutationState(client, (req) => client.webAuthnSetCredentialAlias(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useWebAuthnEnrollmentStatus(client: ApicClient, req: WebAuthnEnrollmentStatusReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<WebAuthnEnrollmentStatusResp> {
  return usePromise(client, "GET", "/v1/oidc/webauthn/enrollment-status", req, () => client.webAuthnEnrollmentStatus(req), deps, options);
}
export function usePostRegisterBeginMutation(client: ApicClient): MutationState<WebAuthnRegisterBeginReq, WebAuthnRegisterBeginResp> {
  return useMutationState(client, (req) => client.postRegisterBegin(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function usePostRegisterCompleteMutation(client: ApicClient): MutationState<WebAuthnRegisterCompleteReq, WebAuthnRegisterCompleteResp> {
  return useMutationState(client, (req) => client.postRegisterComplete(req), (cache) => cache.invalidate((key) => matchesResource(key, "/oidc")));
}
export function useMeListAccessRequests(client: ApicClient, req: MeListAccessRequestsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListAccessRequestsResp> {
  return usePromise(client, "GET", "/v1/pam/me/access-requests", req, () => client.meListAccessRequests(req), deps, options);
}
export function useMeCreateAccessRequestMutation(client: ApicClient): MutationState<MeCreateAccessRequestReq, MeAccessRequestResp> {
  return useMutationState(client, (req) => client.meCreateAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeGetAccessRequest(client: ApicClient, req: MeGetAccessRequestReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeAccessRequestResp> {
  return usePromise(client, "GET", "/v1/pam/me/access-requests/{requestId}", req, () => client.meGetAccessRequest(req), deps, options);
}
export function useMeActivateAccessRequestMutation(client: ApicClient): MutationState<MeActivateAccessRequestReq, PamAccessRequestActivationResp> {
  return useMutationState(client, (req) => client.meActivateAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeCancelAccessRequestMutation(client: ApicClient): MutationState<MeGetAccessRequestReq, MeAccessRequestResp> {
  return useMutationState(client, (req) => client.meCancelAccessRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeListApprovals(client: ApicClient, req: MeListApprovalsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListApprovalsResp> {
  return usePromise(client, "GET", "/v1/pam/me/approvals", req, () => client.meListApprovals(req), deps, options);
}
export function useMeApproveRequestMutation(client: ApicClient): MutationState<MeApproveRequestReq, MeAccessRequestResp> {
  return useMutationState(client, (req) => client.meApproveRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeDenyRequestMutation(client: ApicClient): MutationState<MeDenyRequestReq, MeAccessRequestResp> {
  return useMutationState(client, (req) => client.meDenyRequest(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeListCredentials(client: ApicClient, req: MeListCredentialsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListCredentialsResp> {
  return usePromise(client, "GET", "/v1/pam/me/credentials", req, () => client.meListCredentials(req), deps, options);
}
export function useMeIssueCredentialMutation(client: ApicClient): MutationState<MeIssueCredentialReq, MeEphemeralCredResp> {
  return useMutationState(client, (req) => client.meIssueCredential(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeGetCredential(client: ApicClient, req: MeGetCredentialReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeCredentialLeaseResp> {
  return usePromise(client, "GET", "/v1/pam/me/credentials/{leaseId}", req, () => client.meGetCredential(req), deps, options);
}
export function useMeRevokeCredentialMutation(client: ApicClient): MutationState<MeRevokeCredentialReq, MeRevokeCredentialResp> {
  return useMutationState(client, (req) => client.meRevokeCredential(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeListEntitlements(client: ApicClient, req: MeListEntitlementsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListEntitlementsResp> {
  return usePromise(client, "GET", "/v1/pam/me/entitlements", req, () => client.meListEntitlements(req), deps, options);
}
export function useMeListGrants(client: ApicClient, req: MeListGrantsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListGrantsResp> {
  return usePromise(client, "GET", "/v1/pam/me/grants", req, () => client.meListGrants(req), deps, options);
}
export function useMeGetGrant(client: ApicClient, req: MeGetGrantReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeGetGrantResp> {
  return usePromise(client, "GET", "/v1/pam/me/grants/{grantId}", req, () => client.meGetGrant(req), deps, options);
}
export function useMeListSessions(client: ApicClient, req: MeListSessionsReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeListSessionsResp> {
  return usePromise(client, "GET", "/v1/pam/me/sessions", req, () => client.meListSessions(req), deps, options);
}
export function useMeInitiateSessionMutation(client: ApicClient): MutationState<MeInitiateSessionReq, MeBrokeredSessionResp> {
  return useMutationState(client, (req) => client.meInitiateSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useMeGetSession(client: ApicClient, req: MeGetSessionReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<MeBrokeredSessionResp> {
  return usePromise(client, "GET", "/v1/pam/me/sessions/{sessionId}", req, () => client.meGetSession(req), deps, options);
}
export function useMeEndSessionMutation(client: ApicClient): MutationState<MeEndSessionReq, MeBrokeredSessionResp> {
  return useMutationState(client, (req) => client.meEndSession(req), (cache) => cache.invalidate((key) => matchesResource(key, "/pam")));
}
export function useListRevokedAPIKeys(client: ApicClient, req: ListRevokedAPIKeysReq, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): RequestState<ListRevokedAPIKeysResp> {
  return usePromise(client, "GET", "/v1/tenants/{tenantId}/apikeys/revoked", req, () => client.listRevokedAPIKeys(req), deps, options);
}
export function useEventsSocket(client: ApicClient, deps: ReadonlyArray<unknown> = [], options?: QueryHookOptions): StreamState<OIDCEventMsg> {
  return useStream(() => client.connectEvents(), deps, options);
}
